import { useCallback, useEffect, useId, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { useMatch, useSearchParams } from 'react-router-dom';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import SignalsSummary from '@app/components/SignalsSummary';
import KpiCard from '@app/components/KpiCard';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useDashboardsApi, useDevicesApi, useMetricsApi, useSignalsApi, useSitesApi } from '@app/hooks/useApis';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useGrafanaEmbedSessionKeepalive } from '@app/hooks/useGrafanaEmbedSessionKeepalive';
import { useLatestRequestFetcher } from '@app/hooks/useLatestRequestFetcher';
import { useVisibilityAwareRefresh } from '@app/hooks/useVisibilityAwareRefresh';
import { GRAFANA_EXPLORER_ROUTE } from '@app/utils/org-routes';
import GrafanaIframeCard from '@app/components/GrafanaIframeCard';
import Drawer from '@app/components/Drawer';
import ResizableTelemetryTable from '@app/components/ResizableTelemetryTable';
import { ChartIcon } from '@app/components/icons';
import { computeTimeRange, type TimeRangePreset } from '@app/utils/time';
import { useConfig } from '@app/config/provider';
import { buildOrgUrl, resolveBasePath } from '@app/utils/api';
import { deviceRoleMatches, formatDeviceRoleLabel, sortDeviceRoles } from '@app/utils/deviceRoles';
import { replaceOwnedSearchParams } from '@app/utils/search-params';
import { formatRelativeTime } from '@app/utils/format';
import {
  mergeKeyedMetricsSeriesResponse,
  metricsSeriesForCacheKey,
  type KeyedMetricsSeriesResponse,
  type MetricsSeriesCache
} from '@app/utils/metricsSeries';
import {
  chartPlanForRow,
  featureMetricNameForKey,
  RAW_SIGNAL_IDENTITY_GROUPS,
  type ChartBindingMode,
  type RowChartSpec,
  type SignalStage
} from '@app/config/signalsChartRegistry';
import { ORG_INTERFACE_ERROR_COVERAGE_CONTRACT, ORG_INTERFACE_ERROR_RATE_CONTRACT } from './grafanaTelemetryContracts';
import { compareLatestMetricRows, latestRawMetricSample } from './grafanaInterfaceErrorRate';
import { buildOrgDeviceHealthRows, type OrgDeviceHealthRowShape } from './grafanaDeviceHealth';
import type {
  DashboardWidgetSpec,
  DashboardsRecommendedResponse,
  DashboardsTemplatesResponse,
  DevicesListResponse,
  MetricsIntentQueryRangeRequest,
  MetricsSeriesResponse,
  Signal,
  SignalCondition,
  SignalConditionsListResponse,
  SignalFeature,
  SignalFeaturesListResponse,
  SignalListResponse,
  SitesListResponse,
  TopologyV2ResolveResponse
} from '@app/types/sdk';
import type { AdminConnectorRuntimeMetricsSummary, ConnectorSummary, CursorListResponse, DeviceSummary, RuntimeKpiSummary, RuntimeMetricFamilySummary, RuntimeSectionSummary } from '@app/types/org-console';
import {
  routingAdjacencyAreaEquivalenceKey,
  routingAdjacencyAreaDisplay,
  routingAdjacencyAreaFromLabels,
  shouldReplaceRoutingHealthSample,
  signalRoutingAdjacencyDedupeKey,
  topologyRoutingAdjacencyMergeKey,
  topologyRoutingAdjacencyPeerKeyCandidates,
  topologyRoutingAdjacencyPeerKey,
  topologyRoutingAdjacencyRowKey,
  topologyRoutingStateIsUp,
  topologyRoutingStateLabel
} from './grafanaRoutingDedupe';
import { shouldShowSignalNumericMeta } from './grafanaSignals';
import {
  groupSSHSessionSignals,
  SSH_CONNECTION_LIMIT_SIGNAL_KEY,
  SSH_CONNECTIONS_BY_VRF_SIGNAL_KEY,
  SSH_CONNECTIONS_TOTAL_SIGNAL_KEY,
  SSH_PER_HOST_CONNECTION_LIMIT_SIGNAL_KEY
} from './sshSessionSummary';
import {
  flattenNestedTopologyResolveResults,
  normalizeTopologyProtocols,
  resolveTopologyForProtocols,
  resolveTopologyForSites,
  shouldShowProtocolTopologyUnavailable,
  topologyResolveFailureResult,
  topologyResolveHasFailures,
  topologyResolveAllFailed,
  type TopologySitesResolveResult
} from './grafanaTopologyFetch';

const GRAFANA_REFRESH_OPTIONS_SECONDS = [0, 5, 10, 30, 60] as const;
const INSPECTION_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const DEVICE_STALE_MINUTES = 15;
const INVENTORY_DEVICE_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CONNECTOR_STALE_MINUTES = 10;
const ORG_ROLLUP_PAGE_LIMIT = 200;
// Keep the summary compact while still showing the highest-priority device rows.
export const DEVICE_HEALTH_SUMMARY_ROW_LIMIT = 12;
const ORG_ROLLUP_MAX_PAGES = 25;
// Keep protocol detail bounded until a dedicated aggregate/details endpoint is
// available. A zero-page loop silently discarded valid telemetry (including
// BGP peers), so always request the first bounded page for the table cards.
const ORG_PROTOCOL_RAW_ROLLUP_MAX_PAGES = 1;
// Targeted health requests are limited to the compact CPU-ranked device set
// (currently 12 devices). Follow a bounded number of cursors for that selected
// set; this is the latest-state table path, not an organization-wide rollup.
const TARGETED_HEALTH_PAGE_SIZE = 500;
const TARGETED_HEALTH_MAX_PAGES = 2;
const ORG_LATEST_HEALTH_DEVICE_LIMIT = 12;
const TOPOLOGY_RESOLVE_CONCURRENCY = 4;
const ROUTING_ADJACENCY_TOPOLOGY_VIEW_ID = 'routing_adjacency';
const ROUTING_ADJACENCY_TOPOLOGY_PROTOCOLS = ['bgp', 'ospf', 'isis', 'bfd'] as const;
const UNKNOWN_ROUTING_AREA = 'unknown';
const GRAFANA_EXPLORER_SEARCH_PARAM_KEYS = ['scope', 'stage', 'family', 'siteId', 'deviceId', 'role', 'key', 'state'] as const;
export const LAG_MEMBERS_TOPOLOGY_VIEW_ID = 'lag_membership';
const VLAN_MEMBERSHIP_TOPOLOGY_VIEW_ID = 'vlan_membership_summary';
export const VPN_CONNECTION_TOPOLOGY_VIEW_ID = 'vpn_connection';

type SignalFamily =
  | 'all'
  | 'environment'
  | 'tunnel'
  | 'bfd'
  | 'bgp'
  | 'device_health'
  | 'interface'
  | 'redundancy'
  | 'stp'
  | 'lacp'
  | 'vtp'
  | 'poe'
  | 'routing'
  | 'platform_hardware'
  | 'qos'
  | 'firewall_sessions'
  | 'security_acl';

interface GrafanaExplorerRouteState {
  selectedScope: string;
  stage: string;
  family: string;
  selectedSiteId: string;
  selectedDeviceId: string;
  selectedDeviceRole: string;
  selectedKey: string;
  conditionState: string;
}

export const buildGrafanaExplorerSearchParams = (
  current: URLSearchParams,
  state: GrafanaExplorerRouteState
): URLSearchParams =>
  replaceOwnedSearchParams(current, GRAFANA_EXPLORER_SEARCH_PARAM_KEYS, {
    scope: state.selectedScope,
    stage: state.selectedScope !== 'org' ? state.stage : null,
    family: state.selectedScope !== 'org' ? state.family : null,
    siteId: state.selectedScope !== 'org' ? state.selectedSiteId : null,
    deviceId: state.selectedScope !== 'org' ? state.selectedDeviceId : null,
    role: state.selectedScope !== 'org' && state.selectedDeviceRole !== 'all' ? state.selectedDeviceRole : null,
    key: state.selectedScope !== 'org' ? state.selectedKey : null,
    state: state.selectedScope !== 'org' && state.stage === 'conditions' ? state.conditionState : null
  });

type ConditionStateFilter = 'ON' | 'OFF' | 'ALL';

interface StageOption {
  id: SignalStage;
  label: string;
  description: string;
}

interface FamilyOption {
  id: SignalFamily;
  label: string;
}

interface ConditionStateOption {
  id: ConditionStateFilter;
  label: string;
}

type DashboardScope = 'org' | 'site' | 'device';

interface InspectorRow {
  id: string;
  stage: SignalStage;
  family: string;
  key: string;
  metricName?: string;
  valueText: string;
  numericValue?: number;
  state?: string;
  timestamp?: string;
  deviceId: string;
  deviceName: string;
  targetId: string;
  targetType: string;
  instance: string;
  attributes?: Record<string, unknown> | null;
  sshSessionSummary?: {
    active: string;
    globalLimit: string;
    vrfs: string;
    perSourceIPLimit: string;
  };
}

interface DeviceOption {
  id: string;
  label: string;
  role?: string;
}

interface GrafanaTemplateSpec {
  templateKey: string;
  dashUid: string;
  panelId: number;
  vars: string[];
}

interface ChartDrawerState {
  row: InspectorRow;
  primary: DashboardWidgetSpec | null;
}

interface RoutingProtocolHealth {
  protocol: 'BGP' | 'OSPF' | 'IS-IS';
  total: number;
  up: number;
  down: number;
  unavailable: boolean;
  previewTruncated?: boolean;
}

interface RoutingHealthKpiDisplay {
  value: string;
  trend: string;
  accent: 'success' | 'warning' | 'danger' | 'default';
}

interface TopCpuDevice {
  deviceId: string;
  label: string;
  value: number;
}

interface TrendSample {
  t: Date;
  value: number;
}

type RoutingAdjacencyTopologyData = TopologyV2ResolveResponse | TopologyV2ResolveResponse[] | TopologySitesResolveResult<TopologyV2ResolveResponse> | null;

interface TrendSeries {
  id: string;
  label: string;
  values: TrendSample[];
  summaryValue: number;
  peakValue: number;
}

interface RangeMetricsRequest {
  cacheKey: string;
  request: MetricsIntentQueryRangeRequest;
}

interface MetricsIntentQueryRequest {
  intent: string;
  scope: {
    scopeType: 'org' | 'site' | 'device';
    scopeId: string;
  };
}

interface OrgFleetCpu {
  avg: number;
  devices: number;
  peakValue: number;
  peakDeviceId: string;
  topDevices: TopCpuDevice[];
  series: TrendSeries[];
  truncated: boolean;
  aggregateOnly?: boolean;
}

interface OrgInterfaceErrorRateSummary {
  value: number;
  deviceId: string;
  targetId: string;
  instance: string;
  reportingInterfaces: number;
  totalFeatureRows: number;
  series: TrendSeries[];
  source: 'range' | 'rolling' | 'snapshot' | 'none';
  truncated: boolean;
}

export const interfaceErrorRateCoverageState = ({
  value,
  observed,
  expected,
  failed
}: {
  value: number;
  observed: number;
  expected: number;
  failed: boolean;
}): 'error' | 'checking' | 'no-errors' | 'unavailable' => {
  if (value > 0) return 'error';
  if (failed) return 'unavailable';
  if (expected === 0 || observed >= expected) return 'no-errors';
  return 'checking';
};

export const shouldPrioritizeInterfaceErrorRate = (state: ReturnType<typeof interfaceErrorRateCoverageState>, value: number): boolean =>
  state === 'error' || value > 0;

export const normalizeInterfaceErrorCoverage = (observedRaw: number, expectedRaw: number) => {
  const observed = Math.max(0, Math.round(Number.isFinite(observedRaw) ? observedRaw : 0));
  const expected = Math.max(0, Math.round(Number.isFinite(expectedRaw) ? expectedRaw : 0));
  const inconsistent = (expected === 0 && observed > 0) || (expected > 0 && observed > expected);
  return {
    observedRaw: observed,
    observed: inconsistent ? Math.min(observed, expected) : observed,
    expected,
    complete: !inconsistent && (expected === 0 ? observed === 0 : observed >= expected),
    inconsistent
  };
};

interface OrgInterfaceCounts {
  total: number;
  up: number;
  down: number;
  operationallyDown: number;
  adminDown: number;
  unexpectedDown: number;
  idleEnabledDown: number;
  unclassifiedDown: number;
  lowerLayerDown: number;
  unknown: number;
  stale: number;
  lastObservedAt: string;
  unexpectedDownDetails: OrgUnexpectedInterface[];
  /** True when per-interface rows were used; summary responses cannot classify these fields. */
  classificationAvailable?: boolean;
  truncated: boolean;
}

interface OrgUnexpectedInterface {
  id: string;
  device: string;
  target: string;
  observedAt?: string;
}

interface OrgInterfaceStatusRequest {
  orgId: string;
  requestKey: string;
  refreshGeneration: number;
  freshnessReferenceTime: Date;
}

interface OrgInterfaceStatusPairResponse {
  requestKey: string;
  refreshGeneration: number;
  freshnessReferenceTime: Date;
  oper: SignalListResponse;
  admin: SignalListResponse;
}

interface SignalSummary {
  signalKey: string;
  observedAt: string;
  total: number;
  equalTo: number;
  equalCount: number;
  nonEqualCount: number;
  sum: number;
  average: number;
  minimum: number;
  maximum: number;
  adminDown?: number;
  enabledDown?: number;
  lowerLayerDown?: number;
  unclassifiedDown?: number;
}

interface OrgDashboardSignalSummaries {
  uptime: SignalSummary;
  interfaceOper: SignalSummary;
  interfaceAdmin: SignalSummary;
  cpu: SignalSummary;
  bgp: SignalSummary;
  ospf: SignalSummary;
  bfd: SignalSummary;
  stp: SignalSummary;
  lacp: SignalSummary;
}

interface OrgLatestDeviceHealthData {
  cpu: SignalListResponse;
  mem: SignalListResponse;
  uptime: SignalListResponse;
  interfaces: SignalListResponse;
  bgp: SignalListResponse;
  truncated: boolean;
}


interface OrgBgpPeerRow {
  id: string;
  deviceId: string;
  peer: string;
  local: string;
  device: string;
  state: string;
  established: boolean;
  prefixes: number | null;
  observedAt?: string;
  source?: 'snmp' | 'topology';
}

interface OrgOspfAdjacencyRow {
  id: string;
  device: string;
  area: string;
  up: number;
  total: number;
  state: string;
}

interface OrgBfdSessionRow {
  id: string;
  device: string;
  peer: string;
  interfaceName: string;
  sessionType?: string;
  state: string;
  up: boolean;
  observedAt?: string;
  source?: 'snmp' | 'topology';
}

type RoutingAdjacencyProtocol = 'bgp' | 'ospf' | 'isis' | 'bfd';

interface TopologyRoutingAdjacencyRow {
  id: string;
  protocol: RoutingAdjacencyProtocol;
  deviceId: string;
  deviceName: string;
  peer: string;
  area: string;
  state: string;
  up: boolean;
  targetId: string;
  instance: string;
  observedAt?: string;
  attributes: Record<string, unknown>;
}

interface TopologyLagMemberRow {
  id: string;
  deviceId: string;
  deviceName: string;
  lagName: string;
  memberName: string;
  targetId: string;
  observedAt?: string;
  attributes: Record<string, unknown>;
}

interface TopologyVlanMembershipRow {
  id: string;
  deviceId: string;
  deviceName: string;
  interfaceName: string;
  vlanId: string;
  vlanName: string;
  targetId: string;
  observedAt?: string;
  attributes: Record<string, unknown>;
}

interface TopologyTunnelRow {
  id: string;
  deviceId: string;
  deviceName: string;
  tunnelName: string;
  state: string;
  up: boolean;
  targetId: string;
  observedAt?: string;
  attributes: Record<string, unknown>;
}

interface OrgStpLagSummary {
  stpTopChanges: number | null;
  stpTopDevice: string;
  stpTopChangesIsRange: boolean;
  lagRows: Array<{
    id: string;
    label: string;
    up: number;
    total: number;
  }>;
  unavailable: boolean;
}

type OrgDeviceHealthRow = OrgDeviceHealthRowShape;

type FetchOrgCollection = <T>(basePath: string, orgId: string, resource: string, init?: RequestInit) => Promise<CursorListResponse<T>>;

const withCursor = (resource: string, cursor: string): string => {
  const [path, rawQuery = ''] = resource.split('?');
  const params = new URLSearchParams(rawQuery);
  params.set('after', cursor);
  return `${path}?${params.toString()}`;
};

const fetchAllOrgPages = async <T,>(
  fetchOrgCollection: FetchOrgCollection,
  basePath: string,
  orgId: string,
  resource: string,
  maxPages = ORG_ROLLUP_MAX_PAGES
): Promise<CursorListResponse<T>> => {
  const items: T[] = [];
  let nextResource = resource;
  let nextCursor = '';
  let meta: CursorListResponse<T>['meta'] | undefined;

  for (let page = 0; page < maxPages; page += 1) {
    const response = await fetchOrgCollection<T>(basePath, orgId, nextResource, { cache: 'no-store' });
    items.push(...(response.items ?? []));
    meta = response.meta;
    nextCursor = response.meta?.next ?? '';
    if (!nextCursor) {
      break;
    }
    nextResource = withCursor(resource, nextCursor);
  }

  return {
    items,
    meta: {
      ...meta,
      exactTotal: meta?.exactTotal ?? items.length,
      next: nextCursor || null
    }
  };
};

const STAGE_OPTIONS: StageOption[] = [
  { id: 'signals', label: 'Signals', description: 'Raw telemetry currently landing for the selected site and category.' },
  { id: 'features', label: 'Features', description: 'Latest derived L1 features computed from the raw telemetry.' },
  { id: 'conditions', label: 'Conditions', description: 'Latest L2 condition state evaluated from the derived features.' }
];

const FAMILY_OPTIONS: FamilyOption[] = [
  { id: 'all', label: 'All Families' },
  { id: 'environment', label: 'Environment' },
  { id: 'tunnel', label: 'Tunnel' },
  { id: 'bfd', label: 'BFD' },
  { id: 'bgp', label: 'BGP' },
  { id: 'device_health', label: 'Device Health' },
  { id: 'interface', label: 'Interface' },
  { id: 'redundancy', label: 'Redundancy' },
  { id: 'stp', label: 'STP' },
  { id: 'lacp', label: 'LACP / LAG' },
  { id: 'vtp', label: 'VTP / VLAN' },
  { id: 'poe', label: 'PoE' },
  { id: 'routing', label: 'Routing / IP' },
  { id: 'platform_hardware', label: 'Platform / Hardware' },
  { id: 'qos', label: 'QoS' },
  { id: 'firewall_sessions', label: 'Firewall / Sessions' },
  { id: 'security_acl', label: 'Security / ACL' }
];

const CONDITION_STATE_OPTIONS: ConditionStateOption[] = [
  { id: 'ON', label: 'ON only' },
  { id: 'OFF', label: 'OFF only' },
  { id: 'ALL', label: 'ON + OFF' }
];

const DASHBOARD_SCOPE_OPTIONS: Array<{ id: DashboardScope; label: string }> = [
  { id: 'org', label: 'Organization' },
  { id: 'site', label: 'Site and Device' }
];

const LEGACY_FAMILY_ALIASES: Record<string, SignalFamily> = {
  cisco_platform: 'platform_hardware',
  cisco_qos: 'qos',
  pan: 'firewall_sessions',
  arista: 'routing',
  aruba: 'environment'
};

const normalizeFamilySelection = (family: string | null): SignalFamily => {
  const candidate = (family ?? '').trim() as SignalFamily;
  const normalized = LEGACY_FAMILY_ALIASES[candidate] ?? candidate;
  return FAMILY_OPTIONS.some((option) => option.id === normalized) ? normalized : 'environment';
};

const PIPELINE_HEALTH_TEMPLATE_KEYS = [
  'org.telemetry.pipeline_sync_age',
  'org.telemetry.pipeline_export_failures',
  'org.telemetry.pipeline_dropped_samples',
  'org.telemetry.pipeline_queue_pressure',
  'org.telemetry.pipeline_process_restarts',
  'org.telemetry.pipeline_vpn_health'
];

const DEVICE_HEALTH_EXTRA_TEMPLATE_KEYS = [
  'site.telemetry.device_uptime_detail',
  'site.telemetry.device_cpu_high_timeline',
  'site.telemetry.device_memory_pressure_timeline',
  'site.telemetry.device_telemetry_freshness_detail'
];

const FAMILY_PANEL_KEYS: Record<SignalFamily, string[]> = {
  all: [],
  environment: [
    'site.telemetry.environment_max_temp_detail',
    'site.telemetry.environment_failed_fan_count_detail',
    'site.telemetry.environment_psu_redundancy_ratio_detail'
  ],
  tunnel: ['site.telemetry.tunnel_state_detail'],
  bfd: ['site.telemetry.bfd_session_state_detail', 'site.telemetry.bfd_transition_churn_detail'],
  bgp: ['site.telemetry.bgp_peer_established_detail', 'site.telemetry.bgp_reset_churn_detail'],
  device_health: ['site.telemetry.device_cpu_p95_detail', 'site.telemetry.device_mem_p95_detail', 'site.telemetry.device_uptime_detail'],
  interface: ['site.telemetry.interface_util_topk', 'site.telemetry.interface_util_detail'],
  redundancy: ['site.telemetry.lag_members_up_ratio', 'site.telemetry.lag_members_up_ratio_target', 'site.telemetry.lag_member_utilization_target'],
  stp: [],
  lacp: [],
  vtp: [],
  poe: [],
  routing: [],
  platform_hardware: [],
  qos: [],
  firewall_sessions: [],
  security_acl: []
};

const TARGET_SCOPED_INTERFACE_TEMPLATE_KEYS = new Set([
  'site.telemetry.interface_util_detail',
  'site.telemetry.interface_state_detail',
  'site.telemetry.interface_error_rate_detail'
]);

const TARGET_SCOPED_REDUNDANCY_TEMPLATE_KEYS = new Set([
  'site.telemetry.lag_members_up_ratio_target',
  'site.telemetry.lag_member_utilization_target'
]);

const FAMILY_SIGNAL_KEYS: Record<SignalFamily, string[]> = {
  all: [],
  environment: [
    'snmp_env_temp_celsius',
    'snmp_env_temp_min_celsius',
    'snmp_env_temp_max_celsius',
    'snmp_env_temp_sensor_ok',
    'snmp_aruba_temp_sensor_temperature_celsius',
    'snmp_aruba_temp_sensor_min_temperature_celsius',
    'snmp_aruba_temp_sensor_max_temperature_celsius',
    'snmp_env_fan_state',
    'snmp_env_psu_state'
  ],
  tunnel: ['snmp_tunnel_admin_up', 'snmp_tunnel_oper_up', 'topology_tunnel_state'],
  bfd: ['routing_bfd_session_state', 'snmp_bfd_session_up', 'snmp_bfd_session_up_transitions_total'],
  bgp: [
    'snmp_bgp_peer_admin_status',
    'snmp_bgp_peer_negotiated_version',
    'snmp_bgp_peer_state',
    'snmp_bgp_peer_remote_as',
    'snmp_bgp_peer_in_updates_total',
    'snmp_bgp_peer_out_updates_total',
    'snmp_bgp_peer_established_transitions_total',
    'snmp_bgp_peer_established_time_seconds',
    'snmp_bgp_peer_in_messages_total',
    'snmp_bgp_peer_out_messages_total',
    'snmp_bgp_peer_hold_time_seconds',
    'snmp_bgp_peer_keepalive_seconds'
  ],
  device_health: [
    'snmp_device_cpu_pct',
    'snmp_device_mem_pct',
    'snmp_device_uptime_seconds',
    'ssh_sessions'
  ],
  interface: [
    'snmp_if_admin_status',
    'snmp_if_oper_status',
    'snmp_if_in_errors_total',
    'snmp_if_out_errors_total',
    'snmp_if_in_discards_total',
    'snmp_if_out_discards_total',
    'snmp_if_in_ucast_pkts_total',
    'snmp_if_out_ucast_pkts_total',
    'snmp_if_in_octets_total',
    'snmp_if_out_octets_total',
    'snmp_if_speed',
    'snmp_if_high_speed_mbps',
    'snmp_dot3_fcs_errors_total',
    'snmp_dot3_alignment_errors_total',
    'snmp_dot3_single_collision_frames_total',
    'snmp_dot3_frame_too_longs_total',
    'snmp_dot3_internal_mac_receive_errors_total'
  ],
  redundancy: ['topology_lag_member'],
  stp: [
    'snmp_stp_top_changes_total',
    'snmp_stp_time_since_topology_change_seconds',
    'snmp_stp_loop_guard_enabled',
    'cli_stp_topology_change_count_total',
    'cli_stp_time_since_last_change_seconds'
  ],
  lacp: [
    'topology_lag_member',
    'snmp_lacp_member_aggregatable',
    'snmp_lacp_member_bundle_consistent',
    'snmp_lacp_member_synchronized',
    'snmp_lacp_member_collecting',
    'snmp_lacp_member_distributing',
    'snmp_lacp_member_defaulted',
    'snmp_lacp_member_expired',
    'snmp_lacp_member_admin_mode',
    'snmp_lacp_channel_max_bundle',
    'snmp_lacp_channel_min_link'
  ],
  vtp: [
    'topology_vlan_membership',
    'snmp_vtp_domain_mode',
    'snmp_vtp_domain_config_revision',
    'snmp_vtp_domain_pruning_state',
    'snmp_vtp_domain_version_in_use',
    'snmp_vtp_vlan_state',
    'snmp_vtp_vlan_type'
  ],
  poe: [
    'snmp_poe_port_detection_status',
    'snmp_poe_port_power_classification',
    'snmp_poe_port_mps_absent_count_total',
    'snmp_poe_port_invalid_signature_count_total',
    'snmp_poe_port_power_denied_count_total',
    'snmp_poe_port_overload_count_total',
    'snmp_poe_port_short_count_total',
    'snmp_poe_main_pse_power_watts',
    'snmp_poe_main_pse_oper_status',
    'snmp_poe_port_enabled',
    'snmp_poe_port_power_max_milliwatts',
    'snmp_poe_port_power_allocated_milliwatts',
    'snmp_poe_port_power_available_milliwatts',
    'snmp_poe_port_power_consumption_milliwatts',
    'snmp_poe_port_max_power_drawn_milliwatts',
    'snmp_poe_port_current_milliamps',
    'snmp_poe_port_voltage_decivolts',
    'snmp_poe_port_reserved_power_milliwatts',
    'snmp_poe_port_power_drawn_milliwatts',
    'snmp_poe_port_average_power_milliwatts',
    'snmp_poe_port_peak_power_milliwatts'
  ],
  routing: [
    'snmp_ip_forwarding',
    'snmp_ip_in_receives_total',
    'snmp_ip_in_delivers_total',
    'snmp_ip_forward_datagrams_total',
    'snmp_ip_in_discards_total',
    'snmp_ip_out_discards_total',
    'snmp_ip_reassembly_requests_total',
    'snmp_ip_fragment_creates_total',
    'snmp_route_count',
    'snmp_ospf_neighbor_state',
    'routing_ospf_neighbor_state',
    'routing_isis_adjacency_state',
    'snmp_ospf_neighbor_events_total',
    'snmp_ospf_interface_state',
    'snmp_arista_sw_ip_forwarding_out_forward_datagrams_total'
  ],
  platform_hardware: [
    'snmp_cisco_fru_module_oper_status',
    'snmp_cisco_fru_fan_tray_oper_status',
    'snmp_cisco_fru_power_supply_admin_status',
    'snmp_cisco_fru_power_supply_oper_status',
    'snmp_platform_stack_member_current_number',
    'snmp_platform_stack_member_next_reload_number',
    'snmp_platform_stack_member_role',
    'snmp_platform_stack_member_software_priority',
    'snmp_platform_stack_member_state',
    'snmp_platform_stack_member_system_power_priority',
    'snmp_platform_stack_member_poe_low_priority_devices',
    'snmp_platform_stack_member_poe_high_priority_devices',
    'snmp_platform_stack_port_oper_status',
    'snmp_platform_stack_port_neighbor_switch_number',
    'snmp_platform_stack_power_port_oper_status',
    'snmp_platform_stack_power_port_link_status',
    'snmp_platform_stack_power_port_overcurrent_threshold'
  ],
  qos: ['snmp_qos_class_pre_policy_bitrate_bps', 'snmp_qos_class_drop_packets_total', 'snmp_qos_class_drop_bitrate_bps'],
  firewall_sessions: [
    'snmp_pan_session_utilization_pct',
    'snmp_pan_session_max',
    'snmp_pan_session_active',
    'snmp_pan_session_active_tcp',
    'snmp_pan_session_active_udp',
    'snmp_pan_session_active_icmp',
    'snmp_pan_session_cps',
    'snmp_pan_vsys_session_utilization_pct',
    'snmp_pan_vsys_session_active',
    'snmp_pan_vsys_session_max',
    'snmp_pan_vsys_tcp_cps',
    'snmp_pan_vsys_udp_cps',
    'snmp_pan_vsys_other_ip_cps',
    'snmp_pan_zone_tcp_cps',
    'snmp_pan_zone_udp_cps',
    'snmp_pan_zone_other_ip_cps',
    'snmp_pan_interface_tcp_cps',
    'snmp_pan_interface_udp_cps',
    'snmp_pan_interface_other_ip_cps',
    'snmp_pan_mgmt_admin_sessions',
    'snmp_pan_globalprotect_gateway_utilization_pct',
    'snmp_pan_globalprotect_gateway_max_tunnels',
    'snmp_pan_globalprotect_gateway_active_tunnels'
  ],
  security_acl: [
    'snmp_arista_acl_rule_packets_total'
  ]
};

const TOPOLOGY_SIGNAL_KEYS = new Set([
  'routing_bgp_peer_state',
  'routing_ospf_neighbor_state',
  'routing_isis_adjacency_state',
  'routing_bfd_session_state',
  'topology_lag_member',
  'topology_vlan_membership',
  'topology_tunnel_state'
]);

export const shouldIncludeTopologySignalRows = (family: string, selectedKey: string, topologyKey: string): boolean => {
  if (!selectedKey || selectedKey !== topologyKey) {
    return false;
  }
  if (topologyKey === 'topology_lag_member') {
    return family === 'lacp' || family === 'redundancy';
  }
  if (topologyKey === 'topology_vlan_membership') {
    return family === 'vtp';
  }
  if (topologyKey === 'topology_tunnel_state') {
    return family === 'tunnel';
  }
  if (topologyKey === 'routing_bfd_session_state') {
    return family === 'bfd';
  }
  if (
    topologyKey === 'routing_bgp_peer_state' ||
    topologyKey === 'routing_ospf_neighbor_state' ||
    topologyKey === 'routing_isis_adjacency_state'
  ) {
    return family === (topologyKey === 'routing_bgp_peer_state' ? 'bgp' : 'routing');
  }
  return false;
};

// Default "All" views stay backend-paged when raw signal rows exist. Topology rows
// are used as a fallback only for topology-only datasets, or when the topology key
// is explicitly selected.
export const shouldUseBfdTopologyFallback = (family: string, selectedKey: string, signalRowCount: number): boolean =>
  family === 'bfd' && !selectedKey && signalRowCount === 0;

export const shouldUseBgpTopologyFallback = (family: string, selectedKey: string, signalRowCount: number): boolean =>
  family === 'bgp' && !selectedKey && signalRowCount === 0;

export const shouldUseRoutingTopologyFallback = (family: string, selectedKey: string, signalRowCount: number): boolean =>
  family === 'routing' && !selectedKey && signalRowCount === 0;

export const shouldUseTunnelTopologyFallback = (family: string, selectedKey: string, signalRowCount: number): boolean =>
  family === 'tunnel' && !selectedKey && signalRowCount === 0;

export const shouldUseVlanTopologyFallback = (family: string, selectedKey: string, signalRowCount: number): boolean =>
  family === 'vtp' && !selectedKey && signalRowCount === 0;

export const shouldUseLagTopologyFallback = (family: string, selectedKey: string, signalRowCount: number): boolean =>
  (family === 'lacp' || family === 'redundancy') && !selectedKey && signalRowCount === 0;

export const inspectionEmptyMessage = (stage: SignalStage, family: SignalFamily): string => {
  if (stage !== 'signals') {
    return 'No rows matched the current filters.';
  }
  switch (family) {
    case 'tunnel':
      return 'No live tunnel telemetry or matching VPN topology context is available for the selected filters.';
    case 'bfd':
      return 'No live BFD telemetry or matching BFD session topology is available for the selected filters.';
    case 'redundancy':
    case 'lacp':
      return 'No live LAG telemetry or matching LAG membership topology is available for the selected filters.';
    case 'stp':
      return 'No live STP telemetry is available for the selected filters.';
    default:
      return 'No rows matched the current filters.';
  }
};

export const siteScopedTopologyResolveContext = ({
  selectedScope,
  selectedSiteId,
  selectedDeviceId,
  selectedNodeLimit,
  selectedEdgeLimit,
  siteNodeLimit,
  siteEdgeLimit
}: {
  selectedScope: DashboardScope;
  selectedSiteId: string;
  selectedDeviceId: string;
  selectedNodeLimit: number;
  selectedEdgeLimit: number;
  siteNodeLimit: number;
  siteEdgeLimit: number;
}): { scopeType: 'site'; scopeId: string; limits: Record<string, unknown> } | null => {
  if (selectedScope === 'org' || !selectedSiteId) {
    return null;
  }
  const hasSelectedDevice = Boolean(selectedDeviceId);
  const limits: Record<string, unknown> = {
    limitNodes: hasSelectedDevice ? selectedNodeLimit : siteNodeLimit,
    limitEdges: hasSelectedDevice ? selectedEdgeLimit : siteEdgeLimit
  };
  if (hasSelectedDevice) {
    limits.rootNodeId = `ref:inventory.device:${selectedDeviceId}`;
  }
  return {
    scopeType: 'site',
    scopeId: selectedSiteId,
    limits
  };
};

// Structural topology snapshots remain useful beyond the raw telemetry freshness
// window. Their timestamp is still displayed so operators can judge their age.
export const topologyContextRowsForInspection = <T extends { observedAt?: string | null }>(rows: T[]): T[] => rows;

export function activeDataForFetcher<T>(fetcher: (() => Promise<T>) | null, data: T | null): T | null {
  return fetcher ? data : null;
}

export function activeQueryStateForFetcher<T>(
  fetcher: (() => Promise<T>) | null,
  state: { data: T | null; loading: boolean; error: Error | null; backendMissing: boolean }
) {
  if (!fetcher) {
    return {
      data: null,
      loading: false,
      error: null,
      backendMissing: false
    };
  }
  return state;
}

export function selectCurrentOrCachedData<T>(current: T | null | undefined, cached: T | null | undefined): T | null {
  return current ?? cached ?? null;
}

export const deviceSummariesForScope = (
  devices: readonly DeviceSummary[],
  selectedScope: DashboardScope,
  selectedSiteId: string
): DeviceSummary[] => {
  if (selectedScope === 'org' || !selectedSiteId) {
    return [...devices];
  }
  return devices.filter((device) => device.siteId === selectedSiteId);
};

export const inventoryDeviceIdentitySet = (devices: readonly DeviceSummary[]): Set<string> => {
  const out = new Set<string>();
  for (const device of devices) {
    for (const identity of [device.id, device.name, device.displayName, device.hostname, device.mgmtAddress]) {
      const normalized = identity?.trim();
      if (normalized) {
        out.add(normalized);
        out.add(normalizedDeviceIdentity(normalized));
      }
    }
  }
  return out;
};

const normalizedDeviceIdentity = (identity: string): string => identity.replace(/^(\d+\.\d+\.\d+\.\d+):\d+$/, '$1');
const deviceIdentifierLike = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f-]{20,}$/i.test(value) ||
  /^\d+\.\d+\.\d+\.\d+(?::\d+)?$/.test(value) ||
  /^(?:bfd|bgp|isis|ospf):/i.test(value);

export const inventoryDeviceCanonicalIdentityMap = (devices: readonly DeviceSummary[]): Map<string, string> => {
  const out = new Map<string, string>();
  const aliases = new Map<string, Set<string>>();
  for (const device of devices) {
    const canonicalId = device.id?.trim();
    if (!canonicalId) continue;
    out.set(canonicalId, canonicalId);
    for (const identity of [device.name, device.displayName, device.hostname, device.mgmtAddress]) {
      const normalized = identity?.trim();
      if (!normalized) continue;
      const owners = aliases.get(normalized) ?? new Set<string>();
      owners.add(canonicalId);
      aliases.set(normalized, owners);
      const base = normalizedDeviceIdentity(normalized);
      const baseOwners = aliases.get(base) ?? new Set<string>();
      baseOwners.add(canonicalId);
      aliases.set(base, baseOwners);
    }
  }
  for (const [alias, owners] of aliases.entries()) {
    if (owners.size === 1) {
      out.set(alias, Array.from(owners)[0]);
    }
  }
  return out;
};

export const canonicalInventoryDeviceId = (identity: string, identities: ReadonlyMap<string, string>): string => {
  const normalized = identity.trim();
  return identities.get(normalized) ?? identities.get(normalizedDeviceIdentity(normalized)) ?? normalized;
};

export const canonicalInventoryDeviceIdentitySet = (
  identities: readonly string[],
  canonicalMap: ReadonlyMap<string, string>
): Set<string> => new Set(identities.map((identity) => canonicalInventoryDeviceId(identity, canonicalMap)).filter(Boolean));

export const metricSeriesDeviceIdentity = (
  labels: Record<string, string>,
  canonicalMap: ReadonlyMap<string, string>,
  nameMap: ReadonlyMap<string, string>
): { id: string; label: string } => {
  const rawId = deviceIdFromMetricLabels(labels);
  return {
    id: canonicalInventoryDeviceId(rawId, canonicalMap),
    label: metricDeviceDisplayName(labels, nameMap)
  };
};

export const inventoryDeviceNameMap = (devices: readonly DeviceSummary[]): Map<string, string> => {
  const names = new Map<string, string>();
  const aliases = new Map<string, Set<string>>();
  for (const device of devices) {
    const canonicalId = device.id?.trim();
    if (!canonicalId) continue;
    names.set(canonicalId, device.displayName ?? device.name ?? device.hostname ?? canonicalId);
    for (const identity of [device.name, device.displayName, device.hostname, device.mgmtAddress]) {
      const normalized = identity?.trim();
      if (!normalized) continue;
      const owners = aliases.get(normalized) ?? new Set<string>();
      owners.add(canonicalId);
      aliases.set(normalized, owners);
      const base = normalizedDeviceIdentity(normalized);
      const baseOwners = aliases.get(base) ?? new Set<string>();
      baseOwners.add(canonicalId);
      aliases.set(base, baseOwners);
    }
  }
  for (const [alias, owners] of aliases.entries()) {
    if (owners.size === 1) {
      const owner = Array.from(owners)[0];
      const label = names.get(owner);
      if (label) names.set(alias, label);
    }
  }
  return names;
};

// The dashboard needs only the inventory total for reporting coverage. Keep
// the inexpensive count independent from the detailed device rollup, which
// can time out on larger organisations; a detailed inventory total remains
// authoritative whenever it is available.
export const coverageDeviceTotal = (
  summary: CursorListResponse<DeviceSummary> | null | undefined,
  detailedTotal: number | null | undefined
): number => detailedTotal ?? summary?.meta?.exactTotal ?? 0;

export const reportingCoverageDisplay = ({
  activeDeviceCount,
  hasTelemetryCoverageData,
  rawTelemetryReportingDeviceCount,
  reportingCoverageDeviceTotalCount,
  reportingCoverageSourceLabel
}: {
  activeDeviceCount: number;
  hasTelemetryCoverageData: boolean;
  rawTelemetryReportingDeviceCount: number;
  reportingCoverageDeviceTotalCount: number;
  reportingCoverageSourceLabel: string;
}): { label: string; secondary: string } => {
  if (hasTelemetryCoverageData && reportingCoverageDeviceTotalCount === 0 && rawTelemetryReportingDeviceCount > 0) {
    return {
      label: '--',
      secondary: `${rawTelemetryReportingDeviceCount} devices sending ${reportingCoverageSourceLabel} (coverage denominator unavailable)`
    };
  }
  if (reportingCoverageDeviceTotalCount === 0) {
    return { label: '0%', secondary: `0/0 devices reporting from ${reportingCoverageSourceLabel}` };
  }
  const label = `${Math.min(100, Math.round((activeDeviceCount / reportingCoverageDeviceTotalCount) * 100))}%`;
  if (hasTelemetryCoverageData && rawTelemetryReportingDeviceCount > activeDeviceCount) {
    return {
      label,
      secondary: `${activeDeviceCount}/${reportingCoverageDeviceTotalCount} devices reporting from ${reportingCoverageSourceLabel} (${rawTelemetryReportingDeviceCount} telemetry reporters observed)`
    };
  }
  return {
    label,
    secondary: `${activeDeviceCount}/${reportingCoverageDeviceTotalCount} devices reporting from ${reportingCoverageSourceLabel}`
  };
};

export const canUseTopologyFallbackForQuery = ({
  stage,
  loading,
  error,
  backendMissing
}: {
  stage: SignalStage;
  loading: boolean;
  error: Error | null;
  backendMissing: boolean;
}): boolean => stage === 'signals' && !loading && !error && !backendMissing;

export const shouldIncludeTopologySignalRowsForQuery = ({
  family,
  selectedKey,
  topologyKey,
  signalRowCount,
  canUseTopologyFallback
}: {
  family: string;
  selectedKey: string;
  topologyKey: string;
  signalRowCount: number;
  canUseTopologyFallback: boolean;
}): boolean => {
  if (shouldIncludeTopologySignalRows(family, selectedKey, topologyKey)) {
    return true;
  }
  if (!canUseTopologyFallback) {
    return false;
  }
  if (topologyKey === 'routing_bfd_session_state') {
    return shouldUseBfdTopologyFallback(family, selectedKey, signalRowCount);
  }
  if (topologyKey === 'routing_bgp_peer_state') {
    return shouldUseBgpTopologyFallback(family, selectedKey, signalRowCount);
  }
  if (topologyKey === 'routing_ospf_neighbor_state' || topologyKey === 'routing_isis_adjacency_state') {
    return shouldUseRoutingTopologyFallback(family, selectedKey, signalRowCount);
  }
  if (topologyKey === 'topology_tunnel_state') {
    return shouldUseTunnelTopologyFallback(family, selectedKey, signalRowCount);
  }
  if (topologyKey === 'topology_vlan_membership') {
    return shouldUseVlanTopologyFallback(family, selectedKey, signalRowCount);
  }
  if (topologyKey === 'topology_lag_member') {
    return shouldUseLagTopologyFallback(family, selectedKey, signalRowCount);
  }
  return false;
};

export const computeInspectionPagination = ({
  filteredRows,
  currentPage,
  inspectionPageLimit,
  pageMeta
}: {
  filteredRows: InspectorRow[];
  currentPage: number;
  inspectionPageLimit: number;
  pageMeta?: SignalListResponse['meta'] | null;
}) => {
  const usesServerPagination = Boolean(pageMeta);
  const displayRows = usesServerPagination
    ? filteredRows.slice(0, inspectionPageLimit)
    : filteredRows.slice((currentPage - 1) * inspectionPageLimit, currentPage * inspectionPageLimit);
  const minimumVisibleRowCount = usesServerPagination
    ? (currentPage - 1) * inspectionPageLimit + displayRows.length
    : filteredRows.length;
  const rowCount = pageMeta ? Math.max(pageMeta.exactTotal ?? 0, minimumVisibleRowCount) : filteredRows.length;
  const hasPrevPage = usesServerPagination ? Boolean(pageMeta?.prev) : currentPage > 1;
  const hasNextPage = usesServerPagination ? Boolean(pageMeta?.next) : currentPage * inspectionPageLimit < rowCount;
  const totalPages = rowCount > 0 ? Math.max(1, Math.ceil(rowCount / inspectionPageLimit)) : 1;
  const pageStart = rowCount === 0 ? 0 : (currentPage - 1) * inspectionPageLimit + 1;
  const pageEnd = rowCount === 0 || displayRows.length === 0 ? 0 : Math.min(pageStart + displayRows.length - 1, rowCount);

  return {
    rowCount,
    displayRows,
    hasPrevPage,
    hasNextPage,
    totalPages,
    pageStart,
    pageEnd
  };
};

const FAMILY_FEATURE_KEYS: Record<SignalFamily, string[]> = {
  all: [],
  environment: ['environment.max_temp_celsius', 'environment.failed_fan_count', 'environment.psu_redundancy_ratio'],
  tunnel: ['tunnel.admin_up', 'tunnel.up'],
  bfd: ['bfd.up', 'bfd.flap_rate_15m'],
  bgp: ['bgp.peer_established', 'bgp.reset_rate_15m'],
  device_health: ['device.cpu_pct_p95_5m', 'device.mem_pct_p95_5m'],
  interface: ['interface.unexpected_down', 'interface.error_rate_5m', 'interface.crc_fcs_error_rate_5m', 'interface.alignment_error_rate_5m', 'interface.util_pct_p95_5m'],
  redundancy: ['lag.unexpected_down', 'lag.members_up_ratio'],
  stp: [],
  lacp: [],
  vtp: [],
  poe: [],
  routing: [],
  platform_hardware: [],
  qos: [],
  firewall_sessions: [],
  security_acl: []
};

const FAMILY_CONDITION_KEYS: Record<SignalFamily, string[]> = {
  all: [],
  environment: ['device.overheating', 'device.cooling_degraded', 'device.power_redundancy_lost'],
  tunnel: ['tunnel.down'],
  bfd: ['bfd.down', 'bfd.flapping'],
  bgp: ['bgp.neighbor_down', 'bgp.unstable'],
  device_health: ['device.cpu_high', 'device.memory_pressure'],
  interface: ['interface.down_unexpected', 'interface.erroring', 'interface.quality_degraded', 'interface.congested'],
  redundancy: ['lag.degraded', 'lag.down'],
  stp: [],
  lacp: [],
  vtp: [],
  poe: [],
  routing: [],
  platform_hardware: [],
  qos: [],
  firewall_sessions: [],
  security_acl: []
};

const KEY_LABELS: Record<string, string> = {
  snmp_if_admin_status: 'Interface Admin Status',
  snmp_if_oper_status: 'Interface Operational Status',
  snmp_if_in_errors_total: 'Interface Inbound Errors',
  snmp_if_out_errors_total: 'Interface Outbound Errors',
  snmp_if_in_discards_total: 'Interface Inbound Discards',
  snmp_if_out_discards_total: 'Interface Outbound Discards',
  snmp_if_in_ucast_pkts_total: 'Interface Inbound Unicast Packets',
  snmp_if_out_ucast_pkts_total: 'Interface Outbound Unicast Packets',
  snmp_if_in_octets_total: 'Interface Inbound Octets',
  snmp_if_out_octets_total: 'Interface Outbound Octets',
  snmp_if_speed: 'Interface Speed',
  snmp_dot3_fcs_errors_total: 'Interface FCS Errors',
  snmp_dot3_alignment_errors_total: 'Interface Alignment Errors',
  snmp_bgp_peer_admin_status: 'BGP Peer Admin Status',
  snmp_bgp_peer_negotiated_version: 'BGP Peer Negotiated Version',
  snmp_bgp_peer_state: 'BGP Peer State',
  routing_bgp_peer_state: 'Topology BGP Peer State',
  snmp_bgp_peer_remote_as: 'BGP Peer Remote AS',
  snmp_bgp_peer_in_updates_total: 'BGP Peer Inbound Updates',
  snmp_bgp_peer_out_updates_total: 'BGP Peer Outbound Updates',
  snmp_bgp_peer_established_transitions_total: 'BGP Peer Established Transitions',
  snmp_bgp_peer_established_time_seconds: 'BGP Peer Established Time',
  snmp_bgp_peer_in_messages_total: 'BGP Peer In Total Messages',
  snmp_bgp_peer_out_messages_total: 'BGP Peer Out Total Messages',
  snmp_device_cpu_pct: 'Device CPU Percent',
  snmp_device_mem_pct: 'Device Memory Percent',
  snmp_device_uptime_seconds: 'Device Uptime',
  ssh_sessions: 'SSH Sessions',
  cli_tcp_established_ssh_connections_total: 'Total SSH Sessions',
  cli_tcp_established_ssh_connections: 'Active SSH Connections',
  cli_tcp_ssh_connection_limit: 'SSH Connection Limit',
  cli_tcp_ssh_per_host_connection_limit: 'Per-Source-IP SSH Limit',
  snmp_tunnel_admin_up: 'Tunnel Admin Up',
  snmp_tunnel_oper_up: 'Tunnel Operational Up',
  topology_tunnel_state: 'Tunnel State',
  topology_lag_member: 'LAG Member',
  topology_vlan_membership: 'VLAN Membership',
  snmp_bfd_session_up: 'BFD Session Up',
  snmp_bfd_session_up_transitions_total: 'BFD Session Up Transitions',
  routing_bfd_session_state: 'BFD Session State',
  routing_ospf_neighbor_state: 'OSPF Neighbor State',
  routing_isis_adjacency_state: 'IS-IS Adjacency State',
  snmp_env_temp_celsius: 'Temperature Celsius',
  snmp_env_temp_min_celsius: 'Temperature Minimum Celsius',
  snmp_env_temp_max_celsius: 'Temperature Maximum Celsius',
  snmp_env_temp_sensor_ok: 'Temperature Sensor OK',
  snmp_aruba_temp_sensor_temperature_celsius: 'Temperature Sensor Temperature Celsius',
  snmp_aruba_temp_sensor_min_temperature_celsius: 'Temperature Sensor Min Temperature Celsius',
  snmp_aruba_temp_sensor_max_temperature_celsius: 'Temperature Sensor Max Temperature Celsius',
  snmp_env_fan_state: 'Fan State',
  snmp_env_psu_state: 'PSU State',
  snmp_cisco_fru_fan_tray_oper_status: 'Fan Tray Operational Status',
  snmp_cisco_fru_power_supply_admin_status: 'Power Supply Admin Status',
  snmp_cisco_fru_power_supply_oper_status: 'Power Supply Operational Status',
  snmp_stp_loop_guard_enabled: 'STP Loop Guard Enabled',
  snmp_lacp_channel_max_bundle: 'LACP Channel Max Bundle',
  snmp_lacp_channel_min_link: 'LACP Channel Min Link',
  snmp_poe_port_enabled: 'PoE Port Enabled',
  snmp_poe_port_power_max_milliwatts: 'PoE Port Max Power',
  snmp_poe_port_power_allocated_milliwatts: 'PoE Port Allocated Power',
  snmp_poe_port_power_available_milliwatts: 'PoE Port Available Power',
  snmp_poe_port_power_consumption_milliwatts: 'PoE Port Consumed Power',
  snmp_poe_port_max_power_drawn_milliwatts: 'PoE Port Max Power Drawn',
  snmp_poe_port_current_milliamps: 'PoE Port Current',
  snmp_poe_port_voltage_decivolts: 'PoE Port Voltage',
  snmp_poe_port_reserved_power_milliwatts: 'PoE Port Reserved Power',
  snmp_poe_port_power_drawn_milliwatts: 'PoE Port Power Drawn',
  snmp_poe_port_average_power_milliwatts: 'PoE Port Average Power',
  snmp_poe_port_peak_power_milliwatts: 'PoE Port Peak Power',
  snmp_platform_stack_member_state: 'Stack Member State',
  snmp_platform_stack_port_oper_status: 'Stack Port Operational Status',
  snmp_platform_stack_power_port_oper_status: 'Stack Power Port Operational Status',
  snmp_qos_class_pre_policy_bitrate_bps: 'QoS Class Pre-Policy Bitrate',
  snmp_qos_class_drop_packets_total: 'QoS Class Dropped Packets',
  snmp_qos_class_drop_bitrate_bps: 'QoS Class Drop Bitrate',
  snmp_arista_sw_ip_forwarding_out_forward_datagrams_total: 'IP Forwarding Out Datagrams',
  snmp_pan_session_utilization_pct: 'Firewall Session Utilization',
  snmp_pan_session_active: 'Firewall Active Sessions',
  snmp_pan_session_cps: 'Firewall Sessions Per Second',
  'interface.unexpected_down': 'Interface Unexpected Down',
  'interface.error_rate_5m': 'Interface Error Rate 5m',
  'interface.crc_fcs_error_rate_5m': 'Interface CRC/FCS Error Rate 5m',
  'interface.alignment_error_rate_5m': 'Interface Alignment Error Rate 5m',
  'interface.util_pct_p95_5m': 'Interface Utilization P95 5m',
  'lag.unexpected_down': 'LAG Unexpected Down',
  'lag.members_up_ratio': 'LAG Members Up Ratio',
  'bgp.peer_established': 'BGP Peer Established',
  'bgp.reset_rate_15m': 'BGP Reset Rate 15m',
  'device.cpu_pct_p95_5m': 'Device CPU P95 5m',
  'device.mem_pct_p95_5m': 'Device Memory P95 5m',
  'tunnel.admin_up': 'Tunnel Admin Up',
  'tunnel.up': 'Tunnel Up',
  'bfd.up': 'BFD Up',
  'bfd.flap_rate_15m': 'BFD Flap Rate 15m',
  'environment.max_temp_celsius': 'Environment Max Temperature',
  'environment.failed_fan_count': 'Environment Failed Fan Count',
  'environment.psu_redundancy_ratio': 'Environment PSU Redundancy Ratio',
  'interface.down_unexpected': 'Interface Down Unexpected',
  'interface.erroring': 'Interface Erroring',
  'interface.quality_degraded': 'Interface Quality Degraded',
  'interface.congested': 'Interface Congested',
  'lag.degraded': 'LAG Degraded',
  'lag.down': 'LAG Down',
  'bgp.neighbor_down': 'BGP Neighbor Down',
  'bgp.unstable': 'BGP Unstable',
  'tunnel.down': 'Tunnel Down',
  'bfd.down': 'BFD Down',
  'bfd.flapping': 'BFD Flapping',
  'device.cpu_high': 'Device CPU High',
  'device.memory_pressure': 'Device Memory Pressure',
  'device.overheating': 'Device Overheating',
  'device.cooling_degraded': 'Device Cooling Degraded',
  'device.power_redundancy_lost': 'Device Power Redundancy Lost'
};

const SIGNAL_KEY_VENDOR_TOKENS = new Set(['snmp', 'cisco', 'aruba', 'arista', 'pan']);
const SIGNAL_KEY_DROP_TOKENS = new Set(['sw']);
const SIGNAL_KEY_TOKEN_LABELS: Record<string, string> = {
  acl: 'ACL',
  bfd: 'BFD',
  bgp: 'BGP',
  cbqos: 'CBQoS',
  cps: 'CPS',
  decivolts: 'Decivolts',
  env: 'Environment',
  fcs: 'FCS',
  fru: 'FRU',
  globalprotect: 'GlobalProtect',
  icmp: 'ICMP',
  id: 'ID',
  if: 'Interface',
  ip: 'IP',
  lacp: 'LACP',
  mac: 'MAC',
  max: 'Max',
  mbps: 'Mbps',
  mem: 'Memory',
  mgmt: 'Management',
  milliamps: 'Milliamps',
  milliwatts: 'Milliwatts',
  mps: 'MPS',
  oper: 'Operational',
  ospf: 'OSPF',
  poe: 'PoE',
  psu: 'PSU',
  pct: 'Percent',
  pse: 'PSE',
  qos: 'QoS',
  rpm: 'RPM',
  stp: 'STP',
  temp: 'Temperature',
  tcp: 'TCP',
  udp: 'UDP',
  ucast: 'Unicast',
  vlan: 'VLAN',
  vsys: 'VSYS',
  vtp: 'VTP'
};

const TARGET_VAR = 'target_id';
const SITE_VAR = 'site_id';
const DEVICE_VAR = 'device_id';
const DEVICE_NAME_VAR = 'device_name';
const METRIC_VAR = 'metric_name';
const INSTANCE_VAR = 'instance';
const IF_NAME_VAR = 'if_name';
const CONDITION_VAR = 'condition_key';
const ROW_MATCHERS_VAR = 'row_matchers';
export const UNSET_RAW_SIGNAL_DIMENSION = '__reap_unset__';
const RAW_SIGNAL_DIMENSION_VARS = [
  IF_NAME_VAR,
  'peer_addr',
  'peer_address',
  'local_addr',
  'local_address',
  'channel',
  'class',
  'policy',
  'acl_name',
  'rule_name',
  'rule_sequence',
  'pse_id',
  'port',
  'component_name',
  'component_kind',
  'component_descr',
  'vrf_name',
  'source_adapter',
  'ent_physical_index',
  'pse_group_index',
  'channel_if_index',
  'class_map_name',
  'class_map_desc',
  'policy_map_name',
  'cbqos_config_index',
  'cbqos_object_index',
  'cbqos_policy_index',
  'object_type',
  'parent_object_index'
] as const;

const labelForKey = (key: string): string => {
  if (KEY_LABELS[key]) {
    return KEY_LABELS[key];
  }

  return key
    .replace(/\./g, '_')
    .split('_')
    .filter((token) => token && !SIGNAL_KEY_VENDOR_TOKENS.has(token) && !SIGNAL_KEY_DROP_TOKENS.has(token))
    .map((token) => SIGNAL_KEY_TOKEN_LABELS[token] ?? `${token.charAt(0).toUpperCase()}${token.slice(1)}`)
    .join(' ');
};

const deviceIdFromTargetId = (targetId?: string): string | undefined => {
  const value = (targetId ?? '').trim();
  if (!value) {
    return undefined;
  }
  if (value.startsWith('ref:inventory.device:')) {
    return value.slice('ref:inventory.device:'.length);
  }
  if (value.startsWith('ref:inventory.interface:')) {
    const rest = value.slice('ref:inventory.interface:'.length);
    const hashIndex = rest.indexOf('#');
    return hashIndex >= 0 ? rest.slice(0, hashIndex) : rest;
  }
  return undefined;
};

const interfaceNameFromTargetId = (targetId?: string): string | undefined => {
  const value = (targetId ?? '').trim();
  const hashIndex = value.indexOf('#');
  if (hashIndex < 0 || hashIndex >= value.length - 1) {
    return undefined;
  }
  return value.slice(hashIndex + 1);
};

const escapeRegexValue = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const titleFromTemplateKey = (templateKey: string): string =>
  templateKey
    .split('.')
    .at(-1)
    ?.split('_')
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ') ?? templateKey;

export const buildGrafanaSoloUrl = ({
  dashUid,
  panelId,
  title,
  varBindings,
  vars,
  from,
  to,
  mockMode
}: {
  dashUid: string;
  panelId: number;
  title: string;
  varBindings: Record<string, string | undefined>;
  vars: string[];
  from: Date;
  to: Date;
  mockMode: boolean;
}): string => {
  const params = new URLSearchParams();
  params.set('panelId', String(panelId));
  params.set('from', String(from.getTime()));
  params.set('to', String(to.getTime()));
  const variableNames = new Set([...vars, ...Object.keys(varBindings)]);
  for (const varName of variableNames) {
    const value = varBindings[varName];
    if (typeof value === 'string') {
      params.set(`var-${varName}`, value);
    }
  }

  if (mockMode) {
    return `/mock-grafana-iframe.html?${params.toString()}`;
  }

  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `/grafana/d-solo/${encodeURIComponent(dashUid)}/${encodeURIComponent(slug || 'panel')}?${params.toString()}`;
};

const resolveAdminBasePath = (apiBaseUrl: string): string => {
  const basePath = resolveBasePath(apiBaseUrl).replace(/\/+$/, '');
  if (basePath.endsWith('/admin/v1')) {
    return basePath;
  }
  if (basePath.endsWith('/public/v1')) {
    return `${basePath.slice(0, -'/public/v1'.length)}/admin/v1`;
  }
  if (basePath.endsWith('/v1')) {
    return `${basePath.slice(0, -'/v1'.length)}/admin/v1`;
  }
  return `${basePath}/admin/v1`;
};

const valueTextFromNumber = (value: number | undefined): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'n/a';
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

const signalValueText = (row: Pick<Signal, 'signalKey' | 'value'>): string => {
  if (row.signalKey === 'snmp_bfd_session_up') {
    return row.value === 1 ? 'up' : row.value === 0 ? 'down' : valueTextFromNumber(row.value);
  }
  if (row.signalKey === 'snmp_bgp_peer_state') {
    return bgpStateLabel(row.value);
  }
  if (row.signalKey === 'snmp_ospf_neighbor_state') {
    return ospfStateLabel(row.value);
  }
  return valueTextFromNumber(row.value);
};

const SSH_CONNECTION_UTILIZATION_SIGNAL_KEY = 'cli_tcp_ssh_connection_utilization_pct';
const SSH_SESSIONS_SIGNAL_KEY = 'ssh_sessions';

const sshSummaryRows = (items: Signal[], deviceNameById: Map<string, string>, family: string): InspectorRow[] =>
  groupSSHSessionSignals(items).map((device) => {
    const source = device.total ?? device.globalLimit ?? device.perSourceIPLimit ?? device.vrfs[0];
    const vrfs = device.vrfs
      .map((item) => `${item.instance || 'unnamed'}: ${valueTextFromNumber(item.value)}`)
      .join(' · ');
    return {
      id: `${SSH_SESSIONS_SIGNAL_KEY}:${device.deviceId}:${device.observedAt ?? ''}`,
      stage: 'signals',
      family: source?.family ?? family,
      key: SSH_CONNECTIONS_TOTAL_SIGNAL_KEY,
      metricName: source?.metricName ?? SSH_CONNECTIONS_TOTAL_SIGNAL_KEY,
      valueText: valueTextFromNumber(device.total?.value),
      numericValue: device.total?.value,
      timestamp: device.observedAt,
      deviceId: device.deviceId,
      deviceName: deviceNameById.get(device.deviceId) ?? device.deviceId,
      targetId: source?.targetId ?? '',
      targetType: source?.targetType ?? '',
      instance: '',
      attributes: device.total?.attributes ?? source?.attributes,
      sshSessionSummary: {
        active: valueTextFromNumber(device.total?.value),
        globalLimit: valueTextFromNumber(device.globalLimit?.value),
        vrfs: vrfs || '—',
        perSourceIPLimit: valueTextFromNumber(device.perSourceIPLimit?.value)
      }
    };
  });

const bfdSessionPeerDisplay = (value: string | null | undefined): string => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return '';
  }
  const indexPrefix = 'bfd:index:';
  if (trimmed.toLowerCase().startsWith(indexPrefix)) {
    const sessionIndex = trimmed.slice(indexPrefix.length).trim();
    return sessionIndex ? `Session ${sessionIndex}` : 'BFD session';
  }
  return trimmed;
};

const bgpSessionParts = (value: string | null | undefined): { peerAddr: string; localAddr: string } => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return { peerAddr: '', localAddr: '' };
  }
  if (!trimmed.includes('=')) {
    return { peerAddr: trimmed, localAddr: '' };
  }
  let peerAddr = '';
  let localAddr = '';
  for (const part of trimmed.split('|')) {
    const [rawKey, rawValue] = part.split('=', 2);
    const key = rawKey?.trim() ?? '';
    const currentValue = rawValue?.trim() ?? '';
    if (key === 'peer_addr') {
      peerAddr = currentValue;
    } else if (key === 'local_addr') {
      localAddr = currentValue;
    }
  }
  return { peerAddr, localAddr };
};

const bgpSessionDisplay = (value: string | null | undefined): string => {
  const { peerAddr, localAddr } = bgpSessionParts(value);
  if (peerAddr && localAddr) {
    return `${peerAddr} / ${localAddr}`;
  }
  return peerAddr || localAddr || '';
};

export const aclRuleDisplay = (
  instance: string | null | undefined,
  labels: Record<string, string>
): string => {
  const rawAclName = labels.acl_name?.trim() || (instance ?? '').trim();
  const aclName = rawAclName.replace(/^\$\$(.+)\$\$$/, '$1');
  const rule = labels.rule_name?.trim() || labels.rule_sequence?.trim();
  if (aclName && rule) {
    return `${aclName} / rule ${rule}`;
  }
  return aclName || rule || '';
};

const inspectorInstanceDisplay = (row: Pick<InspectorRow, 'attributes' | 'family' | 'instance' | 'targetId'>): string => {
  if (row.family === 'bgp') {
    return bgpSessionDisplay(row.instance);
  }
  if (row.family === 'security_acl') {
    return aclRuleDisplay(row.instance, stringRecordFromUnknown(row.attributes?.labels));
  }
  if (row.family === 'lacp') {
    const memberName = lacpMemberNameFromRow(row);
    const aggregateName = lacpAggregateNameFromLabels(stringRecordFromUnknown(row.attributes?.labels));
    if (memberName && aggregateName && memberName !== aggregateName) {
      return `${memberName} / ${aggregateName}`;
    }
    return memberName || aggregateName || '';
  }
  return row.instance || '';
};

const formatInteger = (value: number): string => new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);

const formatPercent = (value: number): string => `${Math.round(value)}%`;

export const routingHealthKpiDisplay = ({
  liveTelemetryUnavailable,
  loading,
  totalPeers,
  downPeers
}: {
  liveTelemetryUnavailable: boolean;
  loading: boolean;
  totalPeers: number;
  downPeers: number;
}): RoutingHealthKpiDisplay => {
  if (loading) {
    return {
      value: '…',
      trend: 'Loading routing telemetry and topology context',
      accent: 'default'
    };
  }
  if (liveTelemetryUnavailable) {
    return {
      value: 'No live telemetry',
      trend: totalPeers > 0
        ? 'No devices are reporting live SNMP telemetry; adjacency rows below are topology-derived context.'
        : 'No devices are reporting live SNMP telemetry.',
      accent: 'warning'
    };
  }
  if (totalPeers === 0) {
    return {
      value: 'No peers',
      trend: 'Backed by BGP, OSPF, and IS-IS adjacency state when available',
      accent: 'success'
    };
  }
  if (downPeers > 0) {
    return {
      value: `${formatInteger(downPeers)} down`,
      trend: 'Backed by BGP, OSPF, and IS-IS adjacency state when available',
      accent: 'warning'
    };
  }
  return {
    value: 'All up',
    trend: 'Backed by BGP, OSPF, and IS-IS adjacency state when available',
    accent: 'success'
  };
};

const formatRate = (value: number): string => {
  if (!Number.isFinite(value)) {
    return 'n/a';
  }
  if (value >= 100) {
    return `${value.toFixed(0)}/s`;
  }
  if (value >= 10) {
    return `${value.toFixed(1)}/s`;
  }
  return `${value.toFixed(2)}/s`;
};

const timeRangeStepSeconds = (preset: TimeRangePreset): number => {
  switch (preset) {
    case '15m':
      return 30;
    case '6h':
      return 300;
    case '24h':
      return 900;
    case '1h':
    default:
      return 60;
  }
};

export const metricRangeRequest = ({
  orgId,
  metric,
  rangePreset,
  timeRange,
  unit
}: {
  orgId: string;
  metric: string;
  rangePreset: TimeRangePreset;
  timeRange: { from: Date; to: Date };
  unit?: string;
}): MetricsIntentQueryRangeRequest => ({
  queryPlan: {
    version: 'v1',
    backend: 'prometheus',
    metric,
    metricKind: 'gauge',
    aggregation: 'none',
    unit
  },
  scope: {
    scopeType: 'org',
    scopeId: orgId
  },
  range: {
    from: timeRange.from,
    to: timeRange.to,
    stepSec: timeRangeStepSeconds(rangePreset)
  }
});

const metricIntentRangeRequest = ({
  orgId,
  intent,
  rangePreset,
  timeRange
}: {
  orgId: string;
  intent: string;
  rangePreset: TimeRangePreset;
  timeRange: { from: Date; to: Date };
}): MetricsIntentQueryRangeRequest => ({
  intent,
  scope: {
    scopeType: 'org',
    scopeId: orgId
  },
  range: {
    from: timeRange.from,
    to: timeRange.to,
    stepSec: timeRangeStepSeconds(rangePreset)
  }
});

const metricIntentInstantRequest = ({
  orgId,
  intent
}: {
  orgId: string;
  intent: string;
}): MetricsIntentQueryRequest => ({
  intent,
  scope: {
    scopeType: 'org',
    scopeId: orgId
  }
});

const chartTimeLabel = (value: Date): string =>
  value.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

const finiteAverage = (values: number[]): number => {
  const finite = values.filter((value) => Number.isFinite(value));
  if (finite.length === 0) {
    return 0;
  }
  return finite.reduce((sum, value) => sum + value, 0) / finite.length;
};

const finiteMax = (values: number[]): number => {
  const finite = values.filter((value) => Number.isFinite(value));
  if (finite.length === 0) {
    return 0;
  }
  return Math.max(...finite);
};

const metricsLabelValue = (labels: Record<string, string>, keys: string[]): string => {
  for (const key of keys) {
    const value = labels[key]?.trim();
    if (value) {
      return value;
    }
  }
  return '';
};

const deviceIdFromMetricLabels = (labels: Record<string, string>): string =>
  metricsLabelValue(labels, ['device_id', 'deviceId', 'device']);

const metricDeviceFallbackLabelKeys = [
  'device_name',
  'deviceName',
  'hostname',
  'host',
  'sys_name',
  'sysName',
  'instance',
  'target',
  'exported_instance'
];

const metricDeviceIdentityFromLabels = (labels: Record<string, string>): string =>
  deviceIdFromMetricLabels(labels) || metricsLabelValue(labels, metricDeviceFallbackLabelKeys);

export const metricDeviceDisplayName = (labels: Record<string, string>, deviceNameById: ReadonlyMap<string, string>): string => {
  const canonicalDeviceId = deviceIdFromMetricLabels(labels);
  const identityCandidates = [canonicalDeviceId, ...metricDeviceFallbackLabelKeys.map((key) => labels[key] ?? '')]
    .map((value) => value.trim())
    .filter(Boolean);
  for (const identity of identityCandidates) {
    const inventoryName = deviceNameById.get(identity) ?? deviceNameById.get(normalizedDeviceIdentity(identity));
    if (inventoryName) {
      return inventoryName;
    }
  }
  return (
    metricsLabelValue(labels, metricDeviceFallbackLabelKeys) ||
    canonicalDeviceId ||
    metricDeviceIdentityFromLabels(labels) ||
    'device'
  );
};

const interfaceNameFromMetricLabels = (labels: Record<string, string>): string =>
  metricsLabelValue(labels, ['if_name', 'ifName', 'interface', 'interface_name', 'if_descr', 'ifDescr', 'target_label', 'targetLabel']);

export const targetIdFromMetricLabels = (labels: Record<string, string>, deviceId: string): string => {
  const explicitTargetId = metricsLabelValue(labels, ['target_id', 'targetId']);
  if (explicitTargetId) {
    return explicitTargetId;
  }
  const ifName = interfaceNameFromMetricLabels(labels);
  return deviceId && ifName ? `ref:inventory.interface:${deviceId}#${ifName}` : '';
};

const trendSamplesFromMetricSeries = (series: MetricsSeriesResponse['series'][number]): TrendSample[] =>
  (series.values ?? [])
    .map((sample) => ({
      t: new Date(sample.t),
      value: sample.value
    }))
    .filter((sample) => !Number.isNaN(sample.t.getTime()) && Number.isFinite(sample.value))
    .sort((left, right) => left.t.getTime() - right.t.getTime());

export const latestInterfaceErrorCoverageValue = (series: MetricsSeriesResponse['series']): number => {
  let latestSample: TrendSample | undefined;
  for (const item of series) {
    for (const sample of trendSamplesFromMetricSeries(item)) {
      if (!latestSample || sample.t.getTime() > latestSample.t.getTime()) {
        latestSample = sample;
      }
    }
  }
  return latestSample?.value ?? 0;
};

const trendSamplesForRangeDisplay = (series: MetricsSeriesResponse['series'][number], timeRange: { from: Date; to: Date }): TrendSample[] => {
  const samples = trendSamplesFromMetricSeries(series);
  if (samples.length !== 1) {
    return samples;
  }
  return [
    { t: timeRange.from, value: samples[0].value },
    { t: timeRange.to, value: samples[0].value }
  ];
};

type TrendFreshnessReference = { to: Date };

const trendSamplesAreFresh = (samples: TrendSample[], timeRange: TrendFreshnessReference, staleMinutes = DEVICE_STALE_MINUTES): boolean => {
  const latest = samples.at(-1);
  if (!latest) {
    return false;
  }
  const rangeEndMs = timeRange.to.getTime();
  if (Number.isNaN(rangeEndMs)) {
    return false;
  }
  const ageMinutes = (rangeEndMs - latest.t.getTime()) / 60000;
  return ageMinutes >= 0 && ageMinutes <= staleMinutes;
};

export const metricSeriesHasTrend = (data: MetricsSeriesResponse | null, timeRange: TrendFreshnessReference): boolean =>
  Boolean(data?.series?.some((series) => {
    const samples = trendSamplesFromMetricSeries(series);
    return samples.length >= 2 && trendSamplesAreFresh(samples, timeRange);
  }));

export const metricSeriesHasSamples = (data: MetricsSeriesResponse | null, timeRange: TrendFreshnessReference): boolean =>
  Boolean(data?.series?.some((series) => {
    const samples = trendSamplesFromMetricSeries(series);
    return samples.length > 0 && trendSamplesAreFresh(samples, timeRange);
  }));

const boundedPercent = (value: number): number => Math.max(0, Math.min(100, value));

/**
 * Build the canonical, CPU-ranked device set shared by the org health table
 * and the CPU chart. The metrics intent is bounded to 12 devices; the chart
 * consumes the first five and the table consumes the full returned set.
 */
export const rankedCpuMetricSeries = ({
  series,
  mode,
  timeRange,
  inventoryDeviceCanonicalIds,
  inventoryDeviceIds,
  deviceInventoryLoaded,
  orgDeviceNameById,
  limit = ORG_LATEST_HEALTH_DEVICE_LIMIT
}: {
  series: MetricsSeriesResponse['series'];
  mode: 'range' | 'instant';
  timeRange: { from: Date; to: Date };
  inventoryDeviceCanonicalIds: ReadonlyMap<string, string>;
  inventoryDeviceIds: ReadonlySet<string>;
  deviceInventoryLoaded: boolean;
  orgDeviceNameById: ReadonlyMap<string, string>;
  limit?: number;
}): TrendSeries[] => {
  const rangeSamplesByDevice = new Map<string, { label: string; samplesByTime: Map<number, number[]> }>();
  const instantSeriesByDevice = new Map<string, TrendSeries>();

  for (const metricSeries of series) {
    const labels = metricSeries.labels ?? {};
    const rawDeviceId = deviceIdFromMetricLabels(labels);
    if (!rawDeviceId) continue;
    const canonicalDeviceId = canonicalInventoryDeviceId(rawDeviceId, inventoryDeviceCanonicalIds);
    if (
      deviceInventoryLoaded &&
      !inventoryDeviceIds.has(rawDeviceId) &&
      !inventoryDeviceCanonicalIds.has(canonicalDeviceId)
    ) {
      continue;
    }

    const metricIdentity = metricSeriesDeviceIdentity(labels, inventoryDeviceCanonicalIds, orgDeviceNameById);
    const rawSamples = trendSamplesFromMetricSeries(metricSeries);
    if (rawSamples.length === 0) continue;
    // A single range sample is expanded to the requested range for chart
    // rendering below. Validate freshness first, while its original timestamp
    // is still available, so an old sample cannot become artificially fresh.
    if (mode === 'range' && !trendSamplesAreFresh(rawSamples, timeRange)) continue;
    const samples = (mode === 'range' ? trendSamplesForRangeDisplay(metricSeries, timeRange) : rawSamples).map((sample) => ({
      ...sample,
      value: boundedPercent(sample.value)
    }));
    if (samples.length === 0) continue;

    if (mode === 'range') {
      let bucket = rangeSamplesByDevice.get(metricIdentity.id);
      if (!bucket) {
        bucket = { label: metricIdentity.label, samplesByTime: new Map<number, number[]>() };
        rangeSamplesByDevice.set(metricIdentity.id, bucket);
      }
      for (const sample of samples) {
        const timestamp = sample.t.getTime();
        const valuesAtTimestamp = bucket.samplesByTime.get(timestamp) ?? [];
        valuesAtTimestamp.push(sample.value);
        bucket.samplesByTime.set(timestamp, valuesAtTimestamp);
      }
      continue;
    }

    const values = samples.map((sample) => sample.value);
    const candidate: TrendSeries = {
      id: metricIdentity.id,
      label: metricIdentity.label,
      values: samples,
      summaryValue: finiteAverage(values),
      peakValue: finiteMax(values)
    };
    const previous = instantSeriesByDevice.get(metricIdentity.id);
    if (!previous || candidate.summaryValue > previous.summaryValue || candidate.peakValue > previous.peakValue) {
      instantSeriesByDevice.set(metricIdentity.id, candidate);
    }
  }

  const candidates = mode === 'range'
    ? Array.from(rangeSamplesByDevice.entries()).flatMap(([id, bucket]) => {
        const values = Array.from(bucket.samplesByTime.entries())
          .sort(([left], [right]) => left - right)
          .map(([timestamp, valuesAtTimestamp]) => ({
            t: new Date(timestamp),
            value: finiteAverage(valuesAtTimestamp)
          }));
        if (values.length === 0) return [];
        return [{
          id,
          label: bucket.label,
          values,
          summaryValue: finiteAverage(values.map((sample) => sample.value)),
          peakValue: finiteMax(values.map((sample) => sample.value))
        } satisfies TrendSeries];
      })
    : Array.from(instantSeriesByDevice.values());

  return candidates
    .sort((left, right) =>
      right.summaryValue - left.summaryValue ||
      right.peakValue - left.peakValue ||
      left.label.localeCompare(right.label) ||
      left.id.localeCompare(right.id)
    )
    .slice(0, limit);
};

export const counterIncreaseFromSamples = (samples: TrendSample[]): number => {
  if (samples.length < 2) {
    return 0;
  }
  let increase = 0;
  let previous = samples[0].value;
  for (const sample of samples.slice(1)) {
    if (sample.value >= previous) {
      increase += sample.value - previous;
    } else {
      // A decrease indicates a counter reset. Count the value accumulated
      // after the reset instead of dropping that interval entirely.
      increase += Math.max(0, sample.value);
    }
    previous = sample.value;
  }
  return increase;
};

export const stpRangeDisplayValue = (
  rangeAvailable: boolean,
  rangeTop: { changes: number } | null
): number | null => (rangeAvailable ? Math.round(rangeTop?.changes ?? 0) : null);

const formatDurationCompact = (seconds: number | null): string => {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) {
    return 'n/a';
  }
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const bgpStateLabel = (value: number | undefined): string => {
  switch (value) {
    case 1:
      return 'Idle';
    case 2:
      return 'Connect';
    case 3:
      return 'Active';
    case 4:
      return 'OpenSent';
    case 5:
      return 'OpenConfirm';
    case 6:
      return 'Established';
    default:
      return 'Unknown';
  }
};

const bgpStateClassName = (state: string): string => {
  const normalized = state.trim().toLowerCase();
  if (normalized === 'established' || normalized === 'estab' || normalized === 'up') {
    return 'telemetry-inspector__state-split-up';
  }
  if (normalized === 'active' || normalized === 'connect' || normalized === 'opensent' || normalized === 'openconfirm' || normalized === 'unknown') {
    return 'telemetry-inspector__state-split-operational-down';
  }
  return 'telemetry-inspector__state-split-down';
};

const ospfStateLabel = (value: number | undefined): string => {
  switch (value) {
    case 1:
      return 'Down';
    case 2:
      return 'Attempt';
    case 3:
      return 'Init';
    case 4:
      return '2-Way';
    case 5:
      return 'Exchange Start';
    case 6:
      return 'Exchange';
    case 7:
      return 'Loading';
    case 8:
      return 'Full';
    default:
      return 'Unknown';
  }
};

const signalTimestampMs = (value?: string): number => {
  const timestamp = new Date(value ?? '').getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const signalRowDeviceId = (row: Pick<Signal, 'deviceId' | 'targetId'> | Pick<SignalFeature, 'deviceId' | 'targetId'>): string => {
  return row.deviceId?.trim() || deviceIdFromTargetId(row.targetId) || '';
};

export const isCurrentInventorySignalRow = (
  row: Pick<Signal, 'deviceId' | 'targetId'> | Pick<SignalFeature, 'deviceId' | 'targetId'>,
  inventoryDeviceIds: Set<string>,
  inventoryLoaded: boolean
): boolean => {
  if (!inventoryLoaded) {
    return true;
  }
  const deviceId = signalRowDeviceId(row);
  if (!deviceId) {
    return false;
  }
  return inventoryDeviceIds.has(deviceId);
};

export const routingTableTotal = ({
  deviceInventoryLoaded,
  detailRowCount,
  detailTruncated,
  aggregateTotal,
  exactTotal
}: {
  deviceInventoryLoaded: boolean;
  detailRowCount: number;
  detailTruncated: boolean;
  aggregateTotal?: number;
  exactTotal?: number;
}): number | undefined => {
  // The compact aggregate endpoint is not inventory-scoped. Once inventory is
  // loaded, a filtered detail count is reliable only after all pages arrive.
  if (deviceInventoryLoaded) {
    return detailTruncated ? undefined : detailRowCount;
  }
  return aggregateTotal ?? exactTotal;
};

const routingAdjacencyKey = (row: Pick<Signal, 'deviceId' | 'instance' | 'targetId'>): string => {
  const deviceId = signalRowDeviceId(row);
  const instance = row.instance?.trim() ?? '';
  if (deviceId && instance) {
    return `${deviceId}\x00${instance}`;
  }
  return row.targetId?.trim() || deviceId || instance;
};

const recordFromUnknown = (value: unknown): Record<string, unknown> => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
};

const topologyString = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
};

const topologyRecordProperties = (value: Record<string, unknown>): Record<string, unknown> => {
  const properties = recordFromUnknown(value.properties);
  if (Object.keys(properties).length > 0) {
    return properties;
  }
  return recordFromUnknown(value.props);
};

const topologyRoutingEdgeIsAdjacency = (labels: Record<string, string>, properties: Record<string, unknown>): boolean => {
  const family = (labels.family || topologyString(properties.family)).toLowerCase();
  const relation = (labels.relation || topologyString(properties.relation)).toLowerCase();
  const level = (labels.level || topologyString(properties.level)).toLowerCase();
  if (family && family !== 'control') {
    return false;
  }
  if (relation === 'connects') {
    return true;
  }
  return !relation && level === 'device_link';
};

const topologyRoutingPeerFromLabelsAndProperties = (labels: Record<string, string>, properties: Record<string, unknown>): string => {
  const candidates = [
    labels.neighbor,
    labels.neighbor_ip,
    labels.neighbor_id,
    labels.peer,
    labels.peer_addr,
    labels.peer_address,
    labels.remote,
    labels.remote_device,
    labels.remote_ip,
    labels.dst_addr,
    labels.router_id,
    labels.system_id,
    labels.neighbor_system_id,
    labels.session_id,
    properties.neighbor,
    properties.neighbor_ip,
    properties.neighbor_id,
    properties.peer,
    properties.peer_addr,
    properties.peer_address,
    properties.remote,
    properties.remote_device,
    properties.remote_ip,
    properties.dst_addr,
    properties.router_id,
    properties.system_id,
    properties.neighbor_system_id,
    properties.session_id
  ];
  for (const candidate of candidates) {
    const value = topologyString(candidate);
    if (value && !value.startsWith('ref:')) {
      return value;
    }
  }
  return '';
};

const ospfSignalPeer = (row: Pick<Signal, 'instance' | 'targetId' | 'attributes'>): string => {
  const labels = stringRecordFromUnknown(row.attributes?.labels);
  return (
    labels.peer ||
    labels.peer_addr ||
    labels.peer_address ||
    labels.neighbor ||
    labels.neighbor_ip ||
    labels.ospf_neighbor_ip ||
    labels.remote_ip ||
    row.instance?.trim() ||
    row.targetId?.trim() ||
    ''
  );
};

const routingDevicePeerKey = (deviceId: string, peer: string): string => `${deviceId}\x00${peer}`;
const routingDeviceInterfaceKey = (deviceId: string, interfaceName: string): string => `${deviceId}\x00${interfaceName}`;

const ROUTING_STATE_LABELS = new Set([
  'active',
  'degraded',
  'down',
  'estab',
  'established',
  'full',
  'idle',
  'init',
  'loading',
  'start',
  'unknown',
  'up'
]);

const routingInterfaceLabel = (...candidates: Array<string | undefined>): string => {
  for (const candidate of candidates) {
    const value = (candidate ?? '').trim();
    if (value && !ROUTING_STATE_LABELS.has(value.toLowerCase())) {
      return value;
    }
  }
  return '';
};

const ospfSignalInterface = (row: Pick<Signal, 'attributes'>): string => {
  const labels = stringRecordFromUnknown(row.attributes?.labels);
  return routingInterfaceLabel(labels.local_interface, labels.if_name, labels.ifName, labels.ifDescr, labels.interface);
};

const topologyRoutingLocalInterface = (
  row: Pick<TopologyRoutingAdjacencyRow, 'attributes'>
): string => {
  const labels = stringRecordFromUnknown(row.attributes?.labels);
  return routingInterfaceLabel(labels.local_interface, labels.if_name, labels.ifName, labels.ifDescr, labels.interface);
};

const topologyRoutingLocalIP = (
  row: Pick<TopologyRoutingAdjacencyRow, 'attributes'>
): string => {
  const labels = stringRecordFromUnknown(row.attributes?.labels);
  return labels.local_ip?.trim() || '';
};

type OspfTopologyMatchMaps = {
  byPeer: Map<string, TopologyRoutingAdjacencyRow | null>;
  byInterface: Map<string, TopologyRoutingAdjacencyRow | null>;
  byLocalIP: Map<string, TopologyRoutingAdjacencyRow | null>;
  byEndpointIP: Map<string, TopologyRoutingAdjacencyRow | null>;
};

export const buildOspfTopologyMatchMaps = (
  rows: TopologyRoutingAdjacencyRow[]
): OspfTopologyMatchMaps => {
  const byPeer = new Map<string, TopologyRoutingAdjacencyRow | null>();
  const byInterface = new Map<string, TopologyRoutingAdjacencyRow | null>();
  const byLocalIP = new Map<string, TopologyRoutingAdjacencyRow | null>();
  const byEndpointIP = new Map<string, TopologyRoutingAdjacencyRow | null>();
  const addEndpointIPMatch = (endpointIP: string, row: TopologyRoutingAdjacencyRow) => {
    if (!endpointIP) {
      return;
    }
    const previousByEndpoint = byEndpointIP.get(endpointIP);
    if (previousByEndpoint === null) {
      return;
    }
    if (
      previousByEndpoint &&
      routingAdjacencyAreaEquivalenceKey(previousByEndpoint.area) !== routingAdjacencyAreaEquivalenceKey(row.area)
    ) {
      byEndpointIP.set(endpointIP, null);
    } else if (
      shouldReplaceRoutingHealthSample(
        previousByEndpoint ? { up: previousByEndpoint.up, timestamp: signalTimestampMs(previousByEndpoint.observedAt) } : undefined,
        { up: row.up, timestamp: signalTimestampMs(row.observedAt) }
      )
    ) {
      byEndpointIP.set(endpointIP, row);
    }
  };
  for (const row of rows) {
    if (row.protocol !== 'ospf') {
      continue;
    }
    const peerKey = routingDevicePeerKey(row.deviceId, row.peer);
    const previous = byPeer.get(peerKey);
    if (previous !== null) {
      if (previous && routingAdjacencyAreaEquivalenceKey(previous.area) !== routingAdjacencyAreaEquivalenceKey(row.area)) {
        byPeer.set(peerKey, null);
      } else if (
        shouldReplaceRoutingHealthSample(
          previous ? { up: previous.up, timestamp: signalTimestampMs(previous.observedAt) } : undefined,
          { up: row.up, timestamp: signalTimestampMs(row.observedAt) }
        )
      ) {
        byPeer.set(peerKey, row);
      }
    }
    const localInterface = topologyRoutingLocalInterface(row);
    if (localInterface) {
      const interfaceKey = routingDeviceInterfaceKey(row.deviceId, localInterface);
      const previousByInterface = byInterface.get(interfaceKey);
      if (previousByInterface !== null) {
        if (
          previousByInterface &&
          (previousByInterface.peer !== row.peer ||
            routingAdjacencyAreaEquivalenceKey(previousByInterface.area) !== routingAdjacencyAreaEquivalenceKey(row.area))
        ) {
          byInterface.set(interfaceKey, null);
        } else if (
          shouldReplaceRoutingHealthSample(
            previousByInterface ? { up: previousByInterface.up, timestamp: signalTimestampMs(previousByInterface.observedAt) } : undefined,
            { up: row.up, timestamp: signalTimestampMs(row.observedAt) }
          )
        ) {
          byInterface.set(interfaceKey, row);
        }
      }
    }
    const localIP = topologyRoutingLocalIP(row);
    addEndpointIPMatch(row.peer, row);
    if (!localIP) {
      continue;
    }
    const previousByLocalIP = byLocalIP.get(localIP);
    if (previousByLocalIP === null) {
      addEndpointIPMatch(localIP, row);
      continue;
    }
    if (
      previousByLocalIP &&
      routingAdjacencyAreaEquivalenceKey(previousByLocalIP.area) !== routingAdjacencyAreaEquivalenceKey(row.area)
    ) {
      byLocalIP.set(localIP, null);
    } else if (
      shouldReplaceRoutingHealthSample(
        previousByLocalIP ? { up: previousByLocalIP.up, timestamp: signalTimestampMs(previousByLocalIP.observedAt) } : undefined,
        { up: row.up, timestamp: signalTimestampMs(row.observedAt) }
      )
    ) {
      byLocalIP.set(localIP, row);
    }
    addEndpointIPMatch(localIP, row);
  }
  return { byPeer, byInterface, byLocalIP, byEndpointIP };
};

export const findOspfTopologyMatch = (
  matchMaps: OspfTopologyMatchMaps,
  deviceId: string,
  peer: string,
  localInterface: string
): TopologyRoutingAdjacencyRow | null | undefined => {
  const peerMatch = matchMaps.byPeer.get(routingDevicePeerKey(deviceId, peer));
  if (peerMatch !== undefined) {
    return peerMatch;
  }
  const interfaceMatch = localInterface ? matchMaps.byInterface.get(routingDeviceInterfaceKey(deviceId, localInterface)) : undefined;
  if (interfaceMatch !== undefined) {
    return interfaceMatch;
  }
  const localIPMatch = peer ? matchMaps.byLocalIP.get(peer) : undefined;
  if (localIPMatch !== undefined) {
    return localIPMatch;
  }
  return peer ? matchMaps.byEndpointIP.get(peer) : undefined;
};

type BuildOrgOspfAdjacencyRowsParams = {
  deviceInventoryLoaded: boolean;
  freshnessReferenceTime?: Date;
  inventoryDeviceIds: Set<string>;
  orgDeviceNameById: Map<string, string>;
  orgOspfStatusItems: Signal[];
  routingAdjacencyTopologyRows: TopologyRoutingAdjacencyRow[];
};

export const buildOrgOspfAdjacencyRows = ({
  deviceInventoryLoaded,
  freshnessReferenceTime,
  inventoryDeviceIds,
  orgDeviceNameById,
  orgOspfStatusItems,
  routingAdjacencyTopologyRows
}: BuildOrgOspfAdjacencyRowsParams): OrgOspfAdjacencyRow[] => {
  const latestByNeighbor = new Map<string, Signal>();
  const seenNeighborKeys = new Set<string>();
  const freshRoutingAdjacencyTopologyRows = freshSignalRows(routingAdjacencyTopologyRows, DEVICE_STALE_MINUTES, freshnessReferenceTime);
  const topologyMatchMaps = buildOspfTopologyMatchMaps(freshRoutingAdjacencyTopologyRows);
  for (const row of orgOspfStatusItems) {
    if (!isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
      continue;
    }
    if (!isFreshSignalRow(row, DEVICE_STALE_MINUTES, freshnessReferenceTime)) {
      continue;
    }
    const key = signalRoutingAdjacencyDedupeKey(row, 'OSPF');
    if (!key.trim()) {
      continue;
    }
    const previous = latestByNeighbor.get(key);
    if (!previous || signalTimestampMs(row.observedAt) >= signalTimestampMs(previous.observedAt)) {
      latestByNeighbor.set(key, row);
    }
  }
  const grouped = new Map<string, { device: string; area: string; up: number; total: number; worstState: string; hasDown: boolean }>();
  for (const row of latestByNeighbor.values()) {
    const deviceId = signalRowDeviceId(row);
    const labels = stringRecordFromUnknown(row.attributes?.labels);
    const signalPeer = ospfSignalPeer(row);
    const signalInterface = ospfSignalInterface(row);
    const topologyMatch = findOspfTopologyMatch(topologyMatchMaps, deviceId, signalPeer, signalInterface);
    const area = routingAdjacencyAreaFromLabels(labels) || topologyMatch?.area || UNKNOWN_ROUTING_AREA;
    const key = `${deviceId}:${routingAdjacencyAreaEquivalenceKey(area)}`;
    const existing = grouped.get(key) ?? {
      device: signalDeviceDisplayName(row, orgDeviceNameById),
      area,
      up: 0,
      total: 0,
      worstState: 'Full',
      hasDown: false
    };
    existing.total += 1;
    if (row.value === 8) {
      existing.up += 1;
    } else {
      existing.hasDown = true;
      existing.worstState = ospfStateLabel(row.value);
    }
    seenNeighborKeys.add(signalRoutingAdjacencyDedupeKey(row, 'OSPF'));
    if (topologyMatch) {
      seenNeighborKeys.add(topologyRoutingAdjacencyPeerKey(topologyMatch));
    }
    grouped.set(key, existing);
  }
  for (const row of freshRoutingAdjacencyTopologyRows) {
    if (!isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
      continue;
    }
    if (row.protocol !== 'ospf') {
      continue;
    }
    const neighborKeyCandidates = topologyRoutingAdjacencyPeerKeyCandidates(row);
    if (neighborKeyCandidates.some((neighborKey) => seenNeighborKeys.has(neighborKey))) {
      continue;
    }
    for (const neighborKey of neighborKeyCandidates) {
      seenNeighborKeys.add(neighborKey);
    }
    const area = row.area || UNKNOWN_ROUTING_AREA;
    const key = `${row.deviceId}:${routingAdjacencyAreaEquivalenceKey(area)}`;
    const existing = grouped.get(key) ?? {
      device: signalDeviceDisplayName(row, orgDeviceNameById),
      area,
      up: 0,
      total: 0,
      worstState: 'Full',
      hasDown: false
    };
    existing.total += 1;
    if (row.up) {
      existing.up += 1;
    } else {
      existing.hasDown = true;
      existing.worstState = row.state || 'Degraded';
    }
    grouped.set(key, existing);
  }
  return Array.from(grouped.entries())
    .map(([id, row]) => ({
      id,
      device: row.device,
      area: row.area,
      up: row.up,
      total: row.total,
      state: row.hasDown ? 'Degraded' : 'Full'
    }))
    .sort((left, right) => left.up - left.total - (right.up - right.total) || left.device.localeCompare(right.device));
};

const numberFromUnknown = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value.trim().replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const bgpPrefixCountFromAttributes = (attributes: Record<string, unknown> | null | undefined): number | null => {
  const labels = stringRecordFromUnknown(attributes?.labels);
  const properties = recordFromUnknown(attributes?.properties);
  const candidates = [
    attributes?.prefixes,
    attributes?.prefix_count,
    attributes?.prefixCount,
    attributes?.accepted_prefixes,
    attributes?.acceptedPrefixes,
    attributes?.received_prefixes,
    attributes?.receivedPrefixes,
    attributes?.advertised_prefixes,
    attributes?.advertisedPrefixes,
    properties.prefixes,
    properties.prefix_count,
    properties.prefixCount,
    properties.accepted_prefixes,
    properties.acceptedPrefixes,
    properties.received_prefixes,
    properties.receivedPrefixes,
    properties.advertised_prefixes,
    properties.advertisedPrefixes,
    labels.prefixes,
    labels.prefix_count,
    labels.prefixCount,
    labels.accepted_prefixes,
    labels.acceptedPrefixes,
    labels.received_prefixes,
    labels.receivedPrefixes,
    labels.advertised_prefixes,
    labels.advertisedPrefixes
  ];
  for (const candidate of candidates) {
    const value = numberFromUnknown(candidate);
    if (value != null) {
      return value;
    }
  }
  return null;
};

const inventoryInterfaceRefParts = (value: unknown): { deviceId: string; interfaceName: string } | null => {
  const ref = topologyString(value);
  const prefix = 'ref:inventory.interface:';
  const index = ref.indexOf(prefix);
  if (index < 0) {
    return null;
  }
  const rest = ref.slice(index + prefix.length);
  const hashIndex = rest.indexOf('#');
  if (hashIndex <= 0 || hashIndex >= rest.length - 1) {
    return null;
  }
  return {
    deviceId: rest.slice(0, hashIndex),
    interfaceName: rest.slice(hashIndex + 1)
  };
};

const topologyDeviceIdFromEdge = (edge: Record<string, unknown>): string => {
  const candidates = [topologyString(edge.fromId), topologyString(edge.toId), topologyString(edge.sourceId), topologyString(edge.targetId)];
  for (const candidate of candidates) {
    const protocolIndex = candidate.indexOf('ref:derived:proto:');
    if (protocolIndex >= 0) {
      const rest = candidate.slice(protocolIndex + 'ref:derived:proto:'.length);
      const hashIndex = rest.indexOf('#');
      return hashIndex >= 0 ? rest.slice(0, hashIndex) : rest;
    }
    const deviceIndex = candidate.indexOf('ref:inventory.device:');
    if (deviceIndex >= 0) {
      const rest = candidate.slice(deviceIndex + 'ref:inventory.device:'.length);
      const hashIndex = rest.indexOf('#');
      return hashIndex >= 0 ? rest.slice(0, hashIndex) : rest;
    }
    const vpnIndex = candidate.indexOf('ref:derived:vpn_connection:');
    if (vpnIndex >= 0) {
      const rest = candidate.slice(vpnIndex + 'ref:derived:vpn_connection:'.length);
      const hashIndex = rest.indexOf('#');
      return hashIndex >= 0 ? rest.slice(0, hashIndex) : rest;
    }
    const interfaceRef = inventoryInterfaceRefParts(candidate);
    if (interfaceRef?.deviceId) {
      return interfaceRef.deviceId;
    }
  }
  return '';
};

const topologyInventoryDeviceIdFromRef = (value: unknown): string => {
  const ref = topologyString(value);
  const deviceIndex = ref.indexOf('ref:inventory.device:');
  if (deviceIndex >= 0) {
    const rest = ref.slice(deviceIndex + 'ref:inventory.device:'.length);
    const hashIndex = rest.indexOf('#');
    return hashIndex >= 0 ? rest.slice(0, hashIndex) : rest;
  }
  const protocolIndex = ref.indexOf('ref:derived:proto:');
  if (protocolIndex >= 0) {
    const rest = ref.slice(protocolIndex + 'ref:derived:proto:'.length);
    const hashIndex = rest.indexOf('#');
    // Some topology providers append the owning inventory device after '#'.
    // Prefer that identity over the protocol/session name (for example
    // isis:CAMPUS or bfd:default).
    if (hashIndex >= 0 && hashIndex < rest.length - 1) {
      const owner = rest.slice(hashIndex + 1).trim();
      if (
        owner &&
        !/^(?:bfd|bgp|isis|ospf):/i.test(owner) &&
        !/^(?:default|neighbor|peer|session|unknown)$/i.test(owner)
      ) {
        return owner;
      }
    }
    return hashIndex >= 0 ? rest.slice(0, hashIndex) : rest;
  }
  return inventoryInterfaceRefParts(ref)?.deviceId ?? '';
};

// Unlike topologyInventoryDeviceIdFromRef, this deliberately ignores protocol
// refs (for example ref:derived:proto:bfd:default). Those identify a session,
// not the inventory device that owns it.
const topologyExplicitInventoryDeviceIdFromRef = (value: unknown): string => {
  const ref = topologyString(value);
  const deviceIndex = ref.indexOf('ref:inventory.device:');
  if (deviceIndex >= 0) {
    const rest = ref.slice(deviceIndex + 'ref:inventory.device:'.length);
    const hashIndex = rest.indexOf('#');
    return hashIndex >= 0 ? rest.slice(0, hashIndex) : rest;
  }
  return inventoryInterfaceRefParts(ref)?.deviceId ?? '';
};

const topologyGraphPayload = (payload: TopologyV2ResolveResponse | null | undefined): Record<string, unknown> => {
  return recordFromUnknown((payload as Record<string, unknown> | null | undefined)?.graph);
};

const topologyGraphGeneratedAt = (payload: TopologyV2ResolveResponse | null | undefined): string => {
  const graph = topologyGraphPayload(payload);
  return topologyString(graph.generatedAt) || topologyString((payload as Record<string, unknown> | null | undefined)?.generatedAt);
};

const topologySitesResolveResult = (
  payload: RoutingAdjacencyTopologyData | undefined
): TopologySitesResolveResult<TopologyV2ResolveResponse> | null => {
  if (!payload || Array.isArray(payload)) {
    return null;
  }
  const candidate = payload as Partial<TopologySitesResolveResult<TopologyV2ResolveResponse>>;
  return Array.isArray(candidate.payloads) && typeof candidate.failedCount === 'number' && typeof candidate.totalCount === 'number'
    ? (candidate as TopologySitesResolveResult<TopologyV2ResolveResponse>)
    : null;
};

const topologyPayloadsFromResolveData = (
  payload: RoutingAdjacencyTopologyData | undefined
): TopologyV2ResolveResponse | TopologyV2ResolveResponse[] | null => {
  const siteResult = topologySitesResolveResult(payload);
  if (siteResult) {
    return siteResult.payloads;
  }
  if (!payload || Array.isArray(payload)) {
    return payload ?? null;
  }
  return payload as TopologyV2ResolveResponse;
};

const topologyRoutingRowsFromResolveResponse = ({
  payload,
  deviceNameById,
  selectedDeviceId,
  inventoryDeviceIds,
  inventoryLoaded
}: {
  payload: TopologyV2ResolveResponse | null | undefined;
  deviceNameById: Map<string, string>;
  selectedDeviceId: string;
  inventoryDeviceIds: Set<string>;
  inventoryLoaded: boolean;
}): TopologyRoutingAdjacencyRow[] => {
  const graph = topologyGraphPayload(payload);
  const edges = Array.isArray(graph.edges) ? graph.edges : [];
  const generatedAt = topologyGraphGeneratedAt(payload);
  const rows: TopologyRoutingAdjacencyRow[] = [];
  const ospfDeviceLinkDirections = new Set<string>();
  const ospfDeviceLinkRowKeys = new Set<string>();
  const ospfDeviceLinkDirectionKey = ({
    fromDeviceId,
    toDeviceId,
    localIP,
    neighborIP,
    area
  }: {
    fromDeviceId: string;
    toDeviceId: string;
    localIP: string;
    neighborIP: string;
    area: string;
  }): string =>
    [
      fromDeviceId,
      toDeviceId,
      localIP,
      neighborIP,
      routingAdjacencyAreaEquivalenceKey(area)
    ].join('\x00');
  const ospfDeviceLinkRowKey = ({
    fromDeviceId,
    toDeviceId,
    deviceId,
    peer,
    area
  }: {
    fromDeviceId: string;
    toDeviceId: string;
    deviceId: string;
    peer: string;
    area: string;
  }): string =>
    [
      fromDeviceId,
      toDeviceId,
      deviceId,
      peer,
      routingAdjacencyAreaEquivalenceKey(area)
    ].join('\x00');
  for (const rawEdge of edges) {
    const edge = recordFromUnknown(rawEdge);
    const labels = stringRecordFromUnknown(edge.labels);
    const properties = topologyRecordProperties(edge);
    const rawDomain = (labels.domain || topologyString(properties.domain)).toLowerCase();
    const domain = rawDomain === 'is-is' ? 'isis' : rawDomain;
    const edgeLevel = (labels.level || topologyString(properties.level)).toLowerCase();
    if (domain !== 'ospf' || edgeLevel !== 'device_link') {
      continue;
    }
    if (!topologyRoutingEdgeIsAdjacency(labels, properties)) {
      continue;
    }
    const deviceId = topologyDeviceIdFromEdge(edge);
    const rawState = topologyString(properties.state) || labels.state;
    if (!rawState || rawState.toLowerCase() === 'unknown') {
      continue;
    }
    const peer = topologyRoutingPeerFromLabelsAndProperties(labels, properties);
    if (!peer) {
      continue;
    }
    const fromDeviceId = topologyInventoryDeviceIdFromRef(edge.fromId);
    const toDeviceId = topologyInventoryDeviceIdFromRef(edge.toId);
    if (!deviceId || !fromDeviceId || !toDeviceId) {
      continue;
    }
    const area =
      topologyString(properties.area) ||
      topologyString(properties.ospf_area) ||
      routingAdjacencyAreaFromLabels(labels);
    if (
      (!selectedDeviceId || selectedDeviceId === deviceId) &&
      (!inventoryLoaded || inventoryDeviceIds.has(deviceId))
    ) {
      ospfDeviceLinkRowKeys.add(ospfDeviceLinkRowKey({ fromDeviceId, toDeviceId, deviceId, peer, area }));
    }
    const localIP = topologyString(properties.local_ip);
    const neighborIP = topologyString(properties.neighbor_ip);
    if (localIP && neighborIP) {
      ospfDeviceLinkDirections.add(ospfDeviceLinkDirectionKey({ fromDeviceId, toDeviceId, localIP, neighborIP, area }));
    }
  }
  const appendRow = ({
    protocol,
    deviceId,
    peer,
    area,
    state,
    rawState,
    targetId,
    instance,
    properties,
    localInterface,
    localIP
  }: {
    protocol: RoutingAdjacencyProtocol;
    deviceId: string;
    peer: string;
    area: string;
    state: string;
    rawState: string;
    targetId: string;
    instance: string;
    properties: Record<string, unknown>;
    localInterface: string;
    localIP: string;
  }) => {
    rows.push({
      id: `${protocol}\x00${deviceId}\x00${peer}\x00${area}`,
      protocol,
      deviceId,
      deviceName: deviceNameById.get(deviceId) ?? deviceId,
      peer,
      area,
      state,
      up: topologyRoutingStateIsUp(protocol, rawState),
      targetId,
      instance,
      observedAt: generatedAt || undefined,
      attributes: {
        source: 'topology_routing_adjacency',
        properties,
        labels: {
          protocol,
          peer,
          area,
          state,
          local_interface: routingInterfaceLabel(localInterface),
          local_ip: localIP,
          instance,
          vrf: topologyString(properties.vrf)
        }
      }
    });
  };
  for (const rawEdge of edges) {
    const edge = recordFromUnknown(rawEdge);
    const labels = stringRecordFromUnknown(edge.labels);
    const properties = topologyRecordProperties(edge);
    const rawDomain = (labels.domain || topologyString(properties.domain)).toLowerCase();
    const domain = rawDomain === 'is-is' ? 'isis' : rawDomain;
    if (domain !== 'bgp' && domain !== 'ospf' && domain !== 'isis' && domain !== 'bfd') {
      continue;
    }
    if (!topologyRoutingEdgeIsAdjacency(labels, properties)) {
      continue;
    }
    const protocol = domain as RoutingAdjacencyProtocol;
    const fromDeviceId = topologyInventoryDeviceIdFromRef(edge.fromId);
    const toDeviceId = topologyInventoryDeviceIdFromRef(edge.toId);
    const topologyDeviceId = topologyDeviceIdFromEdge(edge);
    const explicitFromDeviceId = topologyExplicitInventoryDeviceIdFromRef(edge.fromId);
    const explicitToDeviceId = topologyExplicitInventoryDeviceIdFromRef(edge.toId);
    const labeledDeviceId =
      topologyString(labels.device_id) ||
      topologyString(labels.deviceId) ||
      topologyString(properties.device_id) ||
      topologyString(properties.deviceId);
    const deviceId =
      protocol === 'isis' || protocol === 'bfd'
        ? explicitFromDeviceId || explicitToDeviceId || labeledDeviceId || fromDeviceId || toDeviceId || topologyDeviceId
        : topologyDeviceId;
    if (!deviceId) {
      continue;
    }
    const rawState = topologyString(properties.state) || labels.state;
    if (!rawState || rawState.toLowerCase() === 'unknown') {
      continue;
    }
    const state = topologyRoutingStateLabel(protocol, rawState);
    const peer = topologyRoutingPeerFromLabelsAndProperties(labels, properties);
    if (!peer) {
      continue;
    }
    const instance =
      protocol === 'ospf'
        ? topologyString(properties.local_interface) || peer
        : labels.instance || topologyString(properties.instance) || topologyString(properties.vrf) || 'default';
    const targetId =
      topologyString(properties.target_id) ||
      topologyString(edge.toId) ||
      topologyString(edge.id) ||
      `ref:derived:neighbor:${deviceId}#${peer}`;
    const area =
      protocol === 'ospf'
        ? topologyString(properties.area) ||
          topologyString(properties.ospf_area) ||
          routingAdjacencyAreaFromLabels(labels)
        : '';
    if (
      (!selectedDeviceId || selectedDeviceId === deviceId) &&
      (!inventoryLoaded || inventoryDeviceIds.has(deviceId))
    ) {
      appendRow({
        protocol,
        deviceId,
        peer,
        area,
        state,
        rawState,
        targetId,
        instance,
        properties,
        localInterface: topologyString(properties.local_interface),
        localIP: topologyString(properties.local_ip)
      });
    }
    const reversePeer = topologyString(properties.local_ip);
    const edgeLevel = (labels.level || topologyString(properties.level)).toLowerCase();
    const hasReverseDeviceLink = ospfDeviceLinkDirections.has(ospfDeviceLinkDirectionKey({
      fromDeviceId: toDeviceId,
      toDeviceId: fromDeviceId,
      localIP: topologyString(properties.neighbor_ip),
      neighborIP: topologyString(properties.local_ip),
      area
    }));
    const hasReverseDeviceLinkRow = ospfDeviceLinkRowKeys.has(ospfDeviceLinkRowKey({
      fromDeviceId: toDeviceId,
      toDeviceId: fromDeviceId,
      deviceId: toDeviceId,
      peer: reversePeer,
      area
    }));
    if (
      protocol === 'ospf' &&
      edgeLevel === 'device_link' &&
      fromDeviceId &&
      toDeviceId &&
      toDeviceId !== deviceId &&
      reversePeer &&
      !hasReverseDeviceLink &&
      !hasReverseDeviceLinkRow &&
      (!selectedDeviceId || selectedDeviceId === toDeviceId) &&
      (!inventoryLoaded || inventoryDeviceIds.has(toDeviceId))
    ) {
      const reverseProperties = {
        ...properties,
        local_ip: topologyString(properties.neighbor_ip),
        neighbor_ip: topologyString(properties.local_ip),
        local_interface: ''
      };
      appendRow({
        protocol,
        deviceId: toDeviceId,
        peer: reversePeer,
        area,
        state,
        rawState,
        targetId: topologyString(edge.fromId) || targetId,
        instance: reversePeer,
        properties: reverseProperties,
        localInterface: '',
        localIP: topologyString(reverseProperties.local_ip)
      });
    }
  }
  return rows;
};

export const topologyRoutingRowsFromResolveResponses = ({
  payload,
  deviceNameById,
  selectedDeviceId,
  inventoryDeviceIds,
  inventoryLoaded
}: {
  payload: TopologyV2ResolveResponse | TopologyV2ResolveResponse[] | null | undefined;
  deviceNameById: Map<string, string>;
  selectedDeviceId: string;
  inventoryDeviceIds: Set<string>;
  inventoryLoaded: boolean;
}): TopologyRoutingAdjacencyRow[] => {
  const payloads = Array.isArray(payload) ? payload : payload ? [payload] : [];
  const rows: TopologyRoutingAdjacencyRow[] = [];
  const seen = new Set<string>();
  for (const item of payloads) {
    for (const row of topologyRoutingRowsFromResolveResponse({
      payload: item,
      deviceNameById,
      selectedDeviceId,
      inventoryDeviceIds,
      inventoryLoaded
    })) {
      const key = topologyRoutingAdjacencyRowKey(row);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      rows.push(row);
    }
  }
  return rows;
};

const topologyLagMemberRowsFromResolveResponse = ({
  payload,
  inventoryDeviceIds,
  inventoryLoaded,
  deviceNameById,
  selectedDeviceId
}: {
  payload: TopologyV2ResolveResponse | null | undefined;
  inventoryDeviceIds: Set<string>;
  inventoryLoaded: boolean;
  deviceNameById: Map<string, string>;
  selectedDeviceId: string;
}): TopologyLagMemberRow[] => {
  const graph = topologyGraphPayload(payload);
  const edges = Array.isArray(graph.edges) ? graph.edges : [];
  const generatedAt = topologyGraphGeneratedAt(payload);
  const rows: TopologyLagMemberRow[] = [];
  for (const rawEdge of edges) {
    const edge = recordFromUnknown(rawEdge);
    const labels = stringRecordFromUnknown(edge.labels);
    if ((labels.family || '').toLowerCase() !== 'physical' || (labels.relation || '').toLowerCase() !== 'member_of') {
      continue;
    }
    const member = inventoryInterfaceRefParts(edge.fromId);
    const lag = inventoryInterfaceRefParts(edge.toId);
    if (!member || !lag || member.deviceId !== lag.deviceId) {
      continue;
    }
    const deviceId = member.deviceId;
    if (!deviceId || (selectedDeviceId && selectedDeviceId !== deviceId) || (inventoryLoaded && !inventoryDeviceIds.has(deviceId))) {
      continue;
    }
    rows.push({
      id: `lag_member\x00${deviceId}\x00${lag.interfaceName}\x00${member.interfaceName}`,
      deviceId,
      deviceName: deviceNameById.get(deviceId) ?? deviceId,
      lagName: lag.interfaceName,
      memberName: member.interfaceName,
      targetId: topologyString(edge.fromId) || `ref:inventory.interface:${deviceId}#${member.interfaceName}`,
      observedAt: generatedAt || undefined,
      attributes: {
        source: 'topology_lag_membership',
        labels: {
          lag: lag.interfaceName,
          member: member.interfaceName
        }
      }
    });
  }
  return rows.sort((left, right) => left.deviceName.localeCompare(right.deviceName) || left.lagName.localeCompare(right.lagName) || left.memberName.localeCompare(right.memberName));
};

const topologyVlanIdFromNode = (nodeId: string, labels: Record<string, string>, properties: Record<string, unknown>): string => {
  const explicit =
    topologyString(properties['vlan.id']) ||
    topologyString(properties.vlan_id) ||
    topologyString(properties.vlanId) ||
    topologyString(labels['vlan.id']) ||
    topologyString(labels.vlan_id) ||
    topologyString(labels.vlanId);
  if (explicit) {
    return explicit;
  }
  const instance = topologyString(labels.instance) || topologyString(properties.instance);
  if (instance.toLowerCase().startsWith('vlan:')) {
    return instance.slice(5);
  }
  const nodeMatch = nodeId.match(/(?:^|:)vlan:([^#]+)/i);
  return nodeMatch?.[1] ?? '';
};

const topologyVlanMembershipRowsFromResolveResponse = ({
  payload,
  inventoryDeviceIds,
  inventoryLoaded,
  deviceNameById,
  selectedDeviceId
}: {
  payload: TopologyV2ResolveResponse | null | undefined;
  inventoryDeviceIds: Set<string>;
  inventoryLoaded: boolean;
  deviceNameById: Map<string, string>;
  selectedDeviceId: string;
}): TopologyVlanMembershipRow[] => {
  const graph = topologyGraphPayload(payload);
  const nodes = Array.isArray(graph.nodes) ? graph.nodes : [];
  const edges = Array.isArray(graph.edges) ? graph.edges : [];
  const generatedAt = topologyGraphGeneratedAt(payload);
  const nodesById = new Map<string, Record<string, unknown>>();
  for (const rawNode of nodes) {
    const node = recordFromUnknown(rawNode);
    const nodeId = topologyString(node.id);
    if (nodeId) {
      nodesById.set(nodeId, node);
    }
  }
  const rows: TopologyVlanMembershipRow[] = [];
  const seen = new Set<string>();
  for (const rawEdge of edges) {
    const edge = recordFromUnknown(rawEdge);
    const labels = stringRecordFromUnknown(edge.labels);
    const properties = topologyRecordProperties(edge);
    const family = (labels.family || topologyString(properties.family)).toLowerCase();
    const relation = (labels.relation || topologyString(properties.relation)).toLowerCase();
    if (family !== 'l2' || relation !== 'member_of') {
      continue;
    }
    const fromId = topologyString(edge.fromId);
    const toId = topologyString(edge.toId);
    const fromMember = inventoryInterfaceRefParts(fromId);
    const toMember = inventoryInterfaceRefParts(toId);
    const member = fromMember ?? toMember;
    if (!member) {
      continue;
    }
    const deviceId = member.deviceId;
    if (!deviceId || (selectedDeviceId && selectedDeviceId !== deviceId) || (inventoryLoaded && !inventoryDeviceIds.has(deviceId))) {
      continue;
    }
    const vlanRef = (fromMember ? toId : fromId) || topologyString(properties.vlan_ref) || topologyString(properties.vlanRef);
    const vlanNode = nodesById.get(vlanRef) ?? {};
    const vlanLabels = stringRecordFromUnknown(vlanNode.labels);
    const vlanProperties = topologyRecordProperties(vlanNode);
    const vlanId =
      topologyVlanIdFromNode(vlanRef, vlanLabels, vlanProperties) ||
      topologyString(properties['vlan.id']) ||
      topologyString(properties.vlan_id) ||
      topologyString(properties.vlanId);
    if (!vlanId) {
      continue;
    }
    const vlanName =
      topologyString(vlanProperties['vlan.name']) ||
      topologyString(vlanProperties.vlan_name) ||
      topologyString(vlanProperties.name) ||
      topologyString(properties['vlan.name']) ||
      topologyString(properties.vlan_name);
    const id = `vlan_member\x00${deviceId}\x00${member.interfaceName}\x00${vlanId}`;
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    rows.push({
      id,
      deviceId,
      deviceName: deviceNameById.get(deviceId) ?? deviceId,
      interfaceName: member.interfaceName,
      vlanId,
      vlanName,
      targetId: (fromMember ? fromId : toId) || `ref:inventory.interface:${deviceId}#${member.interfaceName}`,
      observedAt: generatedAt || undefined,
      attributes: {
        source: 'topology_vlan_membership',
        labels: {
          interface: member.interfaceName,
          vlan_id: vlanId,
          vlan_name: vlanName
        }
      }
    });
  }
  return rows.sort(
    (left, right) =>
      left.deviceName.localeCompare(right.deviceName) ||
      left.interfaceName.localeCompare(right.interfaceName) ||
      left.vlanId.localeCompare(right.vlanId, undefined, { numeric: true })
  );
};

const topologyTunnelStateIsUp = (state: string): boolean => {
  const normalized = state.trim().toLowerCase();
  return normalized === 'up' || normalized === 'active' || normalized === 'established' || normalized === 'connected' || normalized === 'installed';
};

const topologyTunnelRowsFromResolveResponse = ({
  payload,
  inventoryDeviceIds,
  inventoryLoaded,
  deviceNameById,
  selectedDeviceId
}: {
  payload: TopologyV2ResolveResponse | null | undefined;
  inventoryDeviceIds: Set<string>;
  inventoryLoaded: boolean;
  deviceNameById: Map<string, string>;
  selectedDeviceId: string;
}): TopologyTunnelRow[] => {
  const graph = topologyGraphPayload(payload);
  const edges = Array.isArray(graph.edges) ? graph.edges : [];
  const generatedAt = topologyGraphGeneratedAt(payload);
  const rows: TopologyTunnelRow[] = [];
  const seen = new Set<string>();
  for (const rawEdge of edges) {
    const edge = recordFromUnknown(rawEdge);
    const labels = stringRecordFromUnknown(edge.labels);
    const properties = recordFromUnknown(edge.properties);
    const family = (labels.family || topologyString(properties.family)).toLowerCase();
    const relation = (labels.relation || topologyString(properties.relation)).toLowerCase();
    if (family !== 'tunnel' || relation !== 'connects') {
      continue;
    }
    const deviceId = topologyDeviceIdFromEdge(edge);
    if (!deviceId || (selectedDeviceId && selectedDeviceId !== deviceId) || (inventoryLoaded && !inventoryDeviceIds.has(deviceId))) {
      continue;
    }
    const tunnelName =
      topologyString(properties.tunnel_name) ||
      topologyString(properties.connection_name) ||
      topologyString(properties.tunnel_interface) ||
      topologyString(edge.fromId) ||
      'unknown-tunnel';
    const state = topologyString(properties.tunnel_state) || topologyString(properties.state) || 'unknown';
    const id = `tunnel\x00${deviceId}\x00${tunnelName}`;
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    rows.push({
      id,
      deviceId,
      deviceName: deviceNameById.get(deviceId) ?? deviceId,
      tunnelName,
      state,
      up: topologyTunnelStateIsUp(state),
      targetId: topologyString(edge.fromId) || `ref:derived:vpn_connection:${deviceId}#${tunnelName}`,
      observedAt: generatedAt || undefined,
      attributes: {
        source: 'topology_vpn_connection',
        labels: {
          tunnel: tunnelName,
          state,
          connection_name: topologyString(properties.connection_name),
          tunnel_type: topologyString(properties.tunnel_type),
          local_inside_ip: topologyString(properties.local_inside_ip),
          remote_inside_ip: topologyString(properties.remote_inside_ip),
          observed_remote_outside_ip: topologyString(properties.observed_remote_outside_ip)
        }
      }
    });
  }
  return rows.sort((left, right) => left.deviceName.localeCompare(right.deviceName) || left.tunnelName.localeCompare(right.tunnelName));
};

const runtimeAccentForStatus = (status?: string): 'default' | 'success' | 'warning' | 'danger' => {
  switch ((status ?? '').toLowerCase()) {
    case 'healthy':
    case 'good':
    case 'reporting':
      return 'success';
    case 'degraded':
    case 'bad':
    case 'missing':
      return 'warning';
    case 'unknown':
      return 'default';
    default:
      return 'default';
  }
};

const formatRuntimeKpiValue = (kpi: RuntimeKpiSummary): string => {
  if (typeof kpi.displayValue === 'string' && kpi.displayValue.trim()) {
    return kpi.displayValue;
  }
  if (typeof kpi.value !== 'number' || Number.isNaN(kpi.value)) {
    return kpi.emptyLabel?.trim() || 'n/a';
  }
  const scaledValue = typeof kpi.scale === 'number' && Number.isFinite(kpi.scale) ? kpi.value * kpi.scale : kpi.value;
  const precision = typeof kpi.precision === 'number' && Number.isFinite(kpi.precision) ? kpi.precision : scaledValue >= 100 ? 0 : 1;
  const suffix =
    kpi.unit === 'percent'
      ? '%'
      : kpi.unit === 'seconds'
        ? 's'
        : kpi.unit === 'rate'
          ? '/s'
          : '';
  return `${scaledValue.toFixed(Math.max(0, precision))}${suffix}`;
};

const coverageAccent = (covered: boolean): 'success' | 'warning' => (covered ? 'success' : 'warning');

const RUNTIME_COVERED_FAMILY_PATTERNS = [
  /^otel_agent_sync_/,
  /^command_agent_/,
  /^connector_vpn_/,
  /^otelcol_/,
  /^otel_collector_/,
  /^otelcol_exporter_/,
  /^otelcol_receiver_/,
  /^otelcol_processor_/,
  /^otelcol_exporter_queue_/,
  /^otelcol_process_/,
  /^process_/,
  /^go_/,
  /^grpc_/,
  /^scrape_/,
  /^exporter_/,
  /^pipeline_/
];

const isRecognizedRuntimeFamily = (name: string): boolean => {
  return RUNTIME_COVERED_FAMILY_PATTERNS.some((pattern) => pattern.test(name));
};

const ageInMinutes = (value?: string | null, referenceTime: Date = new Date(Date.now())): number | null => {
  if (!value) {
    return null;
  }
  const timestamp = new Date(value).getTime();
  const referenceTimestamp = referenceTime.getTime();
  if (Number.isNaN(timestamp) || Number.isNaN(referenceTimestamp)) {
    return null;
  }
  return Math.round((referenceTimestamp - timestamp) / 60000);
};

const freshnessLabel = (
  value?: string | null,
  staleMinutes = DEVICE_STALE_MINUTES,
  referenceTime?: Date
): string => {
  const age = ageInMinutes(value, referenceTime);
  if (age == null) {
    return 'unknown';
  }
  if (age < 0) {
    return 'future';
  }
  if (age < 1) {
    return 'fresh';
  }
  return age > staleMinutes ? `stale ${age}m` : `${age}m ago`;
};

const isFreshSignalRow = (
  row: { observedAt?: string | null },
  staleMinutes = DEVICE_STALE_MINUTES,
  referenceTime?: Date,
): boolean => {
  const age = ageInMinutes(row.observedAt, referenceTime);
  return age != null && age >= 0 && age <= staleMinutes;
};

export const freshSignalRows = <T extends { observedAt?: string | null }>(
  rows: T[],
  staleMinutes = DEVICE_STALE_MINUTES,
  referenceTime?: Date
): T[] => rows.filter((row) => isFreshSignalRow(row, staleMinutes, referenceTime));

const isConnectorConnected = (connector: ConnectorSummary): boolean => {
  const status = (connector.status ?? '').trim().toLowerCase();
  return status === 'connected' || status === 'online' || status === 'registered' || status === 'healthy' || status === 'active';
};

const isConnectorStale = (connector: ConnectorSummary): boolean => {
  if (isConnectorConnected(connector)) {
    return false;
  }
  return (ageInMinutes(connector.lastSeenAt) ?? Number.POSITIVE_INFINITY) > CONNECTOR_STALE_MINUTES;
};

const formatTimestamp = (value?: string): string => {
  if (!value) {
    return 'n/a';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const friendlyTargetLabel = (row: InspectorRow): string => {
  const label = typeof row.attributes?.targetLabel === 'string' ? row.attributes.targetLabel.trim() : '';
  if (label) {
    return label;
  }
  if (row.targetId) {
    const hash = row.targetId.lastIndexOf('#');
    if (hash >= 0 && hash < row.targetId.length - 1) {
      return row.targetId.slice(hash + 1);
    }
  }
  if ((row.targetType === 'node' || row.targetType === 'device') && row.deviceName) {
    return row.deviceName;
  }
  if (row.targetId) {
    return row.targetId;
  }
  return row.deviceName || row.deviceId || 'Unknown target';
};

const hasInterfaceLikeTargetId = (targetId?: string): boolean =>
  typeof targetId === 'string' && targetId.startsWith('ref:inventory.interface:');

const isInterfaceScopedRow = (row: InspectorRow): boolean => row.targetType === 'interface' || hasInterfaceLikeTargetId(row.targetId);

const isDeviceScopedRow = (row: InspectorRow): boolean => row.targetType === 'node' || row.targetType === 'device';

const stringRecordFromUnknown = (value: unknown): Record<string, string> => {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (typeof raw === 'string') {
      out[key] = raw;
    } else if (typeof raw === 'number' || typeof raw === 'boolean') {
      out[key] = String(raw);
    }
  }
  return out;
};

export const signalDeviceDisplayName = (
  row: Pick<Signal, 'deviceId' | 'attributes'> & { deviceName?: string | null },
  deviceNameById: ReadonlyMap<string, string>
): string => {
  const labels = stringRecordFromUnknown(row.attributes?.labels);
  if (row.deviceId?.trim() && !labels.device_id) {
    labels.device_id = row.deviceId.trim();
  }
  const resolved = metricDeviceDisplayName(labels, deviceNameById);
  const rowName = row.deviceName?.trim();
  if (rowName && !deviceIdentifierLike(rowName) && rowName !== labels.device_id) {
    return rowName;
  }
  return resolved;
};

const lacpAggregateNameFromLabels = (labels: Record<string, string>): string =>
  metricsLabelValue(labels, [
    'lacp.selected_agg_name',
    'lacp_selected_agg_name',
    'selected_agg_name',
    'selectedAggName',
    'selected_aggregate_name',
    'selectedAggregateName',
    'agg_name',
    'aggregator_name',
    'channel_name',
    'lag_name',
    'port_channel_name',
    'portChannelName',
    'bundle_name',
    'bundleName'
  ]);

const lacpAggregateIdFromLabels = (labels: Record<string, string>): string =>
  metricsLabelValue(labels, [
    'lacp.selected_agg_id',
    'lacp_selected_agg_id',
    'selected_agg_id',
    'selectedAggId',
    'selected_aggregate_id',
    'selectedAggregateId',
    'selected_aggregator_id',
    'selectedAggregatorId',
    'attached_agg_id',
    'attachedAggId',
    'agg_id',
    'aggregator_id',
    'channel',
    'lag_id',
    'lagId'
  ]);

const lacpMemberNameFromRow = (row: Pick<Signal, 'attributes' | 'targetId' | 'instance'>): string => {
  const labels = stringRecordFromUnknown(row.attributes?.labels);
  return (
    metricsLabelValue(labels, [
      'if_name',
      'ifName',
      'interface',
      'interface_name',
      'interfaceName',
      'member_name',
      'memberName',
      'actor_port',
      'actorPort',
      'target_label',
      'targetLabel'
    ]) ||
    interfaceNameFromTargetId(row.targetId) ||
    row.instance?.trim() ||
    row.targetId?.trim() ||
    'member'
  );
};

const interfaceSignalLabels = (row: Pick<Signal, 'attributes' | 'targetId'>): Record<string, string> => {
  const labels = stringRecordFromUnknown(row.attributes?.labels);
  const ifName = interfaceNameFromTargetId(row.targetId);
  if (ifName && !labels.if_name) {
    labels.if_name = ifName;
  }
  return labels;
};

const IMPORTANT_INTERFACE_NAME_RE =
  /^(po|port-channel|portchannel|ae|bundle-ether|be|lag|tunnel|tu|vlan|vl|loopback|lo|mgmt|management)(?:\b|\d|[-./])/i;

const isLikelyServiceInterface = (row: Pick<Signal, 'attributes' | 'targetId'>): boolean => {
  const labels = interfaceSignalLabels(row);
  const alias = (labels.if_alias ?? labels.ifAlias ?? labels.if_description ?? labels.ifDescription ?? labels.if_descr ?? labels.ifDescr ?? '').trim();
  if (alias) {
    return true;
  }
  const ifName = (labels.if_name ?? labels.ifName ?? interfaceNameFromTargetId(row.targetId) ?? '').trim();
  if (IMPORTANT_INTERFACE_NAME_RE.test(ifName)) {
    return true;
  }
  const ifType = (labels.if_type ?? labels.ifType ?? labels.if_type_name ?? labels.ifTypeName ?? '').trim().toLowerCase();
  return ifType.includes('tunnel') || ifType.includes('propvirtual') || ifType.includes('l3ipvlan') || ifType.includes('ieee8023adlag');
};

export const buildOrgInterfaceCounts = ({
  operRows,
  adminRows,
  inventoryDeviceIds,
  deviceInventoryLoaded,
  orgDeviceNameById,
  freshnessReferenceTime,
  operTruncated = false,
  adminTruncated = false
}: {
  operRows: Signal[];
  adminRows: Signal[];
  inventoryDeviceIds: Set<string>;
  deviceInventoryLoaded: boolean;
  orgDeviceNameById: Map<string, string>;
  freshnessReferenceTime?: Date;
  operTruncated?: boolean;
  adminTruncated?: boolean;
}): OrgInterfaceCounts => {
  const latestByTarget = new Map<string, Signal>();
  const staleTargets = new Set<string>();
  const freshTargets = new Set<string>();
  let lastObservedAt = '';
  for (const row of operRows) {
    if (!isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
      continue;
    }
    const targetKey = row.targetId?.trim() || `${row.deviceId ?? ''}:${row.instance ?? ''}`;
    if (!targetKey.trim()) {
      continue;
    }
    if (!isFreshSignalRow(row, DEVICE_STALE_MINUTES, freshnessReferenceTime)) {
      staleTargets.add(targetKey);
      continue;
    }
    freshTargets.add(targetKey);
    const previous = latestByTarget.get(targetKey);
    const rowTime = new Date(row.observedAt ?? '').getTime();
    const previousTime = new Date(previous?.observedAt ?? '').getTime();
    if (!previous || Number.isNaN(previousTime) || (!Number.isNaN(rowTime) && rowTime >= previousTime)) {
      latestByTarget.set(targetKey, row);
    }
    if (!Number.isNaN(rowTime) && rowTime > signalTimestampMs(lastObservedAt)) {
      lastObservedAt = row.observedAt ?? '';
    }
  }

  const latestAdminByTarget = new Map<string, Signal>();
  for (const row of adminRows) {
    if (!isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
      continue;
    }
    if (!isFreshSignalRow(row, DEVICE_STALE_MINUTES, freshnessReferenceTime)) {
      continue;
    }
    const targetKey = row.targetId?.trim() || `${row.deviceId ?? ''}:${row.instance ?? ''}`;
    if (!targetKey.trim()) {
      continue;
    }
    const previous = latestAdminByTarget.get(targetKey);
    const rowTime = new Date(row.observedAt ?? '').getTime();
    const previousTime = new Date(previous?.observedAt ?? '').getTime();
    if (!previous || Number.isNaN(previousTime) || (!Number.isNaN(rowTime) && rowTime >= previousTime)) {
      latestAdminByTarget.set(targetKey, row);
    }
  }

  let up = 0;
  let adminDown = 0;
  let unexpectedDown = 0;
  let idleEnabledDown = 0;
  let unclassifiedDown = 0;
  let lowerLayerDown = 0;
  let unknown = 0;
  const unexpectedDownDetails: OrgUnexpectedInterface[] = [];
  for (const [targetKey, row] of latestByTarget.entries()) {
    const value = typeof row.value === 'number' ? row.value : Number.NaN;
    if (Number.isNaN(value)) {
      unknown += 1;
    } else if (value === 1) {
      up += 1;
    } else {
      if (value === 7) {
        lowerLayerDown += 1;
        continue;
      }
      const adminValue = latestAdminByTarget.get(targetKey)?.value;
      if (adminValue === 2) {
        adminDown += 1;
      } else if (adminValue === 1) {
        if (isLikelyServiceInterface(row)) {
          unexpectedDown += 1;
          const labels = interfaceSignalLabels(row);
          const deviceId = signalRowDeviceId(row);
          const deviceLabel = signalDeviceDisplayName(row, orgDeviceNameById);
          const targetLabel =
            labels.if_alias ??
            labels.ifAlias ??
            labels.if_name ??
            labels.ifName ??
            interfaceNameFromTargetId(row.targetId) ??
            row.instance ??
            targetKey;
          unexpectedDownDetails.push({
            id: targetKey,
            device: deviceLabel,
            target: targetLabel,
            observedAt: row.observedAt
          });
        } else {
          idleEnabledDown += 1;
        }
      } else {
        unclassifiedDown += 1;
      }
    }
  }

  return {
    total: latestByTarget.size,
    up,
    down: lowerLayerDown + adminDown + unexpectedDown + idleEnabledDown + unclassifiedDown,
    operationallyDown: lowerLayerDown + adminDown + unexpectedDown + idleEnabledDown + unclassifiedDown,
    adminDown,
    unexpectedDown,
    idleEnabledDown,
    unclassifiedDown,
    lowerLayerDown,
    unknown,
    stale: Array.from(staleTargets).filter((targetKey) => !freshTargets.has(targetKey)).length,
    lastObservedAt,
    unexpectedDownDetails: unexpectedDownDetails
      .sort((left, right) => signalTimestampMs(right.observedAt) - signalTimestampMs(left.observedAt) || left.device.localeCompare(right.device))
      .slice(0, 5),
    classificationAvailable: true,
    truncated: operTruncated || adminTruncated
  };
};

export const rawSignalDimensionBindingsForRow = (row: InspectorRow): Record<string, string> => {
  const labels = stringRecordFromUnknown(row.attributes?.labels);
  const bindings: Record<string, string> = {};
  for (const name of RAW_SIGNAL_DIMENSION_VARS) {
    bindings[name] = labels[name]?.trim() || UNSET_RAW_SIGNAL_DIMENSION;
  }
  return bindings;
};

export const rawSignalPanelDimensionBindingsForRow = (row: InspectorRow): Record<string, string> => {
  const available: Record<string, string> = {
    ...rawSignalDimensionBindingsForRow(row),
    signal_instance: rawSignalInstanceBindingForRow(row)
  };
  const selected: Record<string, string> = Object.fromEntries(
    [...RAW_SIGNAL_DIMENSION_VARS, 'signal_instance'].map((name) => [name, UNSET_RAW_SIGNAL_DIMENSION])
  );
  const identity = RAW_SIGNAL_IDENTITY_GROUPS.find((names) =>
    names.every((name) => available[name] !== UNSET_RAW_SIGNAL_DIMENSION)
  );
  for (const name of identity ?? []) {
    selected[name] = available[name];
  }
  return selected;
};

const rawSignalInstanceBindingForRow = (row: InspectorRow): string => {
  const labels = stringRecordFromUnknown(row.attributes?.labels);
  return labels.signal_instance?.trim() || UNSET_RAW_SIGNAL_DIMENSION;
};

const prometheusMatcherValue = (value: string): string =>
  value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');

// Raja still consumes a raw matcher fragment; constrain it to the explicit dimension allowlist during migration.
export const legacyRowMatchersForSignalRow = (row: InspectorRow): string | undefined => {
  const dimensions: Record<string, string> = rawSignalDimensionBindingsForRow(row);
  const fallbackIfName = interfaceNameFromTargetId(row.targetId);
  if (fallbackIfName && dimensions[IF_NAME_VAR] === UNSET_RAW_SIGNAL_DIMENSION) {
    dimensions[IF_NAME_VAR] = fallbackIfName;
  }
  const instance = rawSignalInstanceBindingForRow(row);
  if (instance !== UNSET_RAW_SIGNAL_DIMENSION) {
    dimensions.signal_instance = instance;
  }
  const entries = Object.entries(dimensions)
    .filter(([, value]) => value !== UNSET_RAW_SIGNAL_DIMENSION)
    .sort(([left], [right]) => left.localeCompare(right));
  if (entries.length === 0) {
    return undefined;
  }
  return entries.map(([name, value]) => `,${name}="${prometheusMatcherValue(value)}"`).join('');
};

const targetColumnLabelForFamily = (family: SignalFamily): string => {
  switch (family) {
    case 'interface':
    case 'lacp':
      return 'Interface';
    case 'poe':
      return 'Port/PSE';
    case 'redundancy':
      return 'Target';
    default:
      return 'Target';
  }
};

const instanceColumnLabelForFamily = (family: SignalFamily): string => {
  switch (family) {
    case 'device_health':
      return 'VRF';
    case 'bgp':
    case 'bfd':
      return 'Peer/Session';
    case 'tunnel':
      return 'Tunnel';
    case 'environment':
      return 'Sensor/Component';
    case 'stp':
      return 'Bridge/Port';
    case 'lacp':
      return 'Member/Channel';
    case 'vtp':
      return 'Domain/VLAN';
    case 'poe':
      return 'Port/PSE';
    case 'routing':
      return 'Peer/Route';
    case 'platform_hardware':
      return 'Module/Member';
    case 'qos':
      return 'Policy/Class';
    case 'firewall_sessions':
      return 'Interface/Zone/VSYS';
    case 'security_acl':
      return 'ACL/Rule';
    default:
      return 'Instance';
  }
};

const shouldShowTargetColumnForContext = (stage: SignalStage, family: SignalFamily): boolean => {
  switch (family) {
    case 'interface':
    case 'lacp':
    case 'poe':
    case 'firewall_sessions':
      return true;
    case 'redundancy':
      return stage !== 'signals';
    default:
      return false;
  }
};

const shouldShowInstanceColumnForContext = (stage: SignalStage, family: SignalFamily): boolean => {
  switch (family) {
    case 'bgp':
    case 'bfd':
    case 'tunnel':
      return true;
    case 'environment':
      return stage === 'signals';
    case 'stp':
    case 'lacp':
    case 'vtp':
    case 'poe':
    case 'routing':
    case 'platform_hardware':
    case 'qos':
    case 'firewall_sessions':
    case 'security_acl':
      return stage === 'signals';
    default:
      return false;
  }
};

const baseBindingsForRow = (row: InspectorRow, selectedSiteId: string): Record<string, string | undefined> => {
  const fallbackDeviceId = deviceIdFromTargetId(row.targetId);
  const fallbackIfName = interfaceNameFromTargetId(row.targetId);
  return {
    [SITE_VAR]: selectedSiteId,
    [TARGET_VAR]: row.targetId || undefined,
    [DEVICE_VAR]: row.deviceId || fallbackDeviceId,
    [DEVICE_NAME_VAR]: row.deviceName || undefined,
    [METRIC_VAR]: row.metricName || undefined,
    [INSTANCE_VAR]: row.instance || undefined,
    [IF_NAME_VAR]: fallbackIfName || row.instance || undefined,
    [CONDITION_VAR]: row.key || undefined
  };
};

const varBindingsForRow = (row: InspectorRow, selectedSiteId: string, bindingMode: ChartBindingMode): Record<string, string | undefined> => {
  const bindings = baseBindingsForRow(row, selectedSiteId);
  const panelDimensions = rawSignalPanelDimensionBindingsForRow(row);
  switch (bindingMode) {
    case 'signal_interface':
      return {
        [SITE_VAR]: bindings[SITE_VAR],
        [DEVICE_VAR]: bindings[DEVICE_VAR],
        [DEVICE_NAME_VAR]: bindings[DEVICE_NAME_VAR],
        [METRIC_VAR]: bindings[METRIC_VAR],
        [INSTANCE_VAR]: panelDimensions.signal_instance,
        [ROW_MATCHERS_VAR]: legacyRowMatchersForSignalRow(row),
        ...panelDimensions,
        [IF_NAME_VAR]: rawSignalDimensionBindingsForRow(row)[IF_NAME_VAR]
      };
    case 'signal_device': {
      const deviceDimensions = Object.fromEntries(RAW_SIGNAL_DIMENSION_VARS.map((name) => [name, UNSET_RAW_SIGNAL_DIMENSION]));
      return {
        [SITE_VAR]: bindings[SITE_VAR],
        [DEVICE_VAR]: bindings[DEVICE_VAR],
        [DEVICE_NAME_VAR]: bindings[DEVICE_NAME_VAR],
        [METRIC_VAR]: bindings[METRIC_VAR],
        [INSTANCE_VAR]: UNSET_RAW_SIGNAL_DIMENSION,
        [ROW_MATCHERS_VAR]: legacyRowMatchersForSignalRow(row),
        ...deviceDimensions
      };
    }
    case 'signal_device_instance':
      return {
        [SITE_VAR]: bindings[SITE_VAR],
        [DEVICE_VAR]: bindings[DEVICE_VAR],
        [DEVICE_NAME_VAR]: bindings[DEVICE_NAME_VAR],
        [METRIC_VAR]: bindings[METRIC_VAR],
        [INSTANCE_VAR]: panelDimensions.signal_instance,
        [ROW_MATCHERS_VAR]: legacyRowMatchersForSignalRow(row),
        ...panelDimensions
      };
    case 'feature':
      return {
        [SITE_VAR]: bindings[SITE_VAR],
        [TARGET_VAR]: bindings[TARGET_VAR],
        [METRIC_VAR]: bindings[METRIC_VAR]
      };
    case 'feature_instance':
      return {
        [SITE_VAR]: bindings[SITE_VAR],
        [TARGET_VAR]: bindings[TARGET_VAR],
        [METRIC_VAR]: bindings[METRIC_VAR],
        [INSTANCE_VAR]: bindings[INSTANCE_VAR]
      };
    case 'condition':
      return {
        [SITE_VAR]: bindings[SITE_VAR],
        [TARGET_VAR]: bindings[TARGET_VAR],
        [CONDITION_VAR]: bindings[CONDITION_VAR]
      };
    case 'condition_instance':
      return {
        [SITE_VAR]: bindings[SITE_VAR],
        [TARGET_VAR]: bindings[TARGET_VAR],
        [CONDITION_VAR]: bindings[CONDITION_VAR],
        [INSTANCE_VAR]: bindings[INSTANCE_VAR]
      };
    case 'device':
      return {
        [SITE_VAR]: bindings[SITE_VAR],
        [DEVICE_VAR]: bindings[DEVICE_VAR]
      };
    case 'target':
    default:
      return {
        [SITE_VAR]: bindings[SITE_VAR],
        [TARGET_VAR]: bindings[TARGET_VAR]
      };
  }
};

const widgetForTemplate = ({
  item,
  id,
  title,
  varBindings,
  timeRange,
  dashboardsMockEnabled
}: {
  item: GrafanaTemplateSpec;
  id: string;
  title: string;
  varBindings: Record<string, string | undefined>;
  timeRange: { from: Date; to: Date };
  dashboardsMockEnabled: boolean;
}): DashboardWidgetSpec => ({
  id,
  type: 'grafana_panel_iframe',
  title,
  iframeUrl: buildGrafanaSoloUrl({
    dashUid: item.dashUid,
    panelId: item.panelId,
    title,
    varBindings,
    vars: item.vars,
    from: timeRange.from,
    to: timeRange.to,
    mockMode: dashboardsMockEnabled
  }),
  bindings: {}
});

const withGrafanaVarBindings = (widget: DashboardWidgetSpec, varBindings: Record<string, string | undefined>): DashboardWidgetSpec => {
  if (widget.type !== 'grafana_panel_iframe' || typeof widget.iframeUrl !== 'string') {
    return widget;
  }
  const [baseUrl, query = ''] = widget.iframeUrl.split('?');
  const params = new URLSearchParams(query);
  for (const [varName, value] of Object.entries(varBindings)) {
    if (typeof value === 'string') {
      params.set(`var-${varName}`, value);
    }
  }
  return {
    ...widget,
    iframeUrl: `${baseUrl}?${params.toString()}`
  };
};

const GraphRowSection = ({
  title,
  description,
  widgets,
  refreshSeconds,
  rangePreset,
  onAuthRefresh
}: {
  title: string;
  description: string;
  widgets: DashboardWidgetSpec[];
  refreshSeconds: number;
  rangePreset: TimeRangePreset;
  onAuthRefresh: () => Promise<void>;
}) => {
  return (
    <div className="grafana-explorer__section">
      <div className="grafana-explorer__section-header">
        <div>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
      </div>
      {widgets.length === 0 ? (
        <OrgModulePlaceholder intent="No curated dashboards are available for the current filters." />
      ) : (
        <div className="org-cockpit__grid">
          {widgets.map((widget) => (
            <GrafanaIframeCard
              key={widget.id}
              widget={widget}
              refreshSeconds={refreshSeconds}
              timeRangePreset={rangePreset}
              onAuthRefresh={onAuthRefresh}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const RuntimeSectionCard = ({ section }: { section: RuntimeSectionSummary }) => {
  const topBreakdown = section.breakdowns[0];
  return (
    <div className="telemetry-runtime__section-card">
      <div className="telemetry-runtime__section-header">
        <div>
          <h3>{section.label}</h3>
          <p>{section.summary}</p>
        </div>
        <span className={`telemetry-runtime__status telemetry-runtime__status--${section.status}`}>{section.status}</span>
      </div>

      <div className="telemetry-runtime__meta">
        <span>{section.reporting.label}</span>
        <span>Updated {formatTimestamp(section.lastUpdated)}</span>
      </div>

      {section.kpis.length > 0 ? (
        <div className="kpi-grid telemetry-runtime__kpis">
          {section.kpis.map((kpi) => (
            <KpiCard
              key={`${section.key}-${kpi.id}`}
              label={kpi.label}
              value={formatRuntimeKpiValue(kpi)}
              secondary={kpi.window}
              trend={kpi.description}
              accent={runtimeAccentForStatus(kpi.severity)}
            />
          ))}
        </div>
      ) : null}

      {topBreakdown?.rows?.length ? (
        <div className="telemetry-runtime__breakdown">
          <h4>{topBreakdown.label}</h4>
          <div className="telemetry-runtime__breakdown-table">
            {topBreakdown.rows.slice(0, 8).map((row) => (
              <div key={`${section.key}-${topBreakdown.label}-${row.label}`} className="telemetry-runtime__breakdown-row">
                <span>{row.label}</span>
                <strong>{valueTextFromNumber(row.value)}</strong>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {section.reporting.missingFamilies?.length ? (
        <div className="telemetry-runtime__coverage-group">
          <h4>Missing families</h4>
          <div className="telemetry-runtime__chip-list">
            {section.reporting.missingFamilies.map((family) => (
              <span key={`${section.key}-missing-${family}`} className="telemetry-runtime__chip telemetry-runtime__chip--warning">
                {family}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const TREND_COLORS = ['#ef4444', '#f97316', '#22c55e', '#0ea5e9', '#a78bfa', '#facc15'];
const TREND_DASH_PATTERNS = ['', '9 5', '3 5', '12 4 3 4', '2 3', '7 3 2 3'];
const TREND_HOVER_RADIUS = 34;

interface TrendHoverPoint {
  seriesId: string;
  label: string;
  value: number;
  timestamp: Date;
  x: number;
  y: number;
  color: string;
}

export const TrendLineChart = ({
  series,
  yMax,
  valueFormatter,
  xDomain
}: {
  series: TrendSeries[];
  yMax?: number;
  valueFormatter: (value: number) => string;
  xDomain?: { from: Date; to: Date };
}) => {
  const [hoverPoint, setHoverPoint] = useState<TrendHoverPoint | null>(null);
  const clipPathId = `trend-plot-${useId().replace(/:/g, '')}`;
  const allSamples = series.flatMap((item) => item.values);
  if (series.length === 0 || allSamples.length === 0) {
    return null;
  }

  const width = 640;
  const height = 240;
  const padding = { left: 54, right: 16, top: 30, bottom: 38 };
  const sampleMinTime = Math.min(...allSamples.map((sample) => sample.t.getTime()));
  const sampleMaxTime = Math.max(...allSamples.map((sample) => sample.t.getTime()));
  const domainFrom = xDomain?.from.getTime();
  const domainTo = xDomain?.to.getTime();
  const minTime = typeof domainFrom === 'number' && Number.isFinite(domainFrom) ? domainFrom : sampleMinTime;
  const maxTime = typeof domainTo === 'number' && Number.isFinite(domainTo) && domainTo > minTime ? domainTo : sampleMaxTime;
  const rawMax = Math.max(...allSamples.map((sample) => sample.value), yMax ?? 0);
  const scaleMax = yMax ?? Math.max(1, rawMax * 1.15);
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const timeSpan = Math.max(1, maxTime - minTime);
  const xFor = (date: Date) => padding.left + ((date.getTime() - minTime) / timeSpan) * plotWidth;
  const yFor = (value: number) => padding.top + (1 - Math.max(0, Math.min(scaleMax, value)) / scaleMax) * plotHeight;
  const xTicks = [minTime, minTime + timeSpan / 2, maxTime];
  const yTicks = [0, scaleMax / 2, scaleMax];
  const resolveHoverPoint = (chartX: number, chartY: number): TrendHoverPoint | null => {
    let closestPoint: TrendHoverPoint | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    series.forEach((item, index) => {
      const color = TREND_COLORS[index % TREND_COLORS.length];
      item.values.forEach((sample) => {
        const x = xFor(sample.t);
        const y = yFor(sample.value);
        const distance = (x - chartX) ** 2 + (y - chartY) ** 2;
        if (distance < closestDistance) {
          closestDistance = distance;
          closestPoint = {
            seriesId: item.id,
            label: item.label,
            value: sample.value,
            timestamp: sample.t,
            x,
            y,
            color
          };
        }
      });
    });

    return closestDistance <= TREND_HOVER_RADIUS ** 2 ? closestPoint : null;
  };
  const handleTrendMouseMove = (event: MouseEvent<SVGSVGElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) {
      return;
    }

    const chartX = ((event.clientX - bounds.left) / bounds.width) * width;
    const chartY = ((event.clientY - bounds.top) / bounds.height) * height;
    setHoverPoint(resolveHoverPoint(chartX, chartY));
  };
  const tooltipLeft = hoverPoint ? `${Math.min(92, Math.max(10, (hoverPoint.x / width) * 100))}%` : '0%';
  const tooltipTop = hoverPoint ? `${Math.min(88, Math.max(12, (hoverPoint.y / height) * 100))}%` : '0%';
  const tooltipPlaceLeft = hoverPoint ? hoverPoint.x > padding.left + plotWidth * 0.62 : false;
  const tooltipPlaceBelow = hoverPoint ? hoverPoint.y < padding.top + plotHeight * 0.32 : false;
  const tooltipTransform = hoverPoint
    ? `translate(${tooltipPlaceLeft ? 'calc(-100% - 0.9rem)' : '0.9rem'}, ${tooltipPlaceBelow ? '0.9rem' : 'calc(-100% - 0.9rem)'})`
    : undefined;

  return (
    <div className="telemetry-inspector__trend-chart">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Time-series trend chart"
        onMouseMove={handleTrendMouseMove}
        onMouseLeave={() => setHoverPoint(null)}
      >
        <defs>
          <clipPath id={clipPathId}>
            <rect x={padding.left} y={padding.top} width={plotWidth} height={plotHeight} />
          </clipPath>
        </defs>
        {yTicks.map((tick) => {
          const y = yFor(tick);
          return (
            <g key={`y-${tick}`}>
              <line x1={padding.left} x2={width - padding.right} y1={y} y2={y} className="telemetry-inspector__trend-grid" />
              <text x={padding.left - 8} y={y} textAnchor="end" dominantBaseline="middle" className="telemetry-inspector__trend-axis-label">
                {valueFormatter(tick)}
              </text>
            </g>
          );
        })}
        {xTicks.map((tick) => {
          const x = padding.left + ((tick - minTime) / timeSpan) * plotWidth;
          return (
            <g key={`x-${tick}`}>
              <line x1={x} x2={x} y1={padding.top} y2={height - padding.bottom} className="telemetry-inspector__trend-grid" />
              <text
                x={x}
                y={height - 10}
                textAnchor={tick === minTime ? 'start' : tick === maxTime ? 'end' : 'middle'}
                className="telemetry-inspector__trend-axis-label"
              >
                {chartTimeLabel(new Date(tick))}
              </text>
            </g>
          );
        })}
        <line x1={padding.left} x2={padding.left} y1={padding.top} y2={height - padding.bottom} className="telemetry-inspector__trend-axis" />
        <line x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} className="telemetry-inspector__trend-axis" />
        <g clipPath={`url(#${clipPathId})`} data-testid="trend-plot-content">
          {series.map((item, index) => {
            const color = TREND_COLORS[index % TREND_COLORS.length];
            const dashPattern = TREND_DASH_PATTERNS[index % TREND_DASH_PATTERNS.length];
            const path = item.values.map((sample, sampleIndex) => `${sampleIndex === 0 ? 'M' : 'L'} ${xFor(sample.t).toFixed(1)} ${yFor(sample.value).toFixed(1)}`).join(' ');
            const latest = item.values[item.values.length - 1];
            const markerOffset = series.length > 1 ? (index - (series.length - 1) / 2) * 3 : 0;
            return (
              <g key={item.id}>
                <path
                  d={path}
                  fill="none"
                  stroke={color}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={dashPattern || undefined}
                  strokeOpacity="0.86"
                >
                  <title>{`${item.label}: ${valueFormatter(latest?.value ?? item.summaryValue)}`}</title>
                </path>
                {latest ? (
                  <circle
                    cx={xFor(latest.t) + markerOffset}
                    cy={yFor(latest.value)}
                    r="4"
                    fill="var(--surface-panel)"
                    stroke={color}
                    strokeWidth="2"
                  >
                    <title>{`${item.label}: ${valueFormatter(latest.value)}`}</title>
                  </circle>
                ) : null}
              </g>
            );
          })}
        </g>
        <rect
          x={padding.left}
          y={padding.top}
          width={plotWidth}
          height={plotHeight}
          fill="transparent"
          className="telemetry-inspector__trend-hit-area"
        />
        {hoverPoint ? (
          <g pointerEvents="none">
            <line
              x1={hoverPoint.x}
              x2={hoverPoint.x}
              y1={padding.top}
              y2={height - padding.bottom}
              className="telemetry-inspector__trend-hover-line"
            />
            <circle
              cx={hoverPoint.x}
              cy={hoverPoint.y}
              r="5"
              fill="var(--surface-panel)"
              stroke={hoverPoint.color}
              strokeWidth="2.5"
              className="telemetry-inspector__trend-hover-point"
            />
          </g>
        ) : null}
      </svg>
      {hoverPoint ? (
        <div className="telemetry-inspector__trend-tooltip" style={{ left: tooltipLeft, top: tooltipTop, transform: tooltipTransform }}>
          <strong>{hoverPoint.label}</strong>
          <span>
            {chartTimeLabel(hoverPoint.timestamp)} · {valueFormatter(hoverPoint.value)}
          </span>
        </div>
      ) : null}
      <div className="telemetry-inspector__trend-legend">
        {series.map((item, index) => (
          <span key={item.id} title={item.label}>
            <i style={{ borderColor: TREND_COLORS[index % TREND_COLORS.length] }} />
            {item.label}
          </span>
        ))}
      </div>
      <div className="telemetry-inspector__trend-latest-sample" data-testid="trend-latest-sample">
        Latest sample <time dateTime={new Date(sampleMaxTime).toISOString()}>{formatRelativeTime(new Date(sampleMaxTime))}</time>
      </div>
    </div>
  );
};

const CpuTopFiveCard = ({
  fleetCpu,
  loading,
  backendMissing,
  timeRange
}: {
  fleetCpu: OrgFleetCpu;
  loading: boolean;
  backendMissing: boolean;
  timeRange: { from: Date; to: Date };
}) => {
  const peakDeviceLabel = fleetCpu.peakDeviceId
    ? fleetCpu.topDevices.find((device) => device.deviceId === fleetCpu.peakDeviceId)?.label ?? fleetCpu.peakDeviceId
    : '';
  const accent = fleetCpu.peakValue >= 90 ? 'danger' : fleetCpu.peakValue >= 75 ? 'warning' : 'success';
  const chartSeries = fleetCpu.series.filter((series) => series.values.length >= 2);

  return (
    <article className={`kpi-card kpi-card--${accent} telemetry-inspector__cpu-card`}>
      <header className="kpi-card__label">CPU utilization - top 5 devices</header>
      <div className="telemetry-inspector__cpu-card-header">
        <div>
          <div className="kpi-card__value">{loading ? '...' : backendMissing ? 'No data' : formatPercent(fleetCpu.avg)}</div>
          <div className="kpi-card__secondary">
            {loading
              ? 'loading device CPU telemetry'
              : backendMissing
                ? 'No CPU telemetry data for the selected range'
                : fleetCpu.devices > 0
                  ? `Fleet average from ${formatInteger(fleetCpu.devices)} reporting devices`
                  : 'No CPU telemetry reporting'}
          </div>
        </div>
        {!loading && !backendMissing && peakDeviceLabel ? (
          <div className="telemetry-inspector__cpu-peak" title={peakDeviceLabel}>
            Peak {formatPercent(fleetCpu.peakValue)}
            <span>{peakDeviceLabel}</span>
          </div>
        ) : null}
      </div>

      {!loading && !backendMissing && chartSeries.length > 0 ? (
        <TrendLineChart series={chartSeries} yMax={100} valueFormatter={formatPercent} xDomain={timeRange} />
      ) : !loading && !backendMissing && fleetCpu.topDevices.length > 0 ? (
        <div className="telemetry-inspector__cpu-bars" aria-label="Top 5 devices by CPU utilization">
          {fleetCpu.topDevices.map((device, index) => (
            <div key={device.deviceId} className="telemetry-inspector__cpu-bar-row">
              <div className="telemetry-inspector__cpu-bar-label">
                <span title={device.label}>{index + 1}. {device.label}</span>
                <strong>{formatPercent(device.value)}</strong>
              </div>
              <div className="telemetry-inspector__cpu-bar-track" aria-hidden="true">
                <span className="telemetry-inspector__cpu-bar-fill" style={{ width: `${Math.max(0, Math.min(100, device.value))}%` }} />
              </div>
            </div>
          ))}
        </div>
      ) : !loading && !backendMissing ? (
        <div className="kpi-card__trend">
          {fleetCpu.aggregateOnly ? 'No per-device CPU data for the selected range.' : 'No data available for the selected range.'}
        </div>
      ) : null}

      {!loading && !backendMissing && fleetCpu.truncated ? (
        <div className="kpi-card__trend">Counts capped by the current page limit.</div>
      ) : null}
    </article>
  );
};

export const InterfaceStatusCard = ({
  counts,
  loading,
  error,
  backendMissing,
  freshnessReferenceTime
}: {
  counts: OrgInterfaceCounts;
  loading: boolean;
  error?: boolean;
  backendMissing: boolean;
  freshnessReferenceTime?: Date;
}) => {
  const classificationAvailable = counts.classificationAvailable !== false;
  const accent = counts.unclassifiedDown > 0 || (classificationAvailable && (counts.unexpectedDown > 0 || counts.unknown > 0)) ? 'warning' : counts.operationallyDown > 0 ? 'default' : 'success';

  return (
    <article className={`kpi-card kpi-card--${accent} telemetry-inspector__interface-card`}>
      <header className="kpi-card__label">Interface Status</header>
      {loading ? (
        <>
          <div className="kpi-card__value">...</div>
          <div className="kpi-card__secondary">loading interface oper-status</div>
        </>
      ) : error ? (
        <>
          <div className="kpi-card__value">No data</div>
          <div className="kpi-card__secondary">interface oper-status query failed</div>
        </>
      ) : backendMissing ? (
        <>
          <div className="kpi-card__value">No data</div>
          <div className="kpi-card__secondary">No interface data for the selected range</div>
        </>
      ) : (
        <>
          <div className="telemetry-inspector__interface-total">
            <strong>{formatInteger(counts.total)}</strong>
            <span>Reporting Interfaces</span>
          </div>

          <div className="telemetry-inspector__interface-alerts">
            {classificationAvailable ? <div
              className={`telemetry-inspector__interface-row ${
                counts.unexpectedDown > 0
                  ? 'telemetry-inspector__interface-row--danger'
                  : 'telemetry-inspector__interface-row--success'
              }`}
            >
              <strong>{formatInteger(counts.unexpectedDown)}</strong>
              <span>Unexpected Down</span>
            </div> : null}
            <div className="telemetry-inspector__interface-row telemetry-inspector__interface-row--warning">
              <strong>{formatInteger(counts.lowerLayerDown)}</strong>
              <span>Lower-Layer Down</span>
            </div>
          </div>

          <div className="telemetry-inspector__interface-grid">
            <div className="telemetry-inspector__interface-row telemetry-inspector__interface-row--success">
              <strong>{formatInteger(counts.up)}</strong>
              <span>Up</span>
            </div>
            <div className="telemetry-inspector__interface-row telemetry-inspector__interface-row--warning">
              <strong>{formatInteger(counts.adminDown)}</strong>
              <span>Admin Down</span>
            </div>
            <div className="telemetry-inspector__interface-row telemetry-inspector__interface-row--muted">
              <strong>{formatInteger(counts.idleEnabledDown)}</strong>
              <span>Enabled Not Up</span>
            </div>
            {counts.unclassifiedDown > 0 ? (
              <div className="telemetry-inspector__interface-row telemetry-inspector__interface-row--danger">
                <strong>{formatInteger(counts.unclassifiedDown)}</strong>
                <span>Unclassified Down</span>
              </div>
            ) : null}
            {classificationAvailable && counts.unknown > 0 ? (
              <div className="telemetry-inspector__interface-row telemetry-inspector__interface-row--muted">
                <strong>{formatInteger(counts.unknown)}</strong>
                <span>Unknown</span>
              </div>
            ) : null}
          </div>

          <div className="telemetry-inspector__interface-not-up">
            <strong>{formatInteger(counts.operationallyDown)}</strong>
            <span>Not Up</span>
          </div>

          {counts.unexpectedDownDetails.length > 0 ? (
            <div className="telemetry-inspector__interface-detail-list">
              {counts.unexpectedDownDetails.map((row) => (
                <div className="telemetry-inspector__interface-detail" key={row.id} title={`${row.device} · ${row.target}`}>
                  <strong>{row.device}</strong>
                  <span>{row.target}</span>
                  <small>{freshnessLabel(row.observedAt, DEVICE_STALE_MINUTES, freshnessReferenceTime)}</small>
                </div>
              ))}
            </div>
          ) : null}

          <div className="kpi-card__trend">
            {counts.lastObservedAt ? `Updated ${formatTimestamp(counts.lastObservedAt)}` : 'Updated n/a'}
            {counts.stale > 0 ? ` · ${formatInteger(counts.stale)} stale samples ignored` : ''}
          </div>
          {!classificationAvailable ? (
            <div className="kpi-card__trend">Detailed unexpected/unknown classification is unavailable in summary view.</div>
          ) : null}
          {counts.truncated ? <div className="kpi-card__trend">Counts capped by the current page limit.</div> : null}
        </>
      )}
    </article>
  );
};

const ReportingCoverageTrend = ({
  staleDeviceCount,
  onlineConnectorCount,
  staleConnectorCount,
  connectorTotalCount
}: {
  staleDeviceCount: number;
  onlineConnectorCount: number;
  staleConnectorCount: number;
  connectorTotalCount: number;
}) => (
  <div className="telemetry-inspector__coverage-trend">
    <span
      className={`telemetry-inspector__coverage-stale ${staleDeviceCount > 0 ? 'telemetry-inspector__state-split-operational-down' : 'telemetry-inspector__state-split-up'}`}
    >
      {formatInteger(staleDeviceCount)} stale devices &gt;{DEVICE_STALE_MINUTES}m since last telemetry
    </span>
    <div className="telemetry-inspector__coverage-connectors" aria-label="Connector availability">
      <div className="telemetry-inspector__coverage-connector-row">
        <strong
          className={`telemetry-inspector__coverage-connector-value ${onlineConnectorCount > 0 ? 'telemetry-inspector__state-split-up' : ''}`}
        >
          {formatInteger(onlineConnectorCount)}
        </strong>
        <div className="telemetry-inspector__coverage-connector-copy">
          <span className={onlineConnectorCount > 0 ? 'telemetry-inspector__state-split-up' : ''}>Connectors online</span>
          <small>{formatInteger(connectorTotalCount)} total connectors</small>
        </div>
      </div>
      <div className="telemetry-inspector__coverage-connector-row">
        <strong
          className={`telemetry-inspector__coverage-connector-value ${staleConnectorCount > 0 ? 'telemetry-inspector__state-split-down' : 'telemetry-inspector__state-split-up'}`}
        >
          {formatInteger(staleConnectorCount)}
        </strong>
        <div className="telemetry-inspector__coverage-connector-copy">
          <span className={staleConnectorCount > 0 ? 'telemetry-inspector__state-split-down' : 'telemetry-inspector__state-split-up'}>
            Stale connectors
          </span>
          <small>&gt;{CONNECTOR_STALE_MINUTES}m since last heartbeat</small>
        </div>
      </div>
    </div>
  </div>
);

const OrgTelemetryTableCard = ({
  title,
  children,
  className = ''
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) => (
  <section className={`telemetry-inspector__org-card ${className}`}>
    <h2>{title}</h2>
    {children}
  </section>
);

export const OrgErrorRateTrendCard = ({
  summary,
  loading,
  error,
  backendMissing,
  timeRange
}: {
  summary: OrgInterfaceErrorRateSummary;
  loading: boolean;
  error?: boolean;
  backendMissing: boolean;
  timeRange: { from: Date; to: Date };
}) => (
  <OrgTelemetryTableCard title="Interface error rate trend">
    {loading ? (
      <p className="telemetry-inspector__empty">Loading interface error-rate trend...</p>
    ) : error ? (
      <p className="telemetry-inspector__empty">Interface error-rate trend query failed.</p>
    ) : backendMissing ? (
      <p className="telemetry-inspector__empty">Interface error-rate trend unavailable.</p>
    ) : summary.series.length === 0 ? (
      <p className="telemetry-inspector__empty">No current interface error/discard rate observed in the selected range.</p>
    ) : (
      <>
        <TrendLineChart series={summary.series} valueFormatter={formatRate} xDomain={timeRange} />
        {summary.source === 'snapshot' ? (
          <p className="telemetry-inspector__empty telemetry-inspector__trend-note">
            Historical trend samples are unavailable; showing the latest L1 error-rate snapshot on the selected time axis.
          </p>
        ) : null}
      </>
    )}
  </OrgTelemetryTableCard>
);

export const OrgBgpSessionTable = ({
  rows,
  total,
  partial = false,
  loading,
  backendMissing
}: {
  rows: OrgBgpPeerRow[];
  total?: number;
  partial?: boolean;
  loading: boolean;
  backendMissing: boolean;
}) => (
  <OrgTelemetryTableCard title="BGP session details">
    {loading ? (
      <p className="telemetry-inspector__empty">Loading BGP peer state...</p>
    ) : backendMissing ? (
      <p className="telemetry-inspector__empty">BGP peer state unavailable.</p>
    ) : partial && rows.length === 0 ? (
      <p className="telemetry-inspector__empty">
        BGP peer detail results were truncated; matching peers may be available in additional results.
      </p>
    ) : rows.length === 0 ? (
      <p className="telemetry-inspector__empty">No BGP peers are currently reporting.</p>
    ) : (
      <>
        <ResizableTelemetryTable
          columns={[
            { id: 'peer', label: 'Peer', resizeLabel: 'Peer', width: 120, minWidth: 84 },
            { id: 'local', label: 'Local', resizeLabel: 'Local', width: 120, minWidth: 84 },
            { id: 'device', label: 'Device', resizeLabel: 'Device', width: 140, minWidth: 96 },
            { id: 'state', label: 'State', resizeLabel: 'State', width: 112, minWidth: 84 },
            { id: 'prefixes', label: 'Prefixes', resizeLabel: 'Prefixes', width: 104, minWidth: 84 }
          ]}
        >
          <tbody>
            {rows.slice(0, 8).map((row) => (
              <tr key={row.id}>
              <td title={row.peer || '-'}>{row.peer || '-'}</td>
              <td title={row.local || '-'}>{row.local || '-'}</td>
              <td title={row.device}>{row.device}</td>
              <td title={row.state}>
                <span className={bgpStateClassName(row.state)}>{row.state}</span>
              </td>
              <td>{row.prefixes == null ? '-' : formatInteger(row.prefixes)}</td>
              </tr>
            ))}
          </tbody>
        </ResizableTelemetryTable>
        {typeof total === 'number' && total > rows.length ? (
          <p className="telemetry-inspector__empty telemetry-inspector__trend-note">
            Showing the latest {Math.min(rows.length, 8)} of {formatInteger(total)} BGP peers; aggregate counts include all peers.
          </p>
        ) : partial ? (
          <p className="telemetry-inspector__empty telemetry-inspector__trend-note">
            Showing the latest {Math.min(rows.length, 8)} BGP peers; additional results were truncated.
          </p>
        ) : null}
      </>
    )}
  </OrgTelemetryTableCard>
);

export const OrgOspfAdjacencyTable = ({
  rows,
  loading,
  backendMissing,
  liveTelemetryUnavailable = false
}: {
  rows: OrgOspfAdjacencyRow[];
  loading: boolean;
  backendMissing: boolean;
  liveTelemetryUnavailable?: boolean;
}) => (
  <OrgTelemetryTableCard title="OSPF adjacency health">
    {loading ? (
      <p className="telemetry-inspector__empty">Loading OSPF neighbor state...</p>
    ) : backendMissing ? (
      <p className="telemetry-inspector__empty">OSPF neighbor state unavailable.</p>
    ) : rows.length === 0 ? (
      <p className="telemetry-inspector__empty">No OSPF neighbors are currently reporting.</p>
    ) : (
      <>
        {liveTelemetryUnavailable ? (
          <p className="telemetry-inspector__empty">No devices are reporting live telemetry; rows below are topology-derived context.</p>
        ) : null}
        <ResizableTelemetryTable
          columns={[
            { id: 'device', label: 'Device', resizeLabel: 'Device', width: 150, minWidth: 96 },
            { id: 'area', label: 'Area', resizeLabel: 'Area', width: 90, minWidth: 64 },
            { id: 'neighbors', label: 'Neighbors', resizeLabel: 'Neighbors', width: 120, minWidth: 92 },
            { id: 'state', label: 'State', resizeLabel: 'State', width: 110, minWidth: 84 }
          ]}
        >
          <tbody>
            {rows.slice(0, 8).map((row) => (
              <tr key={row.id}>
                <td title={row.device}>{row.device}</td>
                <td title={row.area}>{routingAdjacencyAreaDisplay(row.area) || row.area}</td>
                <td>
                  <span className={row.up === row.total ? 'telemetry-inspector__state-split-up' : 'telemetry-inspector__state-split-operational-down'}>
                    {formatInteger(row.up)} / {formatInteger(row.total)}
                  </span>
                </td>
                <td title={row.state}>
                  <span className={row.up === row.total ? 'telemetry-inspector__state-split-up' : 'telemetry-inspector__state-split-operational-down'}>
                    {row.state}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </ResizableTelemetryTable>
      </>
    )}
  </OrgTelemetryTableCard>
);

export const OrgIsisAdjacencyTable = ({
  rows,
  loading,
  unavailable,
  partialUnavailable = false,
  liveTelemetryUnavailable = false
}: {
  rows: OrgOspfAdjacencyRow[];
  loading: boolean;
  unavailable: boolean;
  partialUnavailable?: boolean;
  liveTelemetryUnavailable?: boolean;
}) => (
  <OrgTelemetryTableCard title="IS-IS adjacency health">
    {loading ? (
      <p className="telemetry-inspector__empty">Loading IS-IS adjacency state...</p>
    ) : unavailable ? (
      <p className="telemetry-inspector__empty">IS-IS adjacency state unavailable for one or more sites.</p>
    ) : rows.length === 0 ? (
      <p className="telemetry-inspector__empty">No IS-IS adjacencies are currently reporting.</p>
    ) : (
      <>
        {partialUnavailable ? (
          <p className="telemetry-inspector__empty">IS-IS adjacency state is unavailable for one or more sites; showing available results.</p>
        ) : null}
        {liveTelemetryUnavailable ? (
          <p className="telemetry-inspector__empty">No devices are reporting live telemetry; rows below are topology-derived context.</p>
        ) : null}
        <ResizableTelemetryTable
          columns={[
            { id: 'device', label: 'Device', resizeLabel: 'Device', width: 150, minWidth: 96 },
            { id: 'instance', label: 'Instance', resizeLabel: 'Instance', width: 132, minWidth: 92 },
            { id: 'neighbors', label: 'Neighbors', resizeLabel: 'Neighbors', width: 120, minWidth: 92 },
            { id: 'state', label: 'State', resizeLabel: 'State', width: 98, minWidth: 76 }
          ]}
        >
          <tbody>
            {rows.slice(0, 8).map((row) => (
              <tr key={row.id}>
                <td title={row.device}>{row.device}</td>
                <td title={row.area}>{row.area}</td>
                <td>
                  <span className={row.up === row.total ? 'telemetry-inspector__state-split-up' : 'telemetry-inspector__state-split-operational-down'}>
                    {formatInteger(row.up)} / {formatInteger(row.total)}
                  </span>
                </td>
                <td title={row.state}>
                  <span className={row.up === row.total ? 'telemetry-inspector__state-split-up' : 'telemetry-inspector__state-split-operational-down'}>
                    {row.state}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </ResizableTelemetryTable>
      </>
    )}
  </OrgTelemetryTableCard>
);

export const OrgBfdSessionTable = ({
  rows,
  total,
  partial = false,
  loading,
  unavailable,
  liveTelemetryUnavailable = false
}: {
  rows: OrgBfdSessionRow[];
  total?: number;
  partial?: boolean;
  loading: boolean;
  unavailable: boolean;
  liveTelemetryUnavailable?: boolean;
}) => (
  <OrgTelemetryTableCard title={liveTelemetryUnavailable ? 'BFD session context' : 'BFD session health'}>
    {loading ? (
      <p className="telemetry-inspector__empty">Loading BFD session state...</p>
    ) : unavailable ? (
      <p className="telemetry-inspector__empty">BFD session state unavailable for one or more sites.</p>
    ) : partial && rows.length === 0 ? (
      <p className="telemetry-inspector__empty">
        BFD session detail results were truncated; matching sessions may be available in additional results.
      </p>
    ) : liveTelemetryUnavailable && rows.length === 0 ? (
      <p className="telemetry-inspector__empty">No devices are reporting live BFD telemetry.</p>
    ) : rows.length === 0 ? (
      <p className="telemetry-inspector__empty">
        {typeof total === 'number' && total > 0
          ? `Aggregate telemetry reports ${formatInteger(total)} BFD sessions; detail rows are unavailable.`
          : 'No BFD sessions are currently reporting.'}
      </p>
    ) : (
      <>
        {liveTelemetryUnavailable ? (
          <p className="telemetry-inspector__empty">No devices are reporting live BFD telemetry; rows below are topology-derived context.</p>
        ) : null}
        <ResizableTelemetryTable
          columns={[
            { id: 'device', label: 'Device', resizeLabel: 'Device', width: 126, minWidth: 92 },
            { id: 'peer', label: 'Peer', resizeLabel: 'Peer', width: 112, minWidth: 84 },
            { id: 'interface', label: 'Interface', resizeLabel: 'Interface', width: 116, minWidth: 92 },
            { id: 'type', label: 'Type', resizeLabel: 'Type', width: 92, minWidth: 72 },
            { id: 'state', label: 'State', resizeLabel: 'State', width: 94, minWidth: 76 }
          ]}
        >
          <tbody>
            {rows.slice(0, 8).map((row) => (
              <tr key={row.id}>
                <td title={row.device}>{row.device}</td>
                <td title={row.peer}>{row.peer}</td>
                <td title={row.interfaceName || '-'}>{row.interfaceName || '-'}</td>
                <td title={row.sessionType || '-'}>{row.sessionType || '-'}</td>
                <td title={row.state}>
                  <span className={row.up ? 'telemetry-inspector__state-split-up' : 'telemetry-inspector__state-split-operational-down'}>
                    {row.state}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </ResizableTelemetryTable>
        {typeof total === 'number' && total > rows.length ? (
          <p className="telemetry-inspector__empty telemetry-inspector__trend-note">
            Showing the latest {Math.min(rows.length, 8)} of {formatInteger(total)} BFD sessions; aggregate counts include all sessions.
          </p>
        ) : partial ? (
          <p className="telemetry-inspector__empty telemetry-inspector__trend-note">
            Showing the latest {Math.min(rows.length, 8)} BFD sessions; additional results were truncated.
          </p>
        ) : null}
      </>
    )}
  </OrgTelemetryTableCard>
);

const OrgStpLagCard = ({ summary, loading }: { summary: OrgStpLagSummary; loading: boolean }) => (
  <OrgTelemetryTableCard title="STP & Port-channel health">
    {loading ? (
      <p className="telemetry-inspector__empty">Loading STP and LACP signals...</p>
    ) : summary.unavailable ? (
      <p className="telemetry-inspector__empty">STP and LACP signals unavailable.</p>
    ) : (
      <div className="telemetry-inspector__stp-lag">
        <div>
          <span className="telemetry-inspector__subhead">
            {summary.stpTopChangesIsRange ? 'STP topology changes in range' : 'STP topology changes counter'}
          </span>
          <div className="telemetry-inspector__metric-line">
            <strong>{summary.stpTopChanges == null ? 'Not reported' : formatInteger(summary.stpTopChanges)}</strong>
            <span>
              {summary.stpTopDevice ||
                (summary.stpTopChangesIsRange ? 'No topology changes in selected range' : 'STP topology-change signal not reporting')}
            </span>
          </div>
        </div>
        <div>
          <span className="telemetry-inspector__subhead">Port-channel members</span>
          {summary.lagRows.length === 0 ? (
            <p className="telemetry-inspector__empty">No LACP member synchronization signals are currently reporting.</p>
          ) : (
            <div className="telemetry-inspector__lag-list">
              {summary.lagRows.slice(0, 6).map((row) => {
                const down = Math.max(0, row.total - row.up);
                return (
                  <div key={row.id} className="telemetry-inspector__lag-row">
                    <span title={row.label}>{row.label}</span>
                    <strong className={down === 0 ? 'telemetry-inspector__state-split-up' : 'telemetry-inspector__state-split-down'}>
                      {down > 0 ? `${formatInteger(down)}/${formatInteger(row.total)} members down` : `${formatInteger(row.up)}/${formatInteger(row.total)} up`}
                    </strong>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    )}
  </OrgTelemetryTableCard>
);

export const deviceHealthEmptyStateMessage = (): string => 'No data available for the selected range.';

export const shouldLoadDeviceHealthSummary = (
  hasDetailedInventory: boolean,
  detailedInventoryError: Error | null,
  targetedHealthLoading = false,
  cpuTopKLoading = false
): boolean => targetedHealthLoading || cpuTopKLoading || (!hasDetailedInventory && !detailedInventoryError);

export const shouldShowCpuBackendMissing = (
  loading: boolean,
  hasRankedSeries: boolean,
  hasAggregateSummary: boolean
): boolean => !loading && !hasRankedSeries && !hasAggregateSummary;

export const shouldShowCpuInstantFallbackError = (
  hasRangeSamples: boolean,
  instantError: Error | null,
  instantBackendMissing: boolean
): boolean => !hasRangeSamples && Boolean(instantError) && !instantBackendMissing;

export const interfaceIdentityFromSignal = (targetId?: string, instance?: string): string => {
  const target = targetId?.trim() ?? '';
  if (target) {
    const separator = target.lastIndexOf('#');
    return separator >= 0 ? target.slice(separator + 1) : target;
  }
  return instance?.trim() ?? '';
};

export const topDeviceHealthRows = (
  rows: readonly OrgDeviceHealthRow[],
  limit = DEVICE_HEALTH_SUMMARY_ROW_LIMIT
): OrgDeviceHealthRow[] => rows.slice(0, limit);

export const OrgDeviceHealthTable = ({
  rows,
  loading,
  truncated = false,
  error = null,
  backendMissing = false
}: {
  rows: OrgDeviceHealthRow[];
  loading: boolean;
  truncated?: boolean;
  error?: Error | null;
  backendMissing?: boolean;
}) => (
  <OrgTelemetryTableCard title="CPU-ranked device health (Top 12)" className="telemetry-inspector__org-card--full">
    {loading && rows.length === 0 ? (
      <p className="telemetry-inspector__empty">Loading CPU-ranked device health...</p>
    ) : rows.length === 0 ? (
      <p className="telemetry-inspector__empty">
        {backendMissing
          ? 'Device-health telemetry is unavailable from the backend.'
          : error
            ? `Device-health telemetry request failed: ${error.message}`
            : deviceHealthEmptyStateMessage()}
      </p>
    ) : (
      <>
        {loading ? <p className="telemetry-inspector__empty">Refreshing CPU-ranked device health...</p> : null}
        <ResizableTelemetryTable
          className="telemetry-inspector__mini-table--wide"
          columns={[
            { id: 'device', label: 'Device', resizeLabel: 'Device', width: 180, minWidth: 112 },
            { id: 'site', label: 'Site', resizeLabel: 'Site', width: 150, minWidth: 96 },
            { id: 'cpu', label: 'CPU %', resizeLabel: 'CPU %', width: 90, minWidth: 72 },
            { id: 'mem', label: 'Mem %', resizeLabel: 'Mem %', width: 90, minWidth: 72 },
            { id: 'uptime', label: 'Uptime', resizeLabel: 'Uptime', width: 110, minWidth: 82 },
            { id: 'interfaces', label: 'Interfaces Up', resizeLabel: 'Interfaces Up', width: 138, minWidth: 110 },
            { id: 'bgp', label: 'BGP', resizeLabel: 'BGP', width: 132, minWidth: 92 },
            { id: 'lastTelemetry', label: 'Last telemetry', resizeLabel: 'Last telemetry', width: 128, minWidth: 92 }
          ]}
        >
          <tbody>
            {topDeviceHealthRows(rows).map((row) => (
              <tr key={row.id}>
                <td className="telemetry-inspector__cell-primary" title={row.device}>{row.device}</td>
                <td title={row.site}>{row.site}</td>
                <td className={(row.cpu ?? 0) >= 90 ? 'telemetry-inspector__state-split-down' : (row.cpu ?? 0) >= 75 ? 'telemetry-inspector__state-split-operational-down' : ''}>
                  {row.cpu == null ? 'n/a' : formatPercent(row.cpu)}
                </td>
                <td>{row.mem == null ? 'n/a' : formatPercent(row.mem)}</td>
                <td>{formatDurationCompact(row.uptimeSeconds)}</td>
                <td>
                  {formatInteger(row.interfacesUp)}/{formatInteger(row.interfacesTotal)}
                </td>
                <td className={row.bgpTotal > 0 && row.bgpUp < row.bgpTotal ? 'telemetry-inspector__state-split-down' : ''}>
                  {row.bgpTotal === 0 ? '-' : `${formatInteger(row.bgpUp)} established`}
                </td>
                <td title={freshnessLabel(row.lastTelemetryAt)}>{freshnessLabel(row.lastTelemetryAt)}</td>
              </tr>
            ))}
          </tbody>
        </ResizableTelemetryTable>
      </>
    )}
    {!loading && truncated ? (
      <p className="telemetry-inspector__empty">Some device-health telemetry was truncated; interface and BGP counts may be incomplete.</p>
    ) : null}
  </OrgTelemetryTableCard>
);

const RuntimeCoverageSection = ({
  runtimeMetrics,
  selectedScope
}: {
  runtimeMetrics: AdminConnectorRuntimeMetricsSummary;
  selectedScope: DashboardScope;
}) => {
  const recognizedFamilies = runtimeMetrics.diagnostics.metricFamilies.filter((family) => isRecognizedRuntimeFamily(family.name));
  const uncategorizedFamilies = runtimeMetrics.diagnostics.metricFamilies.filter((family) => !isRecognizedRuntimeFamily(family.name));
  const sectionTitle = selectedScope === 'org' ? 'Collector Runtime Coverage' : 'Collector Runtime Coverage (Org)';

  return (
    <div className="grafana-explorer__section">
      <div className="grafana-explorer__section-header">
        <div>
          <h2>{sectionTitle}</h2>
          <p>Live runtime health for OTEL agent, command agent, connector VPN, and the observed raw metric families currently reporting into infra Prometheus.</p>
        </div>
        <div className="telemetry-runtime__links">
          <a href={runtimeMetrics.links.dashboard} target="_blank" rel="noreferrer">
            Open Grafana
          </a>
        </div>
      </div>

      <div className="telemetry-inspector__summary kpi-grid">
        <KpiCard label="Runtime status" value={runtimeMetrics.status} secondary={runtimeMetrics.summary} accent={runtimeAccentForStatus(runtimeMetrics.status)} />
        <KpiCard label="Connectors reporting" value={String(runtimeMetrics.connectors.length)} secondary={`Window ${runtimeMetrics.window}`} />
        <KpiCard
          label="Metric families observed"
          value={String(runtimeMetrics.diagnostics.metricFamilies.length)}
          secondary={`${recognizedFamilies.length} recognized runtime families`}
          accent={coverageAccent(uncategorizedFamilies.length === 0)}
        />
        <KpiCard
          label="Families still uncategorized"
          value={String(uncategorizedFamilies.length)}
          secondary="Observed families outside known runtime patterns"
          accent={uncategorizedFamilies.length > 0 ? 'warning' : 'success'}
        />
      </div>

      {runtimeMetrics.sections.length > 0 ? (
        <div className="telemetry-runtime__sections">
          {runtimeMetrics.sections.map((section) => (
            <RuntimeSectionCard key={section.key} section={section} />
          ))}
        </div>
      ) : null}

      <div className="telemetry-runtime__coverage-grid">
        <div className="telemetry-runtime__coverage-card">
          <h3>Observed runtime families</h3>
          <p>These are the raw metric families the runtime endpoint says are currently present.</p>
          <div className="telemetry-runtime__chip-list">
            {runtimeMetrics.diagnostics.metricFamilies.slice(0, 48).map((family: RuntimeMetricFamilySummary) => (
              <span
                key={`observed-${family.name}`}
                className={`telemetry-runtime__chip ${isRecognizedRuntimeFamily(family.name) ? 'telemetry-runtime__chip--success' : 'telemetry-runtime__chip--warning'}`}
                title={`${family.series} series`}
              >
                {family.name} ({family.series})
              </span>
            ))}
          </div>
        </div>

        <div className="telemetry-runtime__coverage-card">
          <h3>Uncategorized runtime families</h3>
          <p>Observed families outside known runtime patterns need catalog mapping before they can be treated as supported dashboard metrics.</p>
          {uncategorizedFamilies.length > 0 ? (
            <div className="telemetry-runtime__chip-list">
              {uncategorizedFamilies.slice(0, 24).map((family) => (
                <span key={`uncategorized-${family.name}`} className="telemetry-runtime__chip telemetry-runtime__chip--warning" title={`${family.series} series`}>
                  {family.name}
                </span>
              ))}
            </div>
          ) : (
            <p className="telemetry-runtime__empty">All observed runtime families match known runtime patterns.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const buildCatalogWidgets = ({
  keys,
  prefix,
  templateByKey,
  titleSuffix,
  varBindings,
  timeRange,
  dashboardsMockEnabled
}: {
  keys: string[];
  prefix: string;
  templateByKey: Map<string, GrafanaTemplateSpec>;
  titleSuffix?: string;
  varBindings: Record<string, string | undefined>;
  timeRange: { from: Date; to: Date };
  dashboardsMockEnabled: boolean;
}): DashboardWidgetSpec[] =>
  keys
    .map((templateKey) => templateByKey.get(templateKey))
    .filter((item): item is GrafanaTemplateSpec => Boolean(item))
    .map((item) =>
      widgetForTemplate({
        item,
        id: `${prefix}-${item.templateKey}`,
        title: `${titleFromTemplateKey(item.templateKey)}${titleSuffix ? ` ${titleSuffix}` : ''}`,
        varBindings,
        timeRange,
        dashboardsMockEnabled
      })
    );

const GrafanaExplorerPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const grafanaRouteMatch = useMatch(`${GRAFANA_EXPLORER_ROUTE}/*`);
  const orgId = useActiveOrgId();
  const config = useConfig();
  const basePath = resolveBasePath(config.apiBaseUrl);
  const adminBasePath = resolveAdminBasePath(config.apiBaseUrl);
  const { fetchJson, fetchOrgCollection } = useAuthFetch();
  const dashboardsApi = useDashboardsApi();
  const devicesApi = useDevicesApi();
  const metricsApi = useMetricsApi();
  const signalsApi = useSignalsApi();
  const sitesApi = useSitesApi();

  const initialStage = (() => {
    const raw = searchParams.get('stage');
    return raw === 'signals' || raw === 'features' || raw === 'conditions' ? raw : 'signals';
  })();
  const initialFamily = (() => {
    const raw = searchParams.get('family');
    if (raw === 'all') {
      return initialStage === 'conditions' ? 'all' : 'environment';
    }
    return normalizeFamilySelection(raw);
  })();
  const initialConditionState = (() => {
    const raw = (searchParams.get('state') ?? '').trim().toUpperCase();
    return raw === 'ON' || raw === 'OFF' || raw === 'ALL' ? (raw as ConditionStateFilter) : 'ON';
  })();
  const initialScope = (() => {
    const raw = searchParams.get('scope');
    if (raw === 'org' || raw === 'site') {
      return raw as DashboardScope;
    }
    if (raw === 'device' || searchParams.get('deviceId')) {
      return 'site';
    }
    return 'org';
  })();
  const [stage, setStage] = useState<SignalStage>(initialStage);
  const [family, setFamily] = useState<SignalFamily>(initialFamily);
  const [conditionState, setConditionState] = useState<ConditionStateFilter>(initialConditionState);
  const [selectedScope, setSelectedScope] = useState<DashboardScope>(initialScope);
  const [selectedSiteId, setSelectedSiteId] = useState(searchParams.get('siteId') ?? '');
  const [selectedDeviceId, setSelectedDeviceId] = useState(searchParams.get('deviceId') ?? '');
  const [selectedDeviceRole, setSelectedDeviceRole] = useState(searchParams.get('role') ?? 'all');
  const [selectedKey, setSelectedKey] = useState(searchParams.get('key') ?? '');
  const [rangePreset, setRangePreset] = useState<TimeRangePreset>('1h');
  const [refreshSeconds, setRefreshSeconds] = useState<number>(30);
  const [apiRefreshNonce, setApiRefreshNonce] = useState(0);
  const [chartDrawer, setChartDrawer] = useState<ChartDrawerState | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [inspectionPageLimit, setInspectionPageLimit] = useState<number>(10);
  const [afterCursor, setAfterCursor] = useState('');
  const [beforeCursor, setBeforeCursor] = useState('');
  const [orgCpuRangeCache, setOrgCpuRangeCache] = useState<MetricsSeriesCache>({});
  const [orgInterfaceErrorRateRangeCache, setOrgInterfaceErrorRateRangeCache] = useState<MetricsSeriesCache>({});
  const [orgStpTopChangesRangeCache, setOrgStpTopChangesRangeCache] = useState<MetricsSeriesCache>({});
  const [orgCliStpTopChangesRangeCache, setOrgCliStpTopChangesRangeCache] = useState<MetricsSeriesCache>({});
  const [deviceSummaryCache, setDeviceSummaryCache] = useState<Record<string, CursorListResponse<DeviceSummary>>>({});

  const dashboardsMockEnabled = config.mockRoutes.includes('dashboards');
  const timeRange = useMemo(() => computeTimeRange(rangePreset), [rangePreset, apiRefreshNonce]);
  const orgRangeCacheKey = `${orgId ?? ''}:${selectedScope}:${rangePreset}`;

  useVisibilityAwareRefresh({
    intervalMs: refreshSeconds * 1000,
    onRefresh: () => setApiRefreshNonce((value) => value + 1)
  });

  useEffect(() => {
    setOrgCpuRangeCache({});
    setOrgInterfaceErrorRateRangeCache({});
    setOrgStpTopChangesRangeCache({});
    setOrgCliStpTopChangesRangeCache({});
  }, [orgId, selectedScope]);

  const sitesFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    return () => sitesApi.orgsOrgIdSitesGet({ orgId, limit: 100 }, { cache: 'no-store' });
  }, [orgId, sitesApi]);
  const { data: sitesData, loading: sitesLoading, error: sitesError } = useApiQuery<SitesListResponse>(sitesFetcher, [sitesFetcher]);

  const sites = useMemo(() => {
    const items = sitesData?.items ?? [];
    return items.filter((site) => Boolean(site.id)).map((site) => ({ id: site.id!, name: site.name ?? site.id! }));
  }, [sitesData?.items]);

  useEffect(() => {
    if (selectedScope === 'org' || selectedSiteId || sites.length === 0) {
      return;
    }
    setSelectedSiteId(sites[0].id);
  }, [selectedScope, selectedSiteId, sites]);

  useEffect(() => {
    if (selectedScope !== 'org' && selectedSiteId && sites.length > 0 && !sites.some((site) => site.id === selectedSiteId)) {
      setSelectedSiteId(sites[0]?.id ?? '');
    }
  }, [selectedScope, selectedSiteId, sites]);

  useEffect(() => {
    if (stage !== 'conditions' && family === 'all') {
      setFamily('environment');
      return;
    }
    if (stage === 'features' && FAMILY_FEATURE_KEYS[family].length === 0) {
      setFamily('environment');
      return;
    }
    if (stage === 'conditions' && family !== 'all' && FAMILY_CONDITION_KEYS[family].length === 0) {
      setFamily('environment');
    }
  }, [family, stage]);

  const devicesFetcher = useMemo(() => {
    if (!orgId || !selectedSiteId) {
      return null;
    }
    return () => devicesApi.orgsOrgIdDevicesGet({ orgId, siteId: selectedSiteId, limit: 200 }, { cache: 'no-store' });
  }, [devicesApi, orgId, selectedSiteId]);
  const { data: devicesData, loading: devicesLoading } = useApiQuery<DevicesListResponse>(devicesFetcher, [devicesFetcher]);

  const deviceSummaryFetcher = useMemo(() => {
    // The lightweight inventory mode supplies names/aliases without doing a
    // running-config lookup for every device. It is safe for org-scale lists.
    if (!orgId) {
      return null;
    }
    const params = new URLSearchParams({ limit: String(ORG_ROLLUP_PAGE_LIMIT), includeRunningConfigStatus: 'false' });
    if (selectedScope !== 'org' && selectedSiteId) params.set('siteId', selectedSiteId);
    return () => fetchAllOrgPages<DeviceSummary>(fetchOrgCollection, basePath, orgId, `devices?${params.toString()}`);
  }, [basePath, fetchOrgCollection, orgId, selectedScope, selectedSiteId]);
  const {
    data: deviceSummaryData,
    loading: deviceSummaryLoading,
    error: deviceSummaryError
  } = useApiQuery<CursorListResponse<DeviceSummary>>(deviceSummaryFetcher, [deviceSummaryFetcher, apiRefreshNonce]);
  const deviceSummaryCacheKey = `${orgId ?? ''}:${selectedScope}:${selectedScope === 'org' ? 'all-sites' : selectedSiteId || 'all-sites'}`;
  useEffect(() => {
    if (!deviceSummaryData) {
      return;
    }
    setDeviceSummaryCache((previous) => ({
      ...previous,
      [deviceSummaryCacheKey]: deviceSummaryData
    }));
  }, [deviceSummaryCacheKey, deviceSummaryData]);
  const effectiveDeviceSummaryData = deviceSummaryLoading
    ? deviceSummaryCache[deviceSummaryCacheKey] ?? null
    : selectCurrentOrCachedData(deviceSummaryData, deviceSummaryCache[deviceSummaryCacheKey]);
  const shouldShowDeviceSummaryError = Boolean(deviceSummaryError && !effectiveDeviceSummaryData);

  const coverageDeviceCountFetcher = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    return () => fetchOrgCollection<DeviceSummary>(basePath, orgId, 'devices?limit=1', { cache: 'no-store' });
  }, [basePath, fetchOrgCollection, orgId, selectedScope]);
  const { data: coverageDeviceCountData } = useApiQuery<CursorListResponse<DeviceSummary>>(
    coverageDeviceCountFetcher,
    [coverageDeviceCountFetcher, apiRefreshNonce]
  );

  const connectorSummaryFetcher = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    return () => fetchAllOrgPages<ConnectorSummary>(fetchOrgCollection, basePath, orgId, `connectors?limit=${ORG_ROLLUP_PAGE_LIMIT}`);
  }, [basePath, fetchOrgCollection, orgId, selectedScope]);
  const { data: connectorSummaryData, error: connectorSummaryError } = useApiQuery<CursorListResponse<ConnectorSummary>>(
    connectorSummaryFetcher,
    [connectorSummaryFetcher, apiRefreshNonce]
  );

  const runtimeMetricsFetcher = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    const params = new URLSearchParams();
    params.set('window', rangePreset);
    return () => fetchJson<AdminConnectorRuntimeMetricsSummary>(`${adminBasePath}/orgs/${encodeURIComponent(orgId)}/connector-runtime-metrics?${params.toString()}`);
  }, [adminBasePath, fetchJson, orgId, rangePreset, selectedScope]);
  const {
    data: runtimeMetricsData,
    error: runtimeMetricsError,
    backendMissing: runtimeMetricsBackendMissing
  } = useApiQuery<AdminConnectorRuntimeMetricsSummary>(runtimeMetricsFetcher, [runtimeMetricsFetcher, apiRefreshNonce]);

  // Fleet cards consume compact Prometheus aggregates. This avoids fetching a
  // latest-value row for every device/interface in large organizations.
  const orgDashboardSignalSummariesFetcher = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    const summaryUrl = (family: string, signalKey: string, equalTo: number) => {
      const params = new URLSearchParams({ scopeType: 'org', scopeId: orgId, family, signalKey, equalTo: String(equalTo) });
      return buildOrgUrl(basePath, orgId, `signals/summary?${params.toString()}`);
    };
    return async (): Promise<OrgDashboardSignalSummaries> => {
      const [uptime, interfaceOper, interfaceAdmin, cpu, bgp, ospf, bfd, stp, lacp] = await Promise.all([
        fetchJson<SignalSummary>(summaryUrl('device_health', 'snmp_device_uptime_seconds', 1)),
        fetchJson<SignalSummary>(summaryUrl('interface', 'snmp_if_oper_status', 1)),
        fetchJson<SignalSummary>(summaryUrl('interface', 'snmp_if_admin_status', 2)),
        fetchJson<SignalSummary>(summaryUrl('device_health', 'snmp_device_cpu_pct', 1)),
        fetchJson<SignalSummary>(summaryUrl('bgp', 'snmp_bgp_peer_state', 6)),
        fetchJson<SignalSummary>(summaryUrl('routing', 'snmp_ospf_neighbor_state', 8)),
        fetchJson<SignalSummary>(summaryUrl('bfd', 'snmp_bfd_session_up', 1)),
        fetchJson<SignalSummary>(summaryUrl('stp', 'snmp_stp_top_changes_total', 1)),
        fetchJson<SignalSummary>(summaryUrl('lacp', 'snmp_lacp_member_synchronized', 1))
      ]);
      return { uptime, interfaceOper, interfaceAdmin, cpu, bgp, ospf, bfd, stp, lacp };
    };
  }, [basePath, fetchJson, orgId, selectedScope]);
  const {
    data: orgDashboardSignalSummaries,
    error: orgDashboardSignalSummariesError,
    loading: orgDashboardSignalSummariesLoading,
    backendMissing: orgDashboardSignalSummariesBackendMissing
  } = useApiQuery<OrgDashboardSignalSummaries>(orgDashboardSignalSummariesFetcher, [orgDashboardSignalSummariesFetcher, apiRefreshNonce]);
  const orgCpuRangeRequest = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    return {
      cacheKey: orgRangeCacheKey,
      request: metricIntentRangeRequest({
        orgId,
        intent: 'device.cpu_top12',
        rangePreset,
        timeRange
      })
    } satisfies RangeMetricsRequest;
  }, [orgId, orgRangeCacheKey, rangePreset, selectedScope, timeRange]);
  const orgCpuRangeFetcher = useLatestRequestFetcher(
    orgCpuRangeRequest,
    async (rangeRequest): Promise<KeyedMetricsSeriesResponse> => {
      const data = await metricsApi.orgsOrgIdMetricsQueryRangePost(
        {
          orgId: rangeRequest.request.scope.scopeId,
          metricsIntentQueryRangeRequest: rangeRequest.request
        },
        { cache: 'no-store' }
      );
      return {
        cacheKey: rangeRequest.cacheKey,
        range: {
          from: rangeRequest.request.range.from,
          to: rangeRequest.request.range.to
        },
        data
      };
    },
    `${orgId ?? ''}:${selectedScope}:${rangePreset}:cpu`
  );
  const {
    data: orgCpuRangeResponse,
    error: orgCpuRangeError,
    loading: orgCpuRangeLoading,
    backendMissing: orgCpuRangeBackendMissing
  } = useApiQuery<KeyedMetricsSeriesResponse>(orgCpuRangeFetcher, [orgCpuRangeFetcher, apiRefreshNonce]);
  // Instant Top-K supplies current values when the selected range has no
  // retained samples yet. Values are rendered as a flat window chart.
  const orgCpuTopInstantFetcher = useMemo(() => {
    if (!orgId || selectedScope !== 'org') return null;
    return async (): Promise<MetricsSeriesResponse> =>
      metricsApi.orgsOrgIdMetricsQueryPost(
        {
          orgId,
          metricsIntentQueryRequest: metricIntentInstantRequest({ orgId, intent: 'device.cpu_top12' })
        },
        { cache: 'no-store' }
      );
  }, [metricsApi, orgId, selectedScope]);
  const {
    data: orgCpuTopInstantData,
    loading: orgCpuTopInstantLoading,
    error: orgCpuTopInstantError,
    backendMissing: orgCpuTopInstantBackendMissing
  } = useApiQuery<MetricsSeriesResponse>(orgCpuTopInstantFetcher, [orgCpuTopInstantFetcher, apiRefreshNonce]);

  const orgInterfaceErrorCoverageRangeRequest = useMemo(() => {
    if (!orgId || selectedScope !== 'org') return null;
    return {
      cacheKey: orgRangeCacheKey,
      request: metricIntentRangeRequest({
        orgId,
        intent: ORG_INTERFACE_ERROR_COVERAGE_CONTRACT.intent,
        rangePreset,
        timeRange
      })
    } satisfies RangeMetricsRequest;
  }, [orgId, orgRangeCacheKey, rangePreset, selectedScope, timeRange]);
  const orgInterfaceErrorCoverageRangeFetcher = useLatestRequestFetcher(
    orgInterfaceErrorCoverageRangeRequest,
    async (rangeRequest): Promise<KeyedMetricsSeriesResponse> => {
      const data = await metricsApi.orgsOrgIdMetricsQueryRangePost(
        { orgId: rangeRequest.request.scope.scopeId, metricsIntentQueryRangeRequest: rangeRequest.request },
        { cache: 'no-store' }
      );
      return { cacheKey: rangeRequest.cacheKey, range: { from: rangeRequest.request.range.from, to: rangeRequest.request.range.to }, data };
    },
    `${orgId ?? ''}:${selectedScope}:${rangePreset}:interface-error-coverage`
  );
  const {
    data: orgInterfaceErrorCoverageRangeData,
    error: orgInterfaceErrorCoverageRangeError,
    backendMissing: orgInterfaceErrorCoverageBackendMissing
  } = useApiQuery<KeyedMetricsSeriesResponse>(orgInterfaceErrorCoverageRangeFetcher, [orgInterfaceErrorCoverageRangeFetcher, apiRefreshNonce]);

  useEffect(() => {
    if (orgCpuRangeResponse) {
      setOrgCpuRangeCache((previous) => mergeKeyedMetricsSeriesResponse(previous, orgCpuRangeResponse));
    }
  }, [orgCpuRangeResponse]);
  const effectiveOrgCpuRangeData = metricsSeriesForCacheKey(orgCpuRangeCache, orgCpuRangeResponse, orgRangeCacheKey);
  const orgCpuRangeHasSamples = useMemo(() => metricSeriesHasSamples(effectiveOrgCpuRangeData, timeRange), [effectiveOrgCpuRangeData, timeRange]);

  // Interface error-rate coverage is queried independently from CPU Top-K so
  // the packet-error card can report whether its monitored series are complete.
  const orgInterfaceErrorRateRangeRequest = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    return {
      cacheKey: orgRangeCacheKey,
      request: metricIntentRangeRequest({
        orgId,
        intent: ORG_INTERFACE_ERROR_RATE_CONTRACT.intent,
        rangePreset,
        timeRange
      })
    } satisfies RangeMetricsRequest;
  }, [orgId, orgRangeCacheKey, rangePreset, selectedScope, timeRange]);
  const orgInterfaceErrorRateRangeFetcher = useLatestRequestFetcher(
    orgInterfaceErrorRateRangeRequest,
    async (rangeRequest): Promise<KeyedMetricsSeriesResponse> => {
      const data = await metricsApi.orgsOrgIdMetricsQueryRangePost(
        {
          orgId: rangeRequest.request.scope.scopeId,
          metricsIntentQueryRangeRequest: rangeRequest.request
        },
        { cache: 'no-store' }
      );
      return {
        cacheKey: rangeRequest.cacheKey,
        range: {
          from: rangeRequest.request.range.from,
          to: rangeRequest.request.range.to
        },
        data
      };
    },
    `${orgId ?? ''}:${selectedScope}:${rangePreset}:interface-errors`
  );
  const {
    data: orgInterfaceErrorRateRangeResponse,
    error: orgInterfaceErrorRateRangeError,
    backendMissing: orgInterfaceErrorRateRangeBackendMissing
  } = useApiQuery<KeyedMetricsSeriesResponse>(orgInterfaceErrorRateRangeFetcher, [orgInterfaceErrorRateRangeFetcher, apiRefreshNonce]);

  useEffect(() => {
    if (orgInterfaceErrorRateRangeResponse) {
      setOrgInterfaceErrorRateRangeCache((previous) => mergeKeyedMetricsSeriesResponse(previous, orgInterfaceErrorRateRangeResponse));
    }
  }, [orgInterfaceErrorRateRangeResponse]);
  const effectiveOrgInterfaceErrorRateRangeData = metricsSeriesForCacheKey(
    orgInterfaceErrorRateRangeCache,
    orgInterfaceErrorRateRangeResponse,
    orgRangeCacheKey
  );

  const orgBgpStatusFetcher = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    return async (): Promise<SignalListResponse> => {
      const items: Signal[] = [];
      let after = '';
      let meta: SignalListResponse['meta'] | undefined;
      for (let page = 0; page < ORG_PROTOCOL_RAW_ROLLUP_MAX_PAGES; page += 1) {
        const response = await signalsApi.orgsOrgIdSignalsGet(
          {
            orgId,
            scopeType: 'org',
            scopeId: orgId,
            family: 'bgp',
            signalKey: 'snmp_bgp_peer_state',
            limit: ORG_ROLLUP_PAGE_LIMIT,
            after: after || undefined
          },
          { cache: 'no-store' }
        );
        items.push(...(response.items ?? []));
        meta = response.meta;
        after = response.meta?.next ?? '';
        if (!after) {
          break;
        }
      }
      return {
        items,
        meta: {
          ...meta,
          limit: meta?.limit ?? ORG_ROLLUP_PAGE_LIMIT,
          exactTotal: meta?.exactTotal ?? items.length,
          next: after || undefined
        }
      };
    };
  }, [orgId, selectedScope, signalsApi]);
  const {
    data: orgBgpStatusData,
    error: orgBgpStatusError,
    backendMissing: orgBgpStatusBackendMissing
  } = useApiQuery<SignalListResponse>(orgBgpStatusFetcher, [orgBgpStatusFetcher, apiRefreshNonce]);

  const orgOspfStatusFetcher = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    return async (): Promise<SignalListResponse> => {
      const items: Signal[] = [];
      let after = '';
      let meta: SignalListResponse['meta'] | undefined;
      for (let page = 0; page < ORG_PROTOCOL_RAW_ROLLUP_MAX_PAGES; page += 1) {
        const response = await signalsApi.orgsOrgIdSignalsGet(
          {
            orgId,
            scopeType: 'org',
            scopeId: orgId,
            family: 'routing',
            signalKey: 'snmp_ospf_neighbor_state',
            limit: ORG_ROLLUP_PAGE_LIMIT,
            after: after || undefined
          },
          { cache: 'no-store' }
        );
        items.push(...(response.items ?? []));
        meta = response.meta;
        after = response.meta?.next ?? '';
        if (!after) {
          break;
        }
      }
      return {
        items,
        meta: {
          ...meta,
          limit: meta?.limit ?? ORG_ROLLUP_PAGE_LIMIT,
          exactTotal: meta?.exactTotal ?? items.length,
          next: after || undefined
        }
      };
    };
  }, [orgId, selectedScope, signalsApi]);
  const {
    data: orgOspfStatusData,
    error: orgOspfStatusError,
    backendMissing: orgOspfStatusBackendMissing
  } = useApiQuery<SignalListResponse>(orgOspfStatusFetcher, [orgOspfStatusFetcher, apiRefreshNonce]);

  const orgBfdStatusFetcher = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    return async (): Promise<SignalListResponse> => {
      const items: Signal[] = [];
      let after = '';
      let meta: SignalListResponse['meta'] | undefined;
      for (let page = 0; page < ORG_PROTOCOL_RAW_ROLLUP_MAX_PAGES; page += 1) {
        const response = await signalsApi.orgsOrgIdSignalsGet(
          {
            orgId,
            scopeType: 'org',
            scopeId: orgId,
            family: 'bfd',
            signalKey: 'snmp_bfd_session_up',
            limit: ORG_ROLLUP_PAGE_LIMIT,
            after: after || undefined
          },
          { cache: 'no-store' }
        );
        items.push(...(response.items ?? []));
        meta = response.meta;
        after = response.meta?.next ?? '';
        if (!after) {
          break;
        }
      }
      return {
        items,
        meta: {
          ...meta,
          limit: meta?.limit ?? ORG_ROLLUP_PAGE_LIMIT,
          exactTotal: meta?.exactTotal ?? items.length,
          next: after || undefined
        }
      };
    };
  }, [orgId, selectedScope, signalsApi]);
  const {
    data: orgBfdStatusData,
    backendMissing: orgBfdStatusBackendMissing
  } = useApiQuery<SignalListResponse>(orgBfdStatusFetcher, [orgBfdStatusFetcher, apiRefreshNonce]);

  const routingAdjacencyTopologyFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    const needsRoutingAdjacency =
      selectedScope === 'org' || (stage === 'signals' && (family === 'bgp' || family === 'routing' || family === 'bfd'));
    if (!needsRoutingAdjacency) {
      return null;
    }
    const scopeType = selectedDeviceId ? 'device' : selectedScope;
    const scopeId = selectedDeviceId || (selectedScope === 'org' ? orgId : selectedSiteId);
    if (!scopeId) {
      return null;
    }
    const limits: Record<string, unknown> = {
      limitNodes: selectedDeviceId ? 250 : 1000,
      limitEdges: selectedDeviceId ? 500 : 2500
    };
    if (selectedDeviceId) {
      limits.rootNodeId = `ref:inventory.device:${selectedDeviceId}`;
    }
    return async (): Promise<RoutingAdjacencyTopologyData> => {
      const fetchRoutingAdjacencyProtocols = async (
        requestScopeType: string,
        requestScopeId: string
      ): Promise<RoutingAdjacencyProtocol[]> => {
        const query = new URLSearchParams({
          scopeType: requestScopeType,
          scopeId: requestScopeId
        });
        const payload = await fetchJson<{ items?: Array<{ value?: string | null }> }>(
          buildOrgUrl(
            basePath,
            orgId,
            `topology/views/${ROUTING_ADJACENCY_TOPOLOGY_VIEW_ID}/params/protocol/options?${query.toString()}`
          ),
          { cache: 'no-store' }
        );
        const validProtocols = new Set<RoutingAdjacencyProtocol>(ROUTING_ADJACENCY_TOPOLOGY_PROTOCOLS);
        return normalizeTopologyProtocols(
          (payload.items ?? []).map((item) => item.value),
          validProtocols
        );
      };

      const resolveRoutingAdjacencyTopology = (requestScopeType: string, requestScopeId: string, protocol: RoutingAdjacencyProtocol) =>
        fetchJson<TopologyV2ResolveResponse>(
          buildOrgUrl(basePath, orgId, `topology/views/${ROUTING_ADJACENCY_TOPOLOGY_VIEW_ID}/resolve`),
          {
            method: 'POST',
            cache: 'no-store',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              scopeType: requestScopeType,
              scopeId: requestScopeId,
              params: { protocol },
              limits
            })
          }
        );

      const resolveRoutingAdjacencyTopologyProtocols = (
        requestScopeType: string,
        requestScopeId: string
      ): Promise<TopologySitesResolveResult<TopologyV2ResolveResponse>> =>
        fetchRoutingAdjacencyProtocols(requestScopeType, requestScopeId).then((protocols) => {
          if (protocols.length === 0) {
            return { payloads: [], failedCount: 0, totalCount: 0 };
          }
          return resolveTopologyForProtocols(protocols, (protocol) =>
            resolveRoutingAdjacencyTopology(requestScopeType, requestScopeId, protocol)
          );
        }).catch(() => topologyResolveFailureResult<TopologyV2ResolveResponse>());

      try {
        if (selectedScope === 'org' && !selectedDeviceId && sites.length > 0) {
          const siteResult = await resolveTopologyForSites(
            sites,
            TOPOLOGY_RESOLVE_CONCURRENCY,
            (site) => resolveRoutingAdjacencyTopologyProtocols('site', site.id)
          );
          return flattenNestedTopologyResolveResults(siteResult);
        }
        return await resolveRoutingAdjacencyTopologyProtocols(scopeType, scopeId);
      } catch {
        return null;
      }
    };
  }, [basePath, family, fetchJson, orgId, selectedDeviceId, selectedScope, selectedSiteId, sites, stage]);
  const { data: routingAdjacencyTopologyData, loading: routingAdjacencyTopologyLoading } = useApiQuery<RoutingAdjacencyTopologyData>(
    routingAdjacencyTopologyFetcher,
    [routingAdjacencyTopologyFetcher, apiRefreshNonce]
  );

  const lagMembershipTopologyFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    const needsLagMembership = stage === 'signals' && (family === 'lacp' || family === 'redundancy');
    if (!needsLagMembership) {
      return null;
    }
    // lag_membership is a site-scoped view. Resolve the full site graph and
    // apply the selected-device filter while converting membership edges to rows.
    const resolveContext = siteScopedTopologyResolveContext({
      selectedScope,
      selectedSiteId,
      selectedDeviceId,
      selectedNodeLimit: 250,
      selectedEdgeLimit: 500,
      siteNodeLimit: 1000,
      siteEdgeLimit: 2500
    });
    if (!resolveContext) {
      return null;
    }
    const { scopeType, scopeId, limits } = resolveContext;
    return async (): Promise<TopologyV2ResolveResponse | null> => {
      try {
        return await fetchJson<TopologyV2ResolveResponse>(
          buildOrgUrl(basePath, orgId, `topology/views/${LAG_MEMBERS_TOPOLOGY_VIEW_ID}/resolve`),
          {
            method: 'POST',
            cache: 'no-store',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              scopeType,
              scopeId,
              limits
            })
          }
        );
      } catch {
        return null;
      }
    };
  }, [basePath, family, fetchJson, orgId, selectedDeviceId, selectedScope, selectedSiteId, stage]);
  const { data: lagMembershipTopologyData } = useApiQuery<TopologyV2ResolveResponse | null>(lagMembershipTopologyFetcher, [
    lagMembershipTopologyFetcher,
    apiRefreshNonce
  ]);

  const vlanMembershipTopologyFetcher = useMemo(() => {
    if (!orgId || stage !== 'signals' || family !== 'vtp') {
      return null;
    }
    const scopeType = selectedDeviceId ? 'device' : selectedScope;
    const scopeId = selectedDeviceId || (selectedScope === 'org' ? orgId : selectedSiteId);
    if (!scopeId) {
      return null;
    }
    const limits: Record<string, unknown> = {
      limitNodes: selectedDeviceId ? 500 : 2000,
      limitEdges: selectedDeviceId ? 1000 : 5000
    };
    if (selectedDeviceId) {
      limits.rootNodeId = `ref:inventory.device:${selectedDeviceId}`;
    }
    return async (): Promise<TopologyV2ResolveResponse | null> => {
      try {
        return await fetchJson<TopologyV2ResolveResponse>(
          buildOrgUrl(basePath, orgId, `topology/views/${VLAN_MEMBERSHIP_TOPOLOGY_VIEW_ID}/resolve`),
          {
            method: 'POST',
            cache: 'no-store',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              scopeType,
              scopeId,
              limits
            })
          }
        );
      } catch {
        return null;
      }
    };
  }, [basePath, family, fetchJson, orgId, selectedDeviceId, selectedScope, selectedSiteId, stage]);
  const { data: vlanMembershipTopologyData } = useApiQuery<TopologyV2ResolveResponse | null>(vlanMembershipTopologyFetcher, [
    vlanMembershipTopologyFetcher,
    apiRefreshNonce
  ]);

  const vpnConnectionTopologyFetcher = useMemo(() => {
    if (!orgId || stage !== 'signals' || family !== 'tunnel') {
      return null;
    }
    // vpn_connection is site-scoped. Filtering the resolved rows client-side
    // also keeps the fallback working when a device is selected.
    const resolveContext = siteScopedTopologyResolveContext({
      selectedScope,
      selectedSiteId,
      selectedDeviceId,
      selectedNodeLimit: 250,
      selectedEdgeLimit: 500,
      siteNodeLimit: 1000,
      siteEdgeLimit: 2500
    });
    if (!resolveContext) {
      return null;
    }
    const { scopeType, scopeId, limits } = resolveContext;
    return async (): Promise<TopologyV2ResolveResponse | null> => {
      try {
        return await fetchJson<TopologyV2ResolveResponse>(
          buildOrgUrl(basePath, orgId, `topology/views/${VPN_CONNECTION_TOPOLOGY_VIEW_ID}/resolve`),
          {
            method: 'POST',
            cache: 'no-store',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              scopeType,
              scopeId,
              limits
            })
          }
        );
      } catch {
        return null;
      }
    };
  }, [basePath, family, fetchJson, orgId, selectedDeviceId, selectedScope, selectedSiteId, stage]);
  const { data: vpnConnectionTopologyData } = useApiQuery<TopologyV2ResolveResponse | null>(vpnConnectionTopologyFetcher, [
    vpnConnectionTopologyFetcher,
    apiRefreshNonce
  ]);

  const orgStpTopChangesFetcher = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    return async (): Promise<SignalListResponse> => {
      const items: Signal[] = [];
      let after = '';
      let meta: SignalListResponse['meta'] | undefined;
      for (let page = 0; page < ORG_PROTOCOL_RAW_ROLLUP_MAX_PAGES; page += 1) {
        const response = await signalsApi.orgsOrgIdSignalsGet(
          {
            orgId,
            scopeType: 'org',
            scopeId: orgId,
            family: 'stp',
            signalKey: 'snmp_stp_top_changes_total',
            limit: ORG_ROLLUP_PAGE_LIMIT,
            after: after || undefined
          },
          { cache: 'no-store' }
        );
        items.push(...(response.items ?? []));
        meta = response.meta;
        after = response.meta?.next ?? '';
        if (!after) {
          break;
        }
      }
      return {
        items,
        meta: {
          ...meta,
          limit: meta?.limit ?? ORG_ROLLUP_PAGE_LIMIT,
          exactTotal: meta?.exactTotal ?? items.length,
          next: after || undefined
        }
      };
    };
  }, [orgId, selectedScope, signalsApi]);
  const {
    data: orgStpTopChangesData,
    error: orgStpTopChangesError,
    backendMissing: orgStpTopChangesBackendMissing
  } = useApiQuery<SignalListResponse>(orgStpTopChangesFetcher, [orgStpTopChangesFetcher, apiRefreshNonce]);

  // Arista devices emit STP through the CLI collector rather than the SNMP
  // bridge MIB. Fetch it independently and merge it per device/instance in
  // the card below, so mixed vendor fleets retain both sources without
  // double-counting an STP instance.
  const orgCliStpTopChangesFetcher = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    return async (): Promise<SignalListResponse> => {
      const items: Signal[] = [];
      let after = '';
      let meta: SignalListResponse['meta'] | undefined;
      for (let page = 0; page < ORG_PROTOCOL_RAW_ROLLUP_MAX_PAGES; page += 1) {
        const response = await signalsApi.orgsOrgIdSignalsGet(
          {
            orgId,
            scopeType: 'org',
            scopeId: orgId,
            family: 'stp',
            signalKey: 'cli_stp_topology_change_count_total',
            limit: ORG_ROLLUP_PAGE_LIMIT,
            after: after || undefined
          },
          { cache: 'no-store' }
        );
        items.push(...(response.items ?? []));
        meta = response.meta;
        after = response.meta?.next ?? '';
        if (!after) {
          break;
        }
      }
      return {
        items,
        meta: {
          ...meta,
          limit: meta?.limit ?? ORG_ROLLUP_PAGE_LIMIT,
          exactTotal: meta?.exactTotal ?? items.length,
          next: after || undefined
        }
      };
    };
  }, [orgId, selectedScope, signalsApi]);
  const {
    data: orgCliStpTopChangesData,
    backendMissing: orgCliStpTopChangesBackendMissing
  } = useApiQuery<SignalListResponse>(orgCliStpTopChangesFetcher, [orgCliStpTopChangesFetcher, apiRefreshNonce]);

  const orgStpTopChangesRangeRequest = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    return {
      cacheKey: orgRangeCacheKey,
      request: metricRangeRequest({
        orgId,
        metric: 'snmp_stp_top_changes_total',
        rangePreset,
        timeRange,
        unit: 'changes'
      })
    } satisfies RangeMetricsRequest;
  }, [orgId, orgRangeCacheKey, rangePreset, selectedScope, timeRange]);
  const orgStpTopChangesRangeFetcher = useLatestRequestFetcher(
    orgStpTopChangesRangeRequest,
    async (rangeRequest): Promise<KeyedMetricsSeriesResponse> => {
      const data = await metricsApi.orgsOrgIdMetricsQueryRangePost(
        {
          orgId: rangeRequest.request.scope.scopeId,
          metricsIntentQueryRangeRequest: rangeRequest.request
        },
        { cache: 'no-store' }
      );
      return {
        cacheKey: rangeRequest.cacheKey,
        range: {
          from: rangeRequest.request.range.from,
          to: rangeRequest.request.range.to
        },
        data
      };
    },
    `${orgId ?? ''}:${selectedScope}:${rangePreset}:stp-changes`
  );
  const {
    data: orgStpTopChangesRangeResponse,
    backendMissing: orgStpTopChangesRangeBackendMissing
  } = useApiQuery<KeyedMetricsSeriesResponse>(orgStpTopChangesRangeFetcher, [orgStpTopChangesRangeFetcher, apiRefreshNonce]);

  useEffect(() => {
    if (orgStpTopChangesRangeResponse) {
      setOrgStpTopChangesRangeCache((previous) => mergeKeyedMetricsSeriesResponse(previous, orgStpTopChangesRangeResponse));
    }
  }, [orgStpTopChangesRangeResponse]);
  const effectiveOrgStpTopChangesRangeData = metricsSeriesForCacheKey(
    orgStpTopChangesRangeCache,
    orgStpTopChangesRangeResponse,
    orgRangeCacheKey
  );
  const stpTopChangesRangeUnavailable = orgStpTopChangesRangeBackendMissing && !effectiveOrgStpTopChangesRangeData;

  // CLI-collected STP counters need the same range treatment as their SNMP
  // counterpart. The range API accepts the metric name, so keep a separate
  // cache and query rather than falling back to the latest cumulative value.
  const orgCliStpTopChangesRangeRequest = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    return {
      cacheKey: orgRangeCacheKey,
      request: metricRangeRequest({
        orgId,
        metric: 'cli_stp_topology_change_count_total',
        rangePreset,
        timeRange,
        unit: 'changes'
      })
    } satisfies RangeMetricsRequest;
  }, [orgId, orgRangeCacheKey, rangePreset, selectedScope, timeRange]);
  const orgCliStpTopChangesRangeFetcher = useLatestRequestFetcher(
    orgCliStpTopChangesRangeRequest,
    async (rangeRequest): Promise<KeyedMetricsSeriesResponse> => {
      const data = await metricsApi.orgsOrgIdMetricsQueryRangePost(
        {
          orgId: rangeRequest.request.scope.scopeId,
          metricsIntentQueryRangeRequest: rangeRequest.request
        },
        { cache: 'no-store' }
      );
      return {
        cacheKey: rangeRequest.cacheKey,
        range: {
          from: rangeRequest.request.range.from,
          to: rangeRequest.request.range.to
        },
        data
      };
    },
    `${orgId ?? ''}:${selectedScope}:${rangePreset}:cli-stp-changes`
  );
  const {
    data: orgCliStpTopChangesRangeResponse,
    loading: orgCliStpTopChangesRangeLoading,
    backendMissing: orgCliStpTopChangesRangeBackendMissing
  } = useApiQuery<KeyedMetricsSeriesResponse>(orgCliStpTopChangesRangeFetcher, [orgCliStpTopChangesRangeFetcher, apiRefreshNonce]);

  useEffect(() => {
    if (orgCliStpTopChangesRangeResponse) {
      setOrgCliStpTopChangesRangeCache((previous) => mergeKeyedMetricsSeriesResponse(previous, orgCliStpTopChangesRangeResponse));
    }
  }, [orgCliStpTopChangesRangeResponse]);
  const effectiveOrgCliStpTopChangesRangeData = metricsSeriesForCacheKey(
    orgCliStpTopChangesRangeCache,
    orgCliStpTopChangesRangeResponse,
    orgRangeCacheKey
  );
  const cliStpTopChangesRangeUnavailable = orgCliStpTopChangesRangeBackendMissing && !effectiveOrgCliStpTopChangesRangeData;

  const orgLacpSyncFetcher = useMemo(() => {
    if (!orgId || selectedScope !== 'org') {
      return null;
    }
    return async (): Promise<SignalListResponse> => {
      const items: Signal[] = [];
      let after = '';
      let meta: SignalListResponse['meta'] | undefined;
      for (let page = 0; page < ORG_PROTOCOL_RAW_ROLLUP_MAX_PAGES; page += 1) {
        const response = await signalsApi.orgsOrgIdSignalsGet(
          {
            orgId,
            scopeType: 'org',
            scopeId: orgId,
            family: 'lacp',
            signalKey: 'snmp_lacp_member_synchronized',
            limit: ORG_ROLLUP_PAGE_LIMIT,
            after: after || undefined
          },
          { cache: 'no-store' }
        );
        items.push(...(response.items ?? []));
        meta = response.meta;
        after = response.meta?.next ?? '';
        if (!after) {
          break;
        }
      }
      return {
        items,
        meta: {
          ...meta,
          limit: meta?.limit ?? ORG_ROLLUP_PAGE_LIMIT,
          exactTotal: meta?.exactTotal ?? items.length,
          next: after || undefined
        }
      };
    };
  }, [orgId, selectedScope, signalsApi]);
  const {
    data: orgLacpSyncData,
    error: orgLacpSyncError,
    backendMissing: orgLacpSyncBackendMissing
  } = useApiQuery<SignalListResponse>(orgLacpSyncFetcher, [orgLacpSyncFetcher, apiRefreshNonce]);

  const deviceSummaries = effectiveDeviceSummaryData?.items ?? [];
  const connectorSummaries = connectorSummaryData?.items ?? [];
  const deviceTotalCount = effectiveDeviceSummaryData?.meta?.exactTotal ?? deviceSummaries.length;
  const detailedCoverageDeviceTotal = effectiveDeviceSummaryData ? deviceTotalCount : null;
  const reportingCoverageDeviceTotalCount = coverageDeviceTotal(coverageDeviceCountData, detailedCoverageDeviceTotal);
  const deviceInventoryLoaded = Boolean(effectiveDeviceSummaryData);

  const deviceSummaryById = useMemo(() => {
    const out = new Map<string, DeviceSummary>();
    for (const device of deviceSummaries) {
      if (device.id) {
        out.set(device.id, device);
      }
    }
    return out;
  }, [deviceSummaries]);

  const deviceSummaryByDisplayName = useMemo(() => {
    const out = new Map<string, DeviceSummary>();
    for (const device of deviceSummaries) {
      if (device.displayName) {
        out.set(device.displayName, device);
      }
      if (device.hostname) {
        out.set(device.hostname, device);
      }
    }
    return out;
  }, [deviceSummaries]);

  const deviceOptions = useMemo<DeviceOption[]>(() => {
    const byId = new Map<string, DeviceOption>();
    const upsertDevice = (id?: string | null, label?: string | null, role?: string | null) => {
      const normalizedId = id?.trim();
      if (!normalizedId) {
        return;
      }
      const existing = byId.get(normalizedId);
      const normalizedLabel = label?.trim();
      const normalizedRole = role?.trim();
      byId.set(normalizedId, {
        id: normalizedId,
        label: normalizedLabel || existing?.label || normalizedId,
        role: normalizedRole || existing?.role
      });
    };

    for (const device of devicesData?.items ?? []) {
      if (!device.id) {
        continue;
      }
      const label = device.displayName ?? device.hostname ?? device.id;
      const summary = deviceSummaryById.get(device.id) ?? deviceSummaryByDisplayName.get(label);
      upsertDevice(device.id, label, summary?.role ?? (device as { role?: string | null }).role);
    }

    for (const device of deviceSummariesForScope(deviceSummaries, selectedScope, selectedSiteId)) {
      upsertDevice(device.id, device.displayName ?? device.hostname ?? device.id, device.role);
    }

    return Array.from(byId.values()).sort((left, right) => left.label.localeCompare(right.label));
  }, [deviceSummaries, deviceSummaryByDisplayName, deviceSummaryById, devicesData?.items, selectedScope, selectedSiteId]);

  const siteDeviceRoleOptions = useMemo(() => {
    const roles = new Set<string>();
    for (const device of deviceOptions) {
      if (device.role) {
        roles.add(device.role);
      }
    }
    for (const device of deviceSummaries) {
      if (selectedSiteId && device.siteId !== selectedSiteId) {
        continue;
      }
      if (device.role) {
        roles.add(device.role);
      }
    }
    return sortDeviceRoles(roles, true);
  }, [deviceOptions, deviceSummaries, selectedSiteId]);

  const roleFilteredDeviceOptions = useMemo(
    () => deviceOptions.filter((device) => deviceRoleMatches(device.role, selectedDeviceRole)),
    [deviceOptions, selectedDeviceRole]
  );

  const roleFilteredDeviceIds = useMemo(() => new Set(roleFilteredDeviceOptions.map((device) => device.id)), [roleFilteredDeviceOptions]);

  const hasDeviceRoleFilter = selectedScope !== 'org' && selectedDeviceRole !== 'all';

  const selectedRoleDeviceIds = useMemo(() => {
    const out = new Set<string>();
    if (!hasDeviceRoleFilter) {
      return [];
    }
    for (const device of roleFilteredDeviceOptions) {
      out.add(device.id);
    }
    for (const device of deviceSummaries) {
      if (selectedSiteId && device.siteId !== selectedSiteId) {
        continue;
      }
      if (device.id && deviceRoleMatches(device.role, selectedDeviceRole)) {
        out.add(device.id);
      }
    }
    return Array.from(out).sort();
  }, [deviceSummaries, hasDeviceRoleFilter, roleFilteredDeviceOptions, selectedDeviceRole, selectedSiteId]);

  const selectedRoleDeviceBinding = useMemo(() => {
    if (!hasDeviceRoleFilter) {
      return undefined;
    }
    if (selectedRoleDeviceIds.length === 0) {
      return '__reap_no_matching_devices__';
    }
    return `(${selectedRoleDeviceIds.map(escapeRegexValue).join('|')})`;
  }, [hasDeviceRoleFilter, selectedRoleDeviceIds]);

  const roleFilteredDeviceIdentifiers = useMemo(() => {
    const out = new Set<string>();
    if (!hasDeviceRoleFilter) {
      return out;
    }
    const addIdentifier = (value?: string | null) => {
      const normalized = value?.trim();
      if (normalized) {
        out.add(normalized);
      }
    };
    for (const device of roleFilteredDeviceOptions) {
      addIdentifier(device.id);
      addIdentifier(device.label);
    }
    for (const device of deviceSummaries) {
      if (selectedSiteId && device.siteId !== selectedSiteId) {
        continue;
      }
      if (!deviceRoleMatches(device.role, selectedDeviceRole)) {
        continue;
      }
      addIdentifier(device.id);
      addIdentifier(device.displayName);
    }
    return out;
  }, [deviceSummaries, hasDeviceRoleFilter, roleFilteredDeviceOptions, selectedDeviceRole, selectedSiteId]);

  const deviceNameById = useMemo(() => {
    const out = new Map<string, string>();
    for (const item of deviceOptions) {
      out.set(item.id, item.label);
    }
    return out;
  }, [deviceOptions]);

  const selectedDeviceSummary = useMemo(
    () => deviceSummaries.find((device) => device.id === selectedDeviceId) ?? null,
    [deviceSummaries, selectedDeviceId]
  );
  const inventoryFreshDeviceCount = useMemo(
    () => deviceSummaries.filter((device) => (ageInMinutes(device.lastSeenAt) ?? Number.POSITIVE_INFINITY) <= DEVICE_STALE_MINUTES).length,
    [deviceSummaries]
  );
  // Membership filter: accepts canonical IDs and inventory aliases. All
  // aggregations must use inventoryDeviceCanonicalIds to avoid double-counting.
  const inventoryDeviceIds = useMemo(() => {
    return inventoryDeviceIdentitySet(deviceSummaries);
  }, [deviceSummaries]);
  const inventoryDeviceCanonicalIds = useMemo(
    () => inventoryDeviceCanonicalIdentityMap(deviceSummaries),
    [deviceSummaries]
  );
  // Organization coverage is served only by the compact summary endpoint.
  // Do not fall back to a broad raw signal rollup when the summary is empty or
  // unavailable; that path was capped at 5,000 rows and produced misleading
  // partial counts for large organizations.
  const telemetryReportingDeviceIds = useMemo(() => new Set<string>(), []);
  const hasTelemetryCoverageData = Boolean(orgDashboardSignalSummaries);
  const rawTelemetryReportingDeviceCount = orgDashboardSignalSummaries?.uptime.total ?? 0;
  const telemetryReportingInventoryDeviceCount = hasTelemetryCoverageData
    ? Math.min(rawTelemetryReportingDeviceCount, reportingCoverageDeviceTotalCount)
    : 0;
  const activeDeviceCount = hasTelemetryCoverageData
    ? Math.min(reportingCoverageDeviceTotalCount, telemetryReportingInventoryDeviceCount)
    : Math.min(reportingCoverageDeviceTotalCount, inventoryFreshDeviceCount);
  const staleDeviceCount = Math.max(0, reportingCoverageDeviceTotalCount - activeDeviceCount);
  const reportingCoverageSourceLabel = hasTelemetryCoverageData ? 'SNMP uptime telemetry' : 'No telemetry data';
  const reportingCoverageWarning = orgDashboardSignalSummariesError
    ? `Telemetry coverage unavailable: ${orgDashboardSignalSummariesError.message}`
    : '';
  const reportingCoverageDisplayData = reportingCoverageDisplay({
    activeDeviceCount,
    hasTelemetryCoverageData,
    rawTelemetryReportingDeviceCount,
    reportingCoverageDeviceTotalCount,
    reportingCoverageSourceLabel
  });
  const reportingCoverageLabel = reportingCoverageDisplayData.label;
  const reportingCoverageSecondary = reportingCoverageDisplayData.secondary;
  const connectorTotalCount = connectorSummaryData?.meta?.exactTotal ?? connectorSummaries.length;
  const onlineConnectorCount = useMemo(() => connectorSummaries.filter(isConnectorConnected).length, [connectorSummaries]);
  const staleConnectorCount = useMemo(
    () => connectorSummaries.filter(isConnectorStale).length,
    [connectorSummaries]
  );
  const orgDeviceNameById = useMemo(() => {
    const byId = new Map<string, DeviceSummary>();
    for (const device of [
      ...deviceSummaries,
      ...(coverageDeviceCountData?.items ?? [])
    ]) {
      const id = device.id?.trim();
      if (id && !byId.has(id)) {
        byId.set(id, device);
      }
    }
    return inventoryDeviceNameMap(Array.from(byId.values()));
  }, [coverageDeviceCountData?.items, deviceSummaries]);
  const orgCpuRankedSeries = useMemo(() => {
    if (orgCpuRangeHasSamples && effectiveOrgCpuRangeData?.series?.length) {
      return rankedCpuMetricSeries({
        series: effectiveOrgCpuRangeData.series,
        mode: 'range',
        timeRange,
        inventoryDeviceCanonicalIds,
        inventoryDeviceIds,
        deviceInventoryLoaded,
        orgDeviceNameById
      });
    }
    return rankedCpuMetricSeries({
      series: orgCpuTopInstantData?.series ?? [],
      mode: 'instant',
      timeRange,
      inventoryDeviceCanonicalIds,
      inventoryDeviceIds,
      deviceInventoryLoaded,
      orgDeviceNameById
    });
  }, [
    deviceInventoryLoaded,
    effectiveOrgCpuRangeData?.series,
    inventoryDeviceCanonicalIds,
    inventoryDeviceIds,
    orgCpuRangeHasSamples,
    orgCpuTopInstantData?.series,
    orgDeviceNameById,
    timeRange
  ]);
  const orgCpuRankedDeviceIds = useMemo(
    () => orgCpuRankedSeries.map((series) => series.id).slice(0, ORG_LATEST_HEALTH_DEVICE_LIMIT),
    [orgCpuRankedSeries]
  );
  const orgLatestDeviceIds = orgCpuRankedDeviceIds;
  // The targeted health fetcher cannot start until CPU Top-K identifies its
  // device set. Keep the table loading while neither source has settled so an
  // empty state is never shown between inventory and telemetry requests.
  const orgDeviceHealthTopKLoading =
    !orgCpuRangeHasSamples &&
    !orgCpuTopInstantData &&
    (orgCpuRangeLoading || orgCpuTopInstantLoading);
  const orgLatestDeviceCanonicalIds = useMemo(
    () => new Set(orgLatestDeviceIds.map((id) => canonicalInventoryDeviceId(id, inventoryDeviceCanonicalIds))),
    [inventoryDeviceCanonicalIds, orgLatestDeviceIds]
  );
  const orgLatestDeviceHealthFetcher = useMemo(() => {
    if (!orgId || selectedScope !== 'org' || orgLatestDeviceIds.length === 0) return null;
    const fetchSignals = async (family: string, signalKey: string): Promise<SignalListResponse & { truncated: boolean }> => {
      const items: Signal[] = [];
      let after = '';
      let meta: SignalListResponse['meta'] | undefined;
      for (let page = 0; page < TARGETED_HEALTH_MAX_PAGES; page += 1) {
        const response = await signalsApi.orgsOrgIdSignalsGet(
          {
            orgId,
            scopeType: 'org',
            scopeId: orgId,
            family,
            signalKey,
            deviceIds: orgLatestDeviceIds,
            limit: TARGETED_HEALTH_PAGE_SIZE,
            after: after || undefined
          },
          { cache: 'no-store' }
        );
        items.push(...(response.items ?? []));
        meta = response.meta;
        after = response.meta?.next ?? '';
        if (!after) break;
      }
      return {
        items,
        truncated: Boolean(after),
        meta: {
          ...meta,
          limit: meta?.limit ?? TARGETED_HEALTH_PAGE_SIZE,
          exactTotal: meta?.exactTotal ?? items.length,
          next: after || undefined
        }
      };
    };
    return async (): Promise<OrgLatestDeviceHealthData> => {
      const [cpu, mem, uptime, interfaces, bgp] = await Promise.all([
        fetchSignals('device_health', 'snmp_device_cpu_pct'),
        fetchSignals('device_health', 'snmp_device_mem_pct'),
        fetchSignals('device_health', 'snmp_device_uptime_seconds'),
        fetchSignals('interface', 'snmp_if_oper_status'),
        fetchSignals('bgp', 'snmp_bgp_peer_state')
      ]);
      return { cpu, mem, uptime, interfaces, bgp, truncated: cpu.truncated || mem.truncated || uptime.truncated || interfaces.truncated || bgp.truncated };
    };
  }, [orgId, orgLatestDeviceIds, selectedScope, signalsApi]);
  const {
    data: orgLatestDeviceHealthData,
    loading: orgLatestDeviceHealthLoading,
    error: orgLatestDeviceHealthError,
    backendMissing: orgLatestDeviceHealthBackendMissing
  } = useApiQuery<OrgLatestDeviceHealthData>(
    orgLatestDeviceHealthFetcher,
    [orgLatestDeviceHealthFetcher, apiRefreshNonce]
  );
  const orgSiteNameById = useMemo(() => {
    const out = new Map<string, string>();
    for (const site of sitesData?.items ?? []) {
      const id = site.id?.trim();
      if (!id) {
        continue;
      }
      out.set(id, site.name ?? id);
    }
    return out;
  }, [sitesData?.items]);
  const orgInterfaceCountsDisplay = useMemo<OrgInterfaceCounts>(() => {
    if (!orgDashboardSignalSummaries) {
      return {
        total: 0,
        up: 0,
        down: 0,
        operationallyDown: 0,
        adminDown: 0,
        unexpectedDown: 0,
        idleEnabledDown: 0,
        unclassifiedDown: 0,
        lowerLayerDown: 0,
        unknown: 0,
        stale: 0,
        lastObservedAt: '',
        unexpectedDownDetails: [],
        classificationAvailable: false,
        truncated: false
      };
    }
    const { interfaceOper, interfaceAdmin } = orgDashboardSignalSummaries;
    const adminDown = interfaceOper.adminDown ?? Math.min(interfaceOper.nonEqualCount, interfaceAdmin.equalCount);
    const lowerLayerDown = interfaceOper.lowerLayerDown ?? 0;
    const idleEnabledDown = interfaceOper.enabledDown ?? 0;
    const unclassifiedDown = interfaceOper.unclassifiedDown ?? Math.max(0, interfaceOper.nonEqualCount - adminDown - lowerLayerDown - idleEnabledDown);
    // Every non-oper-up interface is operationally down. The category
    // counters below are mutually exclusive, but operationallyDown is the
    // total down population and must not subtract admin-down rows.
    const operationallyDown = interfaceOper.nonEqualCount;
    return {
      total: interfaceOper.total,
      up: interfaceOper.equalCount,
      down: interfaceOper.nonEqualCount,
      operationallyDown,
      adminDown,
      unexpectedDown: 0,
      idleEnabledDown,
      unclassifiedDown,
      lowerLayerDown,
      unknown: 0,
      stale: 0,
      lastObservedAt: interfaceOper.observedAt,
      unexpectedDownDetails: [],
      // Compact summaries do not expose per-interface labels, so these
      // classifications are unavailable rather than meaningful zeroes.
      classificationAvailable: false,
      truncated: false
    };
  }, [orgDashboardSignalSummaries]);
  const orgInterfaceStatusError = orgDashboardSignalSummariesError;
  const orgInterfaceStatusLoading =
    selectedScope === 'org' &&
    !orgDashboardSignalSummaries &&
    orgDashboardSignalSummariesLoading &&
    !orgInterfaceStatusError;
  const orgInterfaceStatusBackendUnavailable = orgDashboardSignalSummariesBackendMissing;
  const orgInterfaceErrorRateUnavailable = orgInterfaceErrorRateRangeBackendMissing;
  const orgInterfaceErrorRateFailed = Boolean(orgInterfaceErrorRateRangeError && !orgInterfaceErrorRateUnavailable);
  const orgFleetCpu = useMemo<OrgFleetCpu>(() => {
    // The range and instant CPU intents are Top-K queries and therefore only
    // return the chart series.  Keep the headline average and denominator
    // sourced from the organization-wide summary so the card does not label a
    // Top-5 average as a fleet average.
    const hasCpuSummary = Boolean(
      orgDashboardSignalSummaries &&
        Number.isFinite(orgDashboardSignalSummaries.cpu.average) &&
        Number.isFinite(orgDashboardSignalSummaries.cpu.total)
    );
    const summaryAverage = hasCpuSummary ? boundedPercent(orgDashboardSignalSummaries?.cpu.average ?? 0) : undefined;
    const summaryDevices = hasCpuSummary ? Math.max(0, Math.round(orgDashboardSignalSummaries?.cpu.total ?? 0)) : undefined;
    // The compact Top-12 result is the canonical device set for both the
    // health table and this card. Render only its first five here so the
    // chart and table cannot disagree about which devices are ranked.
    if (orgCpuRankedSeries.length > 0) {
      const topSeries = orgCpuRankedSeries.slice(0, 5);
      const peak = topSeries.reduce<TrendSeries | null>((best, item) => (!best || item.peakValue > best.peakValue ? item : best), null);
      return {
        avg: summaryAverage ?? finiteAverage(orgCpuRankedSeries.map((item) => item.summaryValue)),
        devices: summaryDevices ?? orgCpuRankedSeries.length,
        peakValue: peak?.peakValue ?? 0,
        peakDeviceId: peak?.id ?? '',
        topDevices: topSeries.map((item) => ({ deviceId: item.id, label: item.label, value: item.summaryValue })),
        series: topSeries,
        truncated: false
      };
    }
    if (orgDashboardSignalSummaries) {
      return {
        avg: summaryAverage ?? 0,
        devices: summaryDevices ?? 0,
        peakValue: orgDashboardSignalSummaries.cpu.maximum ?? 0,
        peakDeviceId: '',
        topDevices: [],
        series: [],
        truncated: false,
        aggregateOnly: true
      };
    }

    return {
      avg: 0,
      devices: 0,
      peakValue: 0,
      peakDeviceId: '',
      topDevices: [],
      series: [],
      truncated: false
    };
  }, [orgCpuRankedSeries, orgDashboardSignalSummaries]);
  const topInterfaceErrorRate = useMemo<OrgInterfaceErrorRateSummary>(() => {
    if (effectiveOrgInterfaceErrorRateRangeData?.series?.length) {
      const rows = effectiveOrgInterfaceErrorRateRangeData.series
        .map((series) => {
          const labels = series.labels ?? {};
          const deviceId = deviceIdFromMetricLabels(labels);
          if (!deviceId) {
            return null;
          }
          if (deviceInventoryLoaded && !inventoryDeviceIds.has(deviceId)) {
            return null;
          }
          const metricIdentity = metricSeriesDeviceIdentity(labels, inventoryDeviceCanonicalIds, orgDeviceNameById);
          const canonicalDeviceId = metricIdentity.id;
          const rawSamples = trendSamplesFromMetricSeries(series);
          if (rawSamples.length === 0 || !trendSamplesAreFresh(rawSamples, timeRange)) {
            return null;
          }
          const samples = trendSamplesForRangeDisplay(series, timeRange);
          const values = samples.map((sample) => sample.value);
          const targetId = targetIdFromMetricLabels(labels, canonicalDeviceId);
          const ifName = interfaceNameFromMetricLabels(labels) || interfaceNameFromTargetId(targetId) || 'interface';
          const deviceLabel = metricIdentity.label;
          // Use raw, time-sorted samples for KPI semantics. Display samples may
          // be reshaped for chart rendering (for example, a one-point series).
          const latestSample = latestRawMetricSample(rawSamples);
          if (!latestSample) {
            return null;
          }
          const latestValue = latestSample.value;
          const peakValue = finiteMax(values);
          const trendSeries: TrendSeries = {
            id: `${canonicalDeviceId}:${targetId || ifName}`,
            label: `${ifName} on ${deviceLabel}`,
            values: samples,
            summaryValue: finiteAverage(values),
            peakValue
          };
          return {
            trendSeries,
            value: latestValue,
            deviceId: canonicalDeviceId,
            targetId,
            instance: ifName,
            latestObservedAt: latestSample.t.getTime()
          };
        })
        .filter((row): row is { trendSeries: TrendSeries; value: number; deviceId: string; targetId: string; instance: string; latestObservedAt: number } => Boolean(row));

      const sorted = rows.sort((left, right) =>
        compareLatestMetricRows(
          { value: left.value, latestObservedAt: left.latestObservedAt, summaryValue: left.trendSeries.summaryValue },
          { value: right.value, latestObservedAt: right.latestObservedAt, summaryValue: right.trendSeries.summaryValue }
        )
      );
      const top = sorted[0] ?? null;
      return {
        value: top?.value ?? 0,
        deviceId: top?.deviceId ?? '',
        targetId: top?.targetId ?? '',
        instance: top?.instance ?? '',
        reportingInterfaces: rows.length,
        totalFeatureRows: rows.length,
        series: sorted.slice(0, 5).map((row) => row.trendSeries),
        source: 'range',
        truncated: false
      };
    }
    return {
      value: 0,
      deviceId: '',
      targetId: '',
      instance: '',
      reportingInterfaces: 0,
      totalFeatureRows: 0,
      series: [],
      source: 'none',
      truncated: false
    };
  }, [
    inventoryDeviceCanonicalIds,
    deviceInventoryLoaded,
    effectiveOrgInterfaceErrorRateRangeData?.series,
    inventoryDeviceIds,
    orgDeviceNameById,
    timeRange
  ]);
  const orgInterfaceErrorCoverage = useMemo(() => {
    const series = orgInterfaceErrorCoverageRangeData?.data?.series ?? [];
    // The coverage intent returns one scalar count series to stay below the
    // metrics-gateway series guardrail, not one series per interface.
    // Use the newest sample in the selected range. Using Math.max here caused
    // an old, larger count (for example 2,043) to remain visible after the
    // current Prometheus count had decreased.
    const observedRaw = latestInterfaceErrorCoverageValue(series);
    const normalized = normalizeInterfaceErrorCoverage(observedRaw, orgInterfaceCountsDisplay.total);
    return {
      observedRaw: normalized.observedRaw,
      observed: normalized.observed,
      expected: normalized.expected,
      complete: normalized.complete,
      integrityError: normalized.inconsistent,
      failed: normalized.inconsistent || Boolean(orgInterfaceErrorCoverageRangeError) || orgInterfaceErrorCoverageBackendMissing
    };
  }, [orgInterfaceCountsDisplay.total, orgInterfaceErrorCoverageBackendMissing, orgInterfaceErrorCoverageRangeData, orgInterfaceErrorCoverageRangeError]);

  const orgFleetCpuDisplay = orgFleetCpu;
  const topInterfaceErrorRateDisplay = topInterfaceErrorRate;
  const routingAdjacencySiteResolveResult = useMemo(
    () => topologySitesResolveResult(routingAdjacencyTopologyData),
    [routingAdjacencyTopologyData]
  );
  const routingAdjacencyTopologyPayload = useMemo(
    () => topologyPayloadsFromResolveData(routingAdjacencyTopologyData),
    [routingAdjacencyTopologyData]
  );
  const routingAdjacencyTopologyRows = useMemo(
    () =>
      topologyRoutingRowsFromResolveResponses({
        payload: routingAdjacencyTopologyPayload,
        deviceNameById: orgDeviceNameById,
        selectedDeviceId,
        inventoryDeviceIds,
        inventoryLoaded: deviceInventoryLoaded
      }),
    [deviceInventoryLoaded, inventoryDeviceIds, orgDeviceNameById, routingAdjacencyTopologyPayload, selectedDeviceId]
  );
  const lagMembershipTopologyRows = useMemo(
    () =>
      topologyLagMemberRowsFromResolveResponse({
        payload: lagMembershipTopologyData,
        inventoryDeviceIds,
        inventoryLoaded: deviceInventoryLoaded,
        deviceNameById: orgDeviceNameById,
        selectedDeviceId
      }),
    [deviceInventoryLoaded, inventoryDeviceIds, lagMembershipTopologyData, orgDeviceNameById, selectedDeviceId]
  );
  const vlanMembershipTopologyRows = useMemo(
    () =>
      topologyVlanMembershipRowsFromResolveResponse({
        payload: vlanMembershipTopologyData,
        inventoryDeviceIds,
        inventoryLoaded: deviceInventoryLoaded,
        deviceNameById: orgDeviceNameById,
        selectedDeviceId
      }),
    [deviceInventoryLoaded, inventoryDeviceIds, orgDeviceNameById, selectedDeviceId, vlanMembershipTopologyData]
  );
  const vpnConnectionTopologyRows = useMemo(
    () =>
      topologyTunnelRowsFromResolveResponse({
        payload: vpnConnectionTopologyData,
        inventoryDeviceIds,
        inventoryLoaded: deviceInventoryLoaded,
        deviceNameById: orgDeviceNameById,
        selectedDeviceId
      }),
    [deviceInventoryLoaded, inventoryDeviceIds, orgDeviceNameById, selectedDeviceId, vpnConnectionTopologyData]
  );
  const routingProtocolHealth = useMemo<RoutingProtocolHealth[]>(() => {
    const summarize = (
      protocol: RoutingProtocolHealth['protocol'],
      data: SignalListResponse | undefined,
      unavailable: boolean,
      upValue: number
    ): RoutingProtocolHealth => {
      const latestByPeer = new Map<string, { up: boolean; timestamp: number }>();
      for (const row of data?.items ?? []) {
        if (!isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
          continue;
        }
        if (!isFreshSignalRow(row, DEVICE_STALE_MINUTES, timeRange.to)) {
          continue;
        }
        const peerKey = signalRoutingAdjacencyDedupeKey(row, protocol);
        if (!peerKey.trim()) {
          continue;
        }
        const previous = latestByPeer.get(peerKey);
        const timestamp = signalTimestampMs(row.observedAt);
        if (!previous || timestamp >= previous.timestamp) {
          const value = typeof row.value === 'number' ? row.value : Number.NaN;
          latestByPeer.set(peerKey, {
            up: !Number.isNaN(value) && value === upValue,
            timestamp
          });
        }
      }

      const topologyProtocol = (protocol === 'IS-IS' ? 'isis' : protocol.toLowerCase()) as RoutingAdjacencyProtocol;
      for (const row of routingAdjacencyTopologyRows) {
        if (!isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
          continue;
        }
        if (row.protocol !== topologyProtocol) {
          continue;
        }
        if (!isFreshSignalRow(row, DEVICE_STALE_MINUTES, timeRange.to)) {
          continue;
        }
        const peerKey = topologyRoutingAdjacencyMergeKey(row, (candidate) => latestByPeer.has(candidate));
        const previous = latestByPeer.get(peerKey);
        const timestamp = signalTimestampMs(row.observedAt);
        if (shouldReplaceRoutingHealthSample(previous, { up: row.up, timestamp })) {
          latestByPeer.set(peerKey, {
            up: row.up,
            timestamp
          });
        }
      }

      let up = 0;
      let down = 0;
      for (const row of latestByPeer.values()) {
        if (row.up) {
          up += 1;
        } else {
          down += 1;
        }
      }
      return { protocol, total: latestByPeer.size, up, down, unavailable };
    };

    // The compact aggregate endpoint is not device-inventory scoped. Once the
    // inventory is loaded, derive counts from the filtered detail/topology rows
    // rather than allowing historical organization-wide aggregates to leak in.
    const bgpAggregate = deviceInventoryLoaded ? undefined : orgDashboardSignalSummaries?.bgp;
    const bgpPreviewTruncated = Boolean(orgBgpStatusData?.meta?.next);
    const bgpSummary = bgpAggregate
      ? {
          protocol: 'BGP' as const,
          total: Math.max(0, bgpAggregate.total),
          up: Math.max(0, bgpAggregate.equalCount),
          down: Math.max(0, bgpAggregate.nonEqualCount),
          unavailable: false,
          previewTruncated: bgpPreviewTruncated
        }
      : null;
    const ospfAggregate = deviceInventoryLoaded ? undefined : orgDashboardSignalSummaries?.ospf;
    const ospfSummary = ospfAggregate
      ? {
          protocol: 'OSPF' as const,
          total: Math.max(0, ospfAggregate.total),
          up: Math.max(0, ospfAggregate.equalCount),
          down: Math.max(0, ospfAggregate.nonEqualCount),
          unavailable: false
        }
      : null;

    const summaries: RoutingProtocolHealth[] = [
      bgpSummary ?? summarize('BGP', orgBgpStatusData ?? undefined, orgBgpStatusBackendMissing, 6),
      ospfSummary ?? summarize('OSPF', orgOspfStatusData ?? undefined, orgOspfStatusBackendMissing, 8)
    ];
    const isisSummaryBase = summarize('IS-IS', undefined, false, 1);
    const isisSummary = {
      ...isisSummaryBase,
      unavailable: topologyResolveAllFailed(routingAdjacencySiteResolveResult) && isisSummaryBase.total === 0
    };
    if (isisSummary.total > 0 || isisSummary.unavailable) {
      summaries.push(isisSummary);
    }
    return summaries;
  }, [
    deviceInventoryLoaded,
    inventoryDeviceIds,
    orgBgpStatusBackendMissing,
    orgBgpStatusData,
    orgDashboardSignalSummaries?.bgp,
    orgDashboardSignalSummaries?.ospf,
    orgOspfStatusBackendMissing,
    orgOspfStatusData,
    routingAdjacencySiteResolveResult,
    routingAdjacencyTopologyRows,
    timeRange.to
  ]);
  const routingDownCount = routingProtocolHealth.reduce((sum, item) => sum + item.down, 0);
  const routingTotalCount = routingProtocolHealth.reduce((sum, item) => sum + item.total, 0);
  const liveRoutingTelemetryCount =
    (orgBgpStatusData?.items ?? []).filter(
      (row) => isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded) && isFreshSignalRow(row, DEVICE_STALE_MINUTES, timeRange.to)
    ).length +
    (orgOspfStatusData?.items ?? []).filter(
      (row) => isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded) && isFreshSignalRow(row, DEVICE_STALE_MINUTES, timeRange.to)
    ).length;
  const routingHealthLiveTelemetryUnavailable =
    selectedScope === 'org' &&
    hasTelemetryCoverageData &&
    deviceTotalCount > 0 &&
    activeDeviceCount === 0 &&
    liveRoutingTelemetryCount === 0;
  const routingHealthDisplay = routingHealthKpiDisplay({
    liveTelemetryUnavailable: routingHealthLiveTelemetryUnavailable,
    loading: !orgBgpStatusData && !orgOspfStatusData,
    totalPeers: routingTotalCount,
    downPeers: routingDownCount
  });
  const orgBgpPeerRows = useMemo<OrgBgpPeerRow[]>(() => {
    const latestByPeer = new Map<string, Signal>();
    const seenPeerKeys = new Set<string>();
    for (const row of orgBgpStatusData?.items ?? []) {
      if (!isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
        continue;
      }
      if (!isFreshSignalRow(row, DEVICE_STALE_MINUTES, timeRange.to)) {
        continue;
      }
      const key = routingAdjacencyKey(row);
      if (!key.trim()) {
        continue;
      }
      const previous = latestByPeer.get(key);
      if (!previous || signalTimestampMs(row.observedAt) >= signalTimestampMs(previous.observedAt)) {
        latestByPeer.set(key, row);
      }
    }
    const rows: OrgBgpPeerRow[] = Array.from(latestByPeer.values()).map((row) => {
      const deviceId = signalRowDeviceId(row);
      const labels = stringRecordFromUnknown(row.attributes?.labels);
      const session = bgpSessionParts(row.instance);
      const peer = session.peerAddr || labels.peer || labels.peer_address || labels.bgp_peer || row.targetId || '';
      const local = session.localAddr || labels.local_addr || labels.local || '';
      seenPeerKeys.add(signalRoutingAdjacencyDedupeKey(row, 'BGP'));
      return {
        id: routingAdjacencyKey(row),
        deviceId,
        peer,
        local,
        device: signalDeviceDisplayName(row, orgDeviceNameById),
        state: bgpStateLabel(row.value),
        established: row.value === 6,
        prefixes: bgpPrefixCountFromAttributes(row.attributes),
        observedAt: row.observedAt,
        source: 'snmp' as const
      };
    });
    const topologyRowsByPeer = new Map<string, TopologyRoutingAdjacencyRow>();
    for (const row of routingAdjacencyTopologyRows) {
      if (!isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
        continue;
      }
      if (row.protocol !== 'bgp') {
        continue;
      }
      if (!isFreshSignalRow(row, DEVICE_STALE_MINUTES, timeRange.to)) {
        continue;
      }
      const peerKey = topologyRoutingAdjacencyPeerKey(row);
      if (seenPeerKeys.has(peerKey)) {
        continue;
      }
      const previous = topologyRowsByPeer.get(peerKey);
      if (
        shouldReplaceRoutingHealthSample(
          previous ? { up: previous.up, timestamp: signalTimestampMs(previous.observedAt) } : undefined,
          { up: row.up, timestamp: signalTimestampMs(row.observedAt) }
        )
      ) {
        topologyRowsByPeer.set(peerKey, row);
      }
    }
    for (const row of topologyRowsByPeer.values()) {
      rows.push({
        id: row.id,
        deviceId: row.deviceId,
        peer: row.peer,
        local: '',
        device: signalDeviceDisplayName(row, orgDeviceNameById),
        state: row.state || 'unknown',
        established: row.up,
        prefixes: bgpPrefixCountFromAttributes(row.attributes),
        observedAt: row.observedAt,
        source: 'topology'
      });
    }
    return rows.sort((left, right) => {
      const leftDown = left.established ? 1 : 0;
      const rightDown = right.established ? 1 : 0;
      return leftDown - rightDown || signalTimestampMs(right.observedAt) - signalTimestampMs(left.observedAt) || left.device.localeCompare(right.device);
    });
  }, [deviceInventoryLoaded, inventoryDeviceIds, orgBgpStatusData?.items, orgDeviceNameById, routingAdjacencyTopologyRows, timeRange.to]);
  const orgOspfAdjacencyRows = useMemo<OrgOspfAdjacencyRow[]>(() => {
    return buildOrgOspfAdjacencyRows({
      deviceInventoryLoaded,
      freshnessReferenceTime: timeRange.to,
      inventoryDeviceIds,
      orgDeviceNameById,
      orgOspfStatusItems: orgOspfStatusData?.items ?? [],
      routingAdjacencyTopologyRows
    });
  }, [deviceInventoryLoaded, inventoryDeviceIds, orgDeviceNameById, orgOspfStatusData?.items, routingAdjacencyTopologyRows, timeRange.to]);
  const orgIsisAdjacencyRows = useMemo<OrgOspfAdjacencyRow[]>(() => {
    const grouped = new Map<string, { device: string; instance: string; up: number; total: number; hasDown: boolean }>();
    for (const row of routingAdjacencyTopologyRows) {
      if (!isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
        continue;
      }
      if (row.protocol !== 'isis') {
        continue;
      }
      if (!isFreshSignalRow(row, DEVICE_STALE_MINUTES, timeRange.to)) {
        continue;
      }
      const instance = row.instance || row.area || 'default';
      const key = `${row.deviceId}:${instance}`;
      const existing = grouped.get(key) ?? {
        device: signalDeviceDisplayName(row, orgDeviceNameById),
        instance,
        up: 0,
        total: 0,
        hasDown: false
      };
      existing.total += 1;
      if (row.up) {
        existing.up += 1;
      } else {
        existing.hasDown = true;
      }
      grouped.set(key, existing);
    }
    return Array.from(grouped.entries())
      .map(([id, row]) => ({
        id,
        device: row.device,
        area: row.instance,
        up: row.up,
        total: row.total,
        state: row.hasDown ? 'Degraded' : 'Up'
      }))
      .sort((left, right) => left.up - left.total - (right.up - right.total) || left.device.localeCompare(right.device));
  }, [deviceInventoryLoaded, inventoryDeviceIds, routingAdjacencyTopologyRows, timeRange.to]);
  const orgBfdSessionRows = useMemo<OrgBfdSessionRow[]>(() => {
    const latestBySession = new Map<string, OrgBfdSessionRow & { timestamp: number }>();
    // Topology has one row per peer while BFD telemetry has one row per
    // session. Track the coarse topology identity separately so a topology
    // row enriches missing telemetry but never collapses distinct sessions.
    const telemetryTopologyPeerKeys = new Set<string>();
    for (const row of orgBfdStatusData?.items ?? []) {
      if (!isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
        continue;
      }
      if (!isFreshSignalRow(row, DEVICE_STALE_MINUTES, timeRange.to)) {
        continue;
      }
      const key = signalRoutingAdjacencyDedupeKey(row, 'BFD');
      if (!key.trim()) {
        continue;
      }
      const labels = stringRecordFromUnknown(row.attributes?.labels);
      const deviceId = signalRowDeviceId(row);
      const peer = bfdSessionPeerDisplay(
        labels.peer_addr ||
          labels.peer ||
          labels.peer_address ||
          labels.neighbor ||
          labels.neighbor_ip ||
          labels.remote_ip
      ) || 'Unknown peer';
      const vrf = labels.vrf_name || labels.vrf || labels.instance || 'default';
      if (deviceId && peer !== 'Unknown peer') {
        telemetryTopologyPeerKeys.add(
          topologyRoutingAdjacencyPeerKey({ protocol: 'bfd', deviceId, peer, instance: vrf })
        );
      }
      const value = typeof row.value === 'number' ? row.value : Number.NaN;
      const up = !Number.isNaN(value) && value === 1;
      const next = {
        id: key,
        device: signalDeviceDisplayName(row, orgDeviceNameById),
        peer,
        interfaceName: routingInterfaceLabel(
          labels.interface_name,
          labels.local_interface,
          labels.if_name,
          labels.ifName,
          labels.ifDescr,
          labels.interface
        ),
        sessionType: labels.bfd_session_type || labels.session_type || labels.type || '',
        state: signalValueText(row),
        up,
        observedAt: row.observedAt,
        source: 'snmp' as const,
        timestamp: signalTimestampMs(row.observedAt)
      };
      const previous = latestBySession.get(key);
      if (shouldReplaceRoutingHealthSample(previous, next)) {
        latestBySession.set(key, next);
      }
    }

    for (const row of routingAdjacencyTopologyRows) {
      if (!isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
        continue;
      }
      if (row.protocol !== 'bfd') {
        continue;
      }
      if (!isFreshSignalRow(row, DEVICE_STALE_MINUTES, timeRange.to)) {
        continue;
      }
      const key = topologyRoutingAdjacencyPeerKey(row);
      if (!key.trim()) {
        continue;
      }
      if (telemetryTopologyPeerKeys.has(key)) {
        continue;
      }
      const previous = latestBySession.get(key);
      const timestamp = signalTimestampMs(row.observedAt);
      const shouldReplace = shouldReplaceRoutingHealthSample(previous, { up: row.up, timestamp });
      if (shouldReplace || !previous) {
        const labels = stringRecordFromUnknown(row.attributes?.labels);
        latestBySession.set(key, {
          id: key,
          device: signalDeviceDisplayName(row, orgDeviceNameById),
          peer: bfdSessionPeerDisplay(row.peer) || 'Unknown peer',
          interfaceName: routingInterfaceLabel(labels.local_interface),
          sessionType: '',
          state: row.state || (row.up ? 'up' : 'down'),
          up: row.up,
          observedAt: row.observedAt,
          source: 'topology',
          timestamp
        });
      } else {
        const labels = stringRecordFromUnknown(row.attributes?.labels);
        const interfaceName = routingInterfaceLabel(labels.local_interface);
        if (!previous.interfaceName && interfaceName) {
          previous.interfaceName = interfaceName;
        }
        const enrichedPeer = bfdSessionPeerDisplay(row.peer);
        if ((!previous.peer || previous.peer === 'Unknown peer') && enrichedPeer) {
          previous.peer = enrichedPeer;
        }
      }
    }

    return Array.from(latestBySession.values())
      .sort(
        (left, right) =>
          Number(left.up) - Number(right.up) ||
          right.timestamp - left.timestamp ||
          left.device.localeCompare(right.device) ||
          left.peer.localeCompare(right.peer)
      )
      .map(({ timestamp: _timestamp, ...row }) => row);
  }, [deviceInventoryLoaded, inventoryDeviceIds, orgBfdStatusData?.items, orgDeviceNameById, routingAdjacencyTopologyRows, timeRange.to]);
  const liveBfdTelemetryCount = (orgBfdStatusData?.items ?? []).filter((row) =>
    isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded) && isFreshSignalRow(row, DEVICE_STALE_MINUTES, timeRange.to)
  ).length;
  const bfdSessionLiveTelemetryUnavailable =
    selectedScope === 'org' &&
    hasTelemetryCoverageData &&
    deviceTotalCount > 0 &&
    activeDeviceCount === 0 &&
    liveBfdTelemetryCount === 0;
  const routingAdjacencyTopologyInitialLoading = routingAdjacencyTopologyLoading && !routingAdjacencyTopologyData;
  const orgBfdSessionInitialLoading =
    routingAdjacencyTopologyInitialLoading ||
    (!orgBfdStatusData && !orgBfdStatusBackendMissing && orgBfdSessionRows.length === 0);
  const orgIsisAdjacencyUnavailable = topologyResolveAllFailed(routingAdjacencySiteResolveResult) && orgIsisAdjacencyRows.length === 0;
  const orgIsisAdjacencyPartialUnavailable =
    topologyResolveHasFailures(routingAdjacencySiteResolveResult) && orgIsisAdjacencyRows.length > 0;
  const orgBfdSessionUnavailable =
    orgBfdStatusBackendMissing &&
    !orgDashboardSignalSummaries?.bfd &&
    shouldShowProtocolTopologyUnavailable(routingAdjacencySiteResolveResult, orgBfdSessionRows.length);
  const orgStpLagSummary = useMemo<OrgStpLagSummary>(() => {
    const cliStpRows = orgCliStpTopChangesData?.items ?? [];
    const cliStpDeviceIds = new Set(
      cliStpRows
        .filter((row) =>
          isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded) &&
          isFreshSignalRow(row, DEVICE_STALE_MINUTES, timeRange.to)
        )
        .map(signalRowDeviceId)
        .filter(Boolean)
    );
    let stpRangeAvailable = false;
    let stpRangeTop: { deviceId: string; changes: number } | null = null;
    // Prefer the CLI range series for devices that report CLI STP, and use
    // SNMP range series for the remaining devices in mixed-vendor fleets.
    const rangeSeries = [
      ...(effectiveOrgStpTopChangesRangeData?.series ?? []).map((series) => ({ series, source: 'snmp' as const })),
      ...(effectiveOrgCliStpTopChangesRangeData?.series ?? []).map((series) => ({ series, source: 'cli' as const }))
    ];
    for (const { series, source } of rangeSeries) {
      const labels = series.labels ?? {};
      const deviceId = deviceIdFromMetricLabels(labels);
      if (!deviceId) {
        continue;
      }
      if (source === 'snmp' && cliStpDeviceIds.has(deviceId)) {
        continue;
      }
      if (deviceInventoryLoaded && !inventoryDeviceIds.has(deviceId)) {
        continue;
      }
      const samples = trendSamplesFromMetricSeries(series);
      if (samples.length < 2) {
        continue;
      }
      if (!trendSamplesAreFresh(samples, timeRange)) {
        continue;
      }
      stpRangeAvailable = true;
      const changes = counterIncreaseFromSamples(samples);
      if (!stpRangeTop || changes > stpRangeTop.changes) {
        stpRangeTop = { deviceId, changes };
      }
    }

    const latestMembers = new Map<string, Signal>();
    for (const row of orgLacpSyncData?.items ?? []) {
      if (!isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
        continue;
      }
      if (!isFreshSignalRow(row, DEVICE_STALE_MINUTES, timeRange.to)) {
        continue;
      }
      const key = row.targetId?.trim() || `${signalRowDeviceId(row)}:${row.instance ?? ''}`;
      if (!key.trim()) {
        continue;
      }
      const previous = latestMembers.get(key);
      if (!previous || signalTimestampMs(row.observedAt) >= signalTimestampMs(previous.observedAt)) {
        latestMembers.set(key, row);
      }
    }
    const groups = new Map<string, { label: string; up: number; total: number }>();
    for (const row of latestMembers.values()) {
      const labels = stringRecordFromUnknown(row.attributes?.labels);
      const deviceId = signalRowDeviceId(row);
      const aggregateName = lacpAggregateNameFromLabels(labels);
      const aggregateId = lacpAggregateIdFromLabels(labels);
      const memberName = lacpMemberNameFromRow(row);
      const aggregateKey = aggregateName || aggregateId || memberName;
      const groupKey = `${deviceId || 'unknown-device'}:${aggregateKey}`;
      const deviceLabel = deviceId ? signalDeviceDisplayName(row, orgDeviceNameById) : '';
      const aggregateLabel = aggregateName || aggregateId || memberName || 'Port-channel';
      const label = deviceLabel ? `${aggregateLabel} - ${deviceLabel}` : aggregateLabel;
      const existing = groups.get(groupKey) ?? { label, up: 0, total: 0 };
      existing.total += 1;
      if (row.value === 1) {
        existing.up += 1;
      }
      groups.set(groupKey, existing);
    }
    const stpAggregate = orgDashboardSignalSummaries?.stp;
    const lacpAggregate = orgDashboardSignalSummaries?.lacp;
    if (groups.size === 0 && lacpAggregate && lacpAggregate.total > 0) {
      groups.set('aggregate:lacp', {
        label: 'All LACP members',
        up: Math.max(0, lacpAggregate.equalCount),
        total: Math.max(0, lacpAggregate.total)
      });
    }
    const stpRangeDeviceLabel =
      stpRangeTop && stpRangeTop.changes > 0 ? orgDeviceNameById.get(stpRangeTop.deviceId) ?? stpRangeTop.deviceId : '';
    return {
      stpTopChanges: stpRangeDisplayValue(stpRangeAvailable, stpRangeTop),
      stpTopDevice: stpRangeAvailable ? stpRangeDeviceLabel : '',
      stpTopChangesIsRange: stpRangeAvailable,
      lagRows: Array.from(groups.entries())
        .map(([id, row]) => ({ id, ...row }))
        .sort((left, right) => left.up / Math.max(1, left.total) - right.up / Math.max(1, right.total))
        .slice(0, 8),
      unavailable:
        stpTopChangesRangeUnavailable &&
        orgStpTopChangesBackendMissing &&
        orgLacpSyncBackendMissing &&
        !stpAggregate &&
        !lacpAggregate &&
        orgCliStpTopChangesBackendMissing
    };
  }, [
    deviceInventoryLoaded,
    effectiveOrgStpTopChangesRangeData?.series,
    effectiveOrgCliStpTopChangesRangeData?.series,
    inventoryDeviceIds,
    orgCliStpTopChangesBackendMissing,
    orgCliStpTopChangesData?.items,
    orgDeviceNameById,
    orgLacpSyncBackendMissing,
    orgLacpSyncData?.items,
    orgDashboardSignalSummaries?.lacp,
    orgDashboardSignalSummaries?.stp,
    orgStpTopChangesBackendMissing,
    orgStpTopChangesData?.items,
    stpTopChangesRangeUnavailable,
    timeRange
  ]);

  const orgDeviceHealthRows = useMemo<OrgDeviceHealthRow[]>(() => {
    if (selectedScope === 'org' && orgLatestDeviceIds.length === 0) {
      return [];
    }
    const latestByDevice = (rows: Signal[] | undefined): Map<string, Signal> => {
      const out = new Map<string, Signal>();
      for (const row of rows ?? []) {
        const rawDeviceId = signalRowDeviceId(row);
        if (!rawDeviceId || !isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
          continue;
        }
        const deviceId = canonicalInventoryDeviceId(rawDeviceId, inventoryDeviceCanonicalIds);
        const previous = out.get(deviceId);
        if (!previous || signalTimestampMs(row.observedAt) >= signalTimestampMs(previous.observedAt)) {
          out.set(deviceId, row);
        }
      }
      return out;
    };
    // The summary table is a latest-state view. Keep the most recent sample
    // for each displayed device even when that sample is older than the
    // selected trend window; the table explicitly shows its telemetry age.
    const cpuRows = orgLatestDeviceHealthData?.cpu.items;
    const memRows = orgLatestDeviceHealthData?.mem.items;
    const uptimeRows = orgLatestDeviceHealthData?.uptime.items;
    const cpuByDevice = latestByDevice(cpuRows);
    const memByDevice = latestByDevice(memRows);
    const uptimeByDevice = latestByDevice(uptimeRows);
    const cpuValuesByDevice = new Map<string, number | null>();
    for (const [id, row] of cpuByDevice.entries()) {
      cpuValuesByDevice.set(id, typeof row.value === 'number' ? row.value : null);
    }
    // The compact Top-12 query is the authoritative ranked source for the
    // table. Reuse every ranked value (not just the card's first five).
    for (const series of orgCpuRankedSeries.slice(0, ORG_LATEST_HEALTH_DEVICE_LIMIT)) {
      if (!series.id || cpuValuesByDevice.has(series.id)) {
        continue;
      }
      cpuValuesByDevice.set(series.id, series.summaryValue);
    }
    const memValuesByDevice = new Map<string, number | null>();
    for (const [id, row] of memByDevice.entries()) {
      memValuesByDevice.set(id, typeof row.value === 'number' ? row.value : null);
    }
    const uptimeValuesByDevice = new Map<string, number | null>();
    for (const [id, row] of uptimeByDevice.entries()) {
      uptimeValuesByDevice.set(id, typeof row.value === 'number' ? row.value : null);
    }
    const interfacesByDevice = new Map<string, { up: number; total: number }>();
    const latestInterfaces = new Map<string, Signal>();
    for (const row of orgLatestDeviceHealthData?.interfaces.items ?? []) {
      if (!isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded)) {
        continue;
      }
      const deviceId = canonicalInventoryDeviceId(signalRowDeviceId(row), inventoryDeviceCanonicalIds);
      if (!deviceId) {
        continue;
      }
      const interfaceIdentity = interfaceIdentityFromSignal(row.targetId, row.instance);
      const key = `${deviceId}:${interfaceIdentity}`;
      const previous = latestInterfaces.get(key);
      if (!previous || signalTimestampMs(row.observedAt) >= signalTimestampMs(previous.observedAt)) {
        latestInterfaces.set(key, row);
      }
    }
    for (const row of latestInterfaces.values()) {
      const deviceId = canonicalInventoryDeviceId(signalRowDeviceId(row), inventoryDeviceCanonicalIds);
      if (!deviceId) {
        continue;
      }
      const existing = interfacesByDevice.get(deviceId) ?? { up: 0, total: 0 };
      existing.total += 1;
      if (row.value === 1) {
        existing.up += 1;
      }
      interfacesByDevice.set(deviceId, existing);
    }
    const bgpByDevice = new Map<string, { up: number; total: number }>();
    const healthBgpRows = orgLatestDeviceHealthData?.bgp.items
      ? orgLatestDeviceHealthData.bgp.items.map((row) => ({ deviceId: signalRowDeviceId(row), established: row.value === 6 }))
      : orgBgpPeerRows;
    for (const row of healthBgpRows) {
      const deviceId = canonicalInventoryDeviceId(row.deviceId?.trim() ?? '', inventoryDeviceCanonicalIds);
      if (!deviceId) {
        continue;
      }
      const existing = bgpByDevice.get(deviceId) ?? { up: 0, total: 0 };
      existing.total += 1;
      if (row.established) {
        existing.up += 1;
      }
      bgpByDevice.set(deviceId, existing);
    }
    const lastTelemetryAtByDevice = new Map<string, string>();
    const recordTelemetryTimestamp = (rawDeviceId: string, observedAt?: string) => {
      const deviceId = canonicalInventoryDeviceId(rawDeviceId, inventoryDeviceCanonicalIds);
      if (!deviceId || !observedAt) {
        return;
      }
      const previous = lastTelemetryAtByDevice.get(deviceId);
      if (!previous || signalTimestampMs(observedAt) >= signalTimestampMs(previous)) {
        lastTelemetryAtByDevice.set(deviceId, observedAt);
      }
    };
    for (const rows of [cpuByDevice, memByDevice, uptimeByDevice]) {
      for (const [deviceId, row] of rows.entries()) {
        recordTelemetryTimestamp(deviceId, row.observedAt);
      }
    }
    for (const row of latestInterfaces.values()) {
      recordTelemetryTimestamp(signalRowDeviceId(row), row.observedAt);
    }
    for (const row of orgLatestDeviceHealthData?.bgp.items ?? []) {
      recordTelemetryTimestamp(signalRowDeviceId(row), row.observedAt);
    }
    for (const row of orgBgpPeerRows) {
      recordTelemetryTimestamp(row.deviceId, row.observedAt);
    }

    const inventoryDevices = [
      ...deviceSummaries,
      ...(coverageDeviceCountData?.items ?? [])
    ].filter((device) => orgLatestDeviceCanonicalIds.size === 0 || orgLatestDeviceCanonicalIds.has(canonicalInventoryDeviceId(device.id?.trim() ?? '', inventoryDeviceCanonicalIds)));
    const devicesById = new Map<string, DeviceSummary>();
    for (const device of inventoryDevices) {
      if (device.id?.trim() && !devicesById.has(device.id.trim())) {
        devicesById.set(device.id.trim(), device);
      }
    }
    // If inventory is partial, retain devices for which telemetry provides
    // operational evidence so the summary does not collapse to one row.
    const telemetryDeviceIds = new Set([
      ...cpuByDevice.keys(),
      ...memByDevice.keys(),
      ...uptimeByDevice.keys(),
      ...interfacesByDevice.keys(),
      ...bgpByDevice.keys()
    ]);
    for (const id of telemetryDeviceIds) {
      if (!devicesById.has(id) && (INVENTORY_DEVICE_ID_PATTERN.test(id) || inventoryDeviceCanonicalIds.has(id))) {
        devicesById.set(id, { id, displayName: orgDeviceNameById.get(id) ?? id });
      }
    }
    return buildOrgDeviceHealthRows({
      devices: Array.from(devicesById.values()),
      interfacesByDevice,
      bgpByDevice,
      cpuByDevice: cpuValuesByDevice,
      memByDevice: memValuesByDevice,
      uptimeByDevice: uptimeValuesByDevice,
      orgSiteNameById,
      lastTelemetryAtByDevice,
      telemetryReportingDeviceIds,
      staleMinutes: DEVICE_STALE_MINUTES
    });
  }, [
    deviceInventoryLoaded,
    coverageDeviceCountData?.items,
    deviceSummaries,
    inventoryDeviceIds,
    orgLatestDeviceHealthData,
    inventoryDeviceCanonicalIds,
    orgLatestDeviceCanonicalIds,
    orgLatestDeviceIds,
    orgBgpPeerRows,
    orgCpuRankedSeries,
    orgSiteNameById,
    telemetryReportingDeviceIds,
    selectedScope
  ]);

  useEffect(() => {
    if (selectedDeviceId && !devicesLoading && devicesData && (!deviceNameById.has(selectedDeviceId) || !roleFilteredDeviceIds.has(selectedDeviceId))) {
      setSelectedDeviceId('');
    }
  }, [deviceNameById, devicesData, devicesLoading, roleFilteredDeviceIds, selectedDeviceId]);

  useEffect(() => {
    setCurrentPage(1);
    setAfterCursor('');
    setBeforeCursor('');
  }, [stage, family, selectedScope, selectedSiteId, selectedDeviceId, selectedDeviceRole, selectedKey, inspectionPageLimit, rangePreset]);

  const signalsFetcher = useMemo(() => {
    if (!orgId || selectedScope === 'org') {
      return null;
    }
    if (!selectedSiteId) {
      return null;
    }
    if (selectedScope === 'device' && !selectedDeviceId) {
      return null;
    }
    if (stage === 'signals') {
      if (selectedKey && TOPOLOGY_SIGNAL_KEYS.has(selectedKey)) {
        return null;
      }
      const isSSHSessionsSelection = family === 'device_health' && selectedKey === SSH_SESSIONS_SIGNAL_KEY;
      if (isSSHSessionsSelection) {
        return async () => {
          const query = (signalKey: string, deviceIds?: string[]) =>
            signalsApi.orgsOrgIdSignalsGet(
              {
                orgId,
                scopeType: 'site',
                scopeId: selectedSiteId,
                family,
                deviceId: selectedDeviceId || undefined,
                signalKey,
                deviceIds,
                // The SSH view is intentionally a grouped, client-paginated
                // display. Fetch all raw rows so one device summary can
                // include its totals, limits, and every VRF.
                limit: 5000
              },
              { cache: 'no-store' }
            );
          const [total, globalLimit, perSourceIPLimit, vrfs] = await Promise.all([
            query(SSH_CONNECTIONS_TOTAL_SIGNAL_KEY),
            query(SSH_CONNECTION_LIMIT_SIGNAL_KEY),
            query(SSH_PER_HOST_CONNECTION_LIMIT_SIGNAL_KEY),
            query(SSH_CONNECTIONS_BY_VRF_SIGNAL_KEY)
          ]);
          return {
            items: [...(total.items ?? []), ...(globalLimit.items ?? []), ...(perSourceIPLimit.items ?? []), ...(vrfs.items ?? [])]
          } as SignalListResponse;
        };
      }
      const rawSignalKey = selectedKey && !TOPOLOGY_SIGNAL_KEYS.has(selectedKey) ? selectedKey : undefined;
      return async () => {
        const response = await signalsApi.orgsOrgIdSignalsGet(
          {
            orgId,
            scopeType: 'site',
            scopeId: selectedSiteId,
            family,
            deviceId: selectedDeviceId || undefined,
            signalKey: rawSignalKey,
            limit: inspectionPageLimit,
            after: afterCursor || undefined,
            before: beforeCursor || undefined
          },
          { cache: 'no-store' }
        );
        if (family !== 'device_health') {
          return response;
        }
        const isAllDeviceHealthSignals = rawSignalKey === undefined;
        const sshTotals = isAllDeviceHealthSignals
          ? await signalsApi.orgsOrgIdSignalsGet(
              {
                orgId,
                scopeType: 'site',
                scopeId: selectedSiteId,
                family,
                deviceId: selectedDeviceId || undefined,
                signalKey: SSH_CONNECTIONS_TOTAL_SIGNAL_KEY,
                limit: inspectionPageLimit,
                after: afterCursor || undefined,
                before: beforeCursor || undefined
              },
              { cache: 'no-store' }
            )
          : { items: [] };
        const primaryItems = isAllDeviceHealthSignals
          ? (response.items ?? []).filter(
              (item) =>
                item.signalKey !== SSH_CONNECTIONS_TOTAL_SIGNAL_KEY &&
                item.signalKey !== SSH_CONNECTION_LIMIT_SIGNAL_KEY &&
                item.signalKey !== SSH_CONNECTION_UTILIZATION_SIGNAL_KEY &&
                item.signalKey !== SSH_PER_HOST_CONNECTION_LIMIT_SIGNAL_KEY &&
                item.signalKey !== SSH_CONNECTIONS_BY_VRF_SIGNAL_KEY
            )
          : response.items ?? [];
        return { ...response, items: [...primaryItems, ...(sshTotals.items ?? [])] };
      };
    }
    if (stage === 'features') {
      return () =>
        signalsApi.orgsOrgIdSignalsFeaturesGet(
          {
            orgId,
            scopeType: 'site',
            scopeId: selectedSiteId,
            family,
            deviceId: selectedDeviceId || undefined,
            featureKey: selectedKey || undefined,
            limit: inspectionPageLimit,
            after: afterCursor || undefined,
            before: beforeCursor || undefined
          },
          { cache: 'no-store' }
        );
    }
    return () =>
      signalsApi.orgsOrgIdSignalsConditionsGet(
        {
          orgId,
          scopeType: 'site',
          scopeId: selectedSiteId,
          family,
          deviceId: selectedDeviceId || undefined,
          conditionKey: selectedKey || undefined,
          limit: inspectionPageLimit,
          after: afterCursor || undefined,
          before: beforeCursor || undefined,
          state: conditionState
        },
        { cache: 'no-store' }
      );
  }, [
    afterCursor,
    beforeCursor,
    conditionState,
    family,
    inspectionPageLimit,
    orgId,
    selectedDeviceId,
    selectedKey,
    selectedScope,
    selectedSiteId,
    signalsApi,
    stage
  ]);

  const {
    data: stageData,
    loading: stageLoading,
    error: stageError,
    backendMissing: stageBackendMissing
  } = useApiQuery<SignalListResponse | SignalFeaturesListResponse | SignalConditionsListResponse>(signalsFetcher, [signalsFetcher, apiRefreshNonce]);
  const activeStageQuery = activeQueryStateForFetcher(signalsFetcher, {
    data: stageData,
    loading: stageLoading,
    error: stageError,
    backendMissing: stageBackendMissing
  });
  const activeStageData = activeStageQuery.data;
  const activeStageLoading = activeStageQuery.loading;
  const activeStageError = activeStageQuery.error;
  const activeStageBackendMissing = activeStageQuery.backendMissing;
  const canUseTopologyFallback = canUseTopologyFallbackForQuery({
    stage,
    loading: activeStageLoading,
    error: activeStageError,
    backendMissing: activeStageBackendMissing
  });

  const allRows = useMemo<InspectorRow[]>(() => {
    if (stage === 'signals') {
      const items = (activeStageData as SignalListResponse | null)?.items ?? [];
      const selectedKeyIsTopologyOnly = selectedKey ? TOPOLOGY_SIGNAL_KEYS.has(selectedKey) : false;
      const isSSHSessionsSelection = family === 'device_health' && selectedKey === SSH_SESSIONS_SIGNAL_KEY;
      if (isSSHSessionsSelection) {
        return sshSummaryRows(items, deviceNameById, family).sort((left, right) => left.deviceName.localeCompare(right.deviceName));
      }
      const signalRows = selectedKeyIsTopologyOnly ? [] : items
        .filter(
          (item: Signal) =>
            item.signalKey !== SSH_CONNECTION_LIMIT_SIGNAL_KEY &&
            item.signalKey !== SSH_CONNECTION_UTILIZATION_SIGNAL_KEY &&
            item.signalKey !== SSH_PER_HOST_CONNECTION_LIMIT_SIGNAL_KEY &&
            item.signalKey !== SSH_CONNECTIONS_BY_VRF_SIGNAL_KEY
        )
        .map((item: Signal) => {
        const signalKey = item.signalKey ?? '';
        return {
          id: `${signalKey || 'signal'}:${item.targetId ?? ''}:${item.instance ?? ''}:${item.observedAt ?? ''}`,
          stage,
          family: item.family ?? family,
          key: signalKey,
          metricName: item.metricName ?? signalKey,
          valueText: signalValueText(item),
          numericValue: item.value,
          timestamp: item.observedAt,
          deviceId: item.deviceId ?? '',
          deviceName: deviceNameById.get(item.deviceId ?? '') ?? item.deviceId ?? '',
          targetId: item.targetId ?? '',
          targetType: item.targetType ?? '',
          instance: item.instance ?? '',
          attributes: item.attributes
        };
        });
      const includeTopologyBgpRows = shouldIncludeTopologySignalRowsForQuery({
        family,
        selectedKey,
        topologyKey: 'routing_bgp_peer_state',
        signalRowCount: signalRows.length,
        canUseTopologyFallback
      });
      const includeTopologyOspfRows = shouldIncludeTopologySignalRowsForQuery({
        family,
        selectedKey,
        topologyKey: 'routing_ospf_neighbor_state',
        signalRowCount: signalRows.length,
        canUseTopologyFallback
      });
      const includeTopologyIsisRows = shouldIncludeTopologySignalRowsForQuery({
        family,
        selectedKey,
        topologyKey: 'routing_isis_adjacency_state',
        signalRowCount: signalRows.length,
        canUseTopologyFallback
      });
      const includeTopologyBfdRows = shouldIncludeTopologySignalRowsForQuery({
        family,
        selectedKey,
        topologyKey: 'routing_bfd_session_state',
        signalRowCount: signalRows.length,
        canUseTopologyFallback
      });
      const includeTopologyLagRows = shouldIncludeTopologySignalRowsForQuery({
        family,
        selectedKey,
        topologyKey: 'topology_lag_member',
        signalRowCount: signalRows.length,
        canUseTopologyFallback
      });
      const includeTopologyTunnelRows = shouldIncludeTopologySignalRowsForQuery({
        family,
        selectedKey,
        topologyKey: 'topology_tunnel_state',
        signalRowCount: signalRows.length,
        canUseTopologyFallback
      });
      const includeTopologyVlanRows = shouldIncludeTopologySignalRowsForQuery({
        family,
        selectedKey,
        topologyKey: 'topology_vlan_membership',
        signalRowCount: signalRows.length,
        canUseTopologyFallback
      });
      if (
        !includeTopologyBgpRows &&
        !includeTopologyOspfRows &&
        !includeTopologyIsisRows &&
        !includeTopologyBfdRows &&
        !includeTopologyLagRows &&
        !includeTopologyTunnelRows &&
        !includeTopologyVlanRows
      ) {
        return signalRows;
      }
      const snmpBgpPeerKeys = new Set(
        items
          .filter(
            (row) =>
              row.signalKey === 'snmp_bgp_peer_state' &&
              isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded) &&
              isFreshSignalRow(row, DEVICE_STALE_MINUTES, timeRange.to)
          )
          .map((row) => signalRoutingAdjacencyDedupeKey(row, 'BGP'))
          .filter((key) => key.trim().length > 0)
      );
      const routingTopologyRows = freshSignalRows(routingAdjacencyTopologyRows, DEVICE_STALE_MINUTES, timeRange.to)
        .filter((row) => isCurrentInventorySignalRow(row, inventoryDeviceIds, deviceInventoryLoaded))
        .filter(
          (row) =>
            (row.protocol === 'bgp' && includeTopologyBgpRows) ||
            (row.protocol === 'ospf' && includeTopologyOspfRows) ||
            (row.protocol === 'isis' && includeTopologyIsisRows) ||
            (row.protocol === 'bfd' && includeTopologyBfdRows)
        )
        .filter((row) => row.protocol !== 'bgp' || !snmpBgpPeerKeys.has(topologyRoutingAdjacencyPeerKey(row)))
        .map((row): InspectorRow => {
          const signalKey =
            row.protocol === 'bgp'
              ? 'routing_bgp_peer_state'
              : row.protocol === 'isis'
                ? 'routing_isis_adjacency_state'
                : row.protocol === 'bfd'
                  ? 'routing_bfd_session_state'
                  : 'routing_ospf_neighbor_state';
          return {
            id: `topology:${row.id}:${row.observedAt ?? ''}`,
            stage,
            family: row.protocol === 'bgp' ? 'bgp' : row.protocol === 'bfd' ? 'bfd' : 'routing',
            key: signalKey,
            metricName: signalKey,
            valueText: row.state || (row.up ? 'up' : 'down'),
            numericValue: undefined,
            timestamp: row.observedAt,
            deviceId: row.deviceId,
            deviceName: row.deviceName,
            targetId: row.targetId,
            targetType: 'routing_adjacency',
            instance: row.instance,
            attributes: row.attributes
          };
        });
      // Topology snapshots are structural context, not live telemetry samples.
      // Do not discard valid membership/tunnel rows using the 15-minute raw
      // telemetry freshness window; the Observed column still exposes age.
      const lagTopologyRows = includeTopologyLagRows
        ? topologyContextRowsForInspection(lagMembershipTopologyRows).map((row): InspectorRow => ({
            id: `topology:${row.id}:${row.observedAt ?? ''}`,
            stage,
            family,
            key: 'topology_lag_member',
            metricName: 'topology_lag_member',
            valueText: `member of ${row.lagName}`,
            numericValue: undefined,
            timestamp: row.observedAt,
            deviceId: row.deviceId,
            deviceName: row.deviceName,
            targetId: row.targetId,
            targetType: 'interface',
            instance: row.memberName,
            attributes: row.attributes
          }))
        : [];
      const tunnelTopologyRows = includeTopologyTunnelRows
        ? topologyContextRowsForInspection(vpnConnectionTopologyRows).map((row): InspectorRow => ({
            id: `topology:${row.id}:${row.observedAt ?? ''}`,
            stage,
            family: 'tunnel',
            key: 'topology_tunnel_state',
            metricName: 'topology_tunnel_state',
            valueText: row.state || (row.up ? 'up' : 'down'),
            numericValue: undefined,
            timestamp: row.observedAt,
            deviceId: row.deviceId,
            deviceName: row.deviceName,
            targetId: row.targetId,
            targetType: 'tunnel',
            instance: row.tunnelName,
            attributes: row.attributes
          }))
        : [];
      const vlanTopologyRows = includeTopologyVlanRows
        ? freshSignalRows(vlanMembershipTopologyRows, DEVICE_STALE_MINUTES, timeRange.to).map((row): InspectorRow => ({
            id: `topology:${row.id}:${row.observedAt ?? ''}`,
            stage,
            family: 'vtp',
            key: 'topology_vlan_membership',
            metricName: 'topology_vlan_membership',
            valueText: row.vlanName ? `VLAN ${row.vlanId} (${row.vlanName})` : `VLAN ${row.vlanId}`,
            numericValue: numberFromUnknown(row.vlanId) ?? undefined,
            timestamp: row.observedAt,
            deviceId: row.deviceId,
            deviceName: row.deviceName,
            targetId: row.targetId,
            targetType: 'interface',
            instance: `${row.interfaceName} / VLAN ${row.vlanId}`,
            attributes: row.attributes
          }))
        : [];
      return [...signalRows, ...routingTopologyRows, ...lagTopologyRows, ...tunnelTopologyRows, ...vlanTopologyRows];
    }
    if (stage === 'features') {
      const items = (activeStageData as SignalFeaturesListResponse | null)?.items ?? [];
      return items.map((item: SignalFeature) => ({
        id: `${item.featureKey ?? 'feature'}:${item.targetId ?? ''}:${item.instance ?? ''}:${item.updatedAt ?? ''}`,
        stage,
        family: item.family ?? family,
        key: item.featureKey ?? '',
        metricName: featureMetricNameForKey(item.featureKey ?? ''),
        valueText: valueTextFromNumber(item.value),
        numericValue: item.value,
        timestamp: item.updatedAt,
        deviceId: item.deviceId ?? '',
        deviceName: deviceNameById.get(item.deviceId ?? '') ?? item.deviceId ?? '',
        targetId: item.targetId ?? '',
        targetType: item.targetType ?? '',
        instance: item.instance ?? '',
        attributes: item.attributes
      }));
    }
    const items = (activeStageData as SignalConditionsListResponse | null)?.items ?? [];
    return items.map((item: SignalCondition) => ({
      id: `${item.conditionKey ?? 'condition'}:${item.targetId ?? ''}:${item.instance ?? ''}:${item.updatedAt ?? ''}`,
      stage,
      family: item.family ?? family,
      key: item.conditionKey ?? '',
      valueText: item.state ?? 'unknown',
      state: item.state,
      timestamp: item.updatedAt,
      deviceId: item.deviceId ?? '',
      deviceName: deviceNameById.get(item.deviceId ?? '') ?? item.deviceId ?? '',
      targetId: item.targetId ?? '',
      targetType: item.targetType ?? '',
      instance: item.instance ?? '',
      attributes: item.attributes
    }));
  }, [
    deviceInventoryLoaded,
    deviceNameById,
    family,
    lagMembershipTopologyRows,
    routingAdjacencyTopologyRows,
    inventoryDeviceIds,
    selectedKey,
    stage,
    activeStageData,
    canUseTopologyFallback,
    timeRange.to,
    vlanMembershipTopologyRows,
    vpnConnectionTopologyRows
  ]);

  const keyOptions = useMemo(() => {
    const pageKeys = allRows
      .map((row) => row.key)
      .filter(
        (key): key is string =>
          Boolean(key) &&
          key !== SSH_CONNECTIONS_TOTAL_SIGNAL_KEY &&
          key !== SSH_CONNECTION_LIMIT_SIGNAL_KEY &&
          key !== SSH_CONNECTION_UTILIZATION_SIGNAL_KEY &&
          key !== SSH_PER_HOST_CONNECTION_LIMIT_SIGNAL_KEY &&
          key !== SSH_CONNECTIONS_BY_VRF_SIGNAL_KEY
      );
    const catalogKeys =
      stage === 'signals'
        ? family === 'all'
          ? []
          : FAMILY_SIGNAL_KEYS[family]
        : stage === 'features'
          ? family === 'all'
            ? []
            : FAMILY_FEATURE_KEYS[family]
          : family === 'all'
            ? Array.from(new Set(Object.values(FAMILY_CONDITION_KEYS).flat()))
            : FAMILY_CONDITION_KEYS[family];
    return Array.from(new Set([...catalogKeys, ...pageKeys])).sort((left, right) => labelForKey(left).localeCompare(labelForKey(right)));
  }, [allRows, family, stage]);

  useEffect(() => {
    if (selectedKey && !keyOptions.includes(selectedKey)) {
      setSelectedKey('');
    }
  }, [keyOptions, selectedKey]);

  const filteredRows = useMemo(() => {
    if (!hasDeviceRoleFilter) {
      return allRows;
    }
    return allRows.filter((row) => {
      const candidates = [row.deviceId, row.deviceName, deviceIdFromTargetId(row.targetId)];
      return candidates.some((candidate) => Boolean(candidate && roleFilteredDeviceIdentifiers.has(candidate.trim())));
    });
  }, [allRows, hasDeviceRoleFilter, roleFilteredDeviceIdentifiers]);
  const usesBfdTopologyFallback =
    canUseTopologyFallback &&
    shouldUseBfdTopologyFallback(family, selectedKey, ((activeStageData as SignalListResponse | null)?.items ?? []).length);
  const usesBgpTopologyFallback =
    canUseTopologyFallback &&
    shouldUseBgpTopologyFallback(family, selectedKey, ((activeStageData as SignalListResponse | null)?.items ?? []).length);
  const usesRoutingTopologyFallback =
    canUseTopologyFallback &&
    shouldUseRoutingTopologyFallback(family, selectedKey, ((activeStageData as SignalListResponse | null)?.items ?? []).length);
  const usesTunnelTopologyFallback =
    canUseTopologyFallback &&
    shouldUseTunnelTopologyFallback(family, selectedKey, ((activeStageData as SignalListResponse | null)?.items ?? []).length);
  const usesVlanTopologyFallback =
    canUseTopologyFallback &&
    shouldUseVlanTopologyFallback(family, selectedKey, ((activeStageData as SignalListResponse | null)?.items ?? []).length);
  const usesLagTopologyFallback =
    canUseTopologyFallback &&
    shouldUseLagTopologyFallback(family, selectedKey, ((activeStageData as SignalListResponse | null)?.items ?? []).length);
  const inspectionPageMeta =
    stage === 'signals' &&
    (
      (selectedKey && TOPOLOGY_SIGNAL_KEYS.has(selectedKey)) ||
      usesBfdTopologyFallback ||
      usesBgpTopologyFallback ||
      usesRoutingTopologyFallback ||
      usesTunnelTopologyFallback ||
      usesVlanTopologyFallback ||
      usesLagTopologyFallback
    )
      ? null
      : (activeStageData as SignalListResponse | SignalFeaturesListResponse | SignalConditionsListResponse | null)?.meta ?? null;

  const showTargetColumn = useMemo(() => shouldShowTargetColumnForContext(stage, family), [family, stage]);
  const isSSHSessionTable = stage === 'signals' && family === 'device_health' && selectedKey === SSH_SESSIONS_SIGNAL_KEY;
  const showInstanceColumn = useMemo(
    () =>
      !isSSHSessionTable && shouldShowInstanceColumnForContext(stage, family),
    [family, isSSHSessionTable, stage]
  );
  const showBgpPeerColumns = showInstanceColumn && family === 'bgp';

  const chartContextParts = useMemo(() => {
    if (!chartDrawer) {
      return [];
    }
    const row = chartDrawer.row;
    const parts = [`Device: ${row.deviceName || row.deviceId || 'Unknown device'}`];
    if (showTargetColumn && isInterfaceScopedRow(row)) {
      parts.push(`${targetColumnLabelForFamily(family)}: ${friendlyTargetLabel(row)}`);
    }
    if (showBgpPeerColumns) {
      const { peerAddr, localAddr } = bgpSessionParts(row.instance);
      if (peerAddr) {
        parts.push(`Peer: ${peerAddr}`);
      }
      if (localAddr) {
        parts.push(`Local: ${localAddr}`);
      }
    } else {
      const instanceDisplay = inspectorInstanceDisplay(row);
      if (showInstanceColumn && instanceDisplay) {
        parts.push(`${instanceColumnLabelForFamily(family)}: ${instanceDisplay}`);
      }
    }
    return parts;
  }, [chartDrawer, family, showBgpPeerColumns, showInstanceColumn, showTargetColumn]);

  const templatesFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    return () => dashboardsApi.orgsOrgIdDashboardsTemplatesGet({ orgId }, { cache: 'no-store' });
  }, [dashboardsApi, orgId]);
  const { data: templatesData, backendMissing: templatesBackendMissing } = useApiQuery<DashboardsTemplatesResponse>(templatesFetcher, [templatesFetcher]);

  const templateByKey = useMemo(() => {
    const out = new Map<string, GrafanaTemplateSpec>();
    for (const item of templatesData?.grafanaCatalog ?? []) {
      if (typeof item.templateKey !== 'string' || typeof item.dashUid !== 'string' || typeof item.panelId !== 'number' || !Array.isArray(item.vars)) {
        continue;
      }
      out.set(item.templateKey, {
        templateKey: item.templateKey,
        dashUid: item.dashUid,
        panelId: item.panelId,
        vars: item.vars
      });
    }
    return out;
  }, [templatesData?.grafanaCatalog]);

  const matchingTargetIds = useMemo(() => {
    return Array.from(new Set(filteredRows.map((row) => row.targetId).filter(Boolean)));
  }, [filteredRows]);

  const siteCockpitFetcher = useMemo(() => {
    if (!orgId || selectedScope === 'org' || !selectedSiteId) {
      return null;
    }
    return (): Promise<DashboardsRecommendedResponse> =>
      dashboardsApi.orgsOrgIdDashboardsRecommendedGet({
        orgId,
        pageKey: 'site.cockpit',
        scopeType: 'site',
        scopeId: selectedSiteId,
        from: timeRange.from,
        to: timeRange.to
      });
  }, [dashboardsApi, orgId, selectedScope, selectedSiteId, timeRange.from, timeRange.to]);
  const { data: siteCockpitData, error: siteCockpitError, backendMissing: siteCockpitBackendMissing } = useApiQuery<DashboardsRecommendedResponse>(
    siteCockpitFetcher,
    [siteCockpitFetcher, apiRefreshNonce]
  );

  const hasConcreteTargetSelection = !hasDeviceRoleFilter && matchingTargetIds.length === 1;

  const selectedTargetId = useMemo(() => {
    if (hasDeviceRoleFilter && !selectedDeviceId) {
      return undefined;
    }
    if (matchingTargetIds.length === 1) {
      return matchingTargetIds[0];
    }
    if (selectedDeviceId) {
      return `ref:inventory.device:${selectedDeviceId}`;
    }
    return undefined;
  }, [hasDeviceRoleFilter, matchingTargetIds, selectedDeviceId]);

  const selectedDeviceBinding = useMemo(() => {
    if (selectedDeviceId) {
      return selectedDeviceId;
    }
    if (selectedRoleDeviceBinding) {
      return selectedRoleDeviceBinding;
    }
    return selectedTargetId ? deviceIdFromTargetId(selectedTargetId) : undefined;
  }, [selectedDeviceId, selectedRoleDeviceBinding, selectedTargetId]);

  const dashboardWidgets = useMemo<DashboardWidgetSpec[]>(() => {
    if (selectedScope === 'org' || !selectedSiteId) {
      return [];
    }
    return (FAMILY_PANEL_KEYS[family] ?? [])
      .filter((templateKey) => {
        if (family === 'interface' && TARGET_SCOPED_INTERFACE_TEMPLATE_KEYS.has(templateKey)) {
          return hasConcreteTargetSelection;
        }
        if (family === 'redundancy' && TARGET_SCOPED_REDUNDANCY_TEMPLATE_KEYS.has(templateKey)) {
          return hasConcreteTargetSelection;
        }
        return true;
      })
      .map((templateKey) => templateByKey.get(templateKey))
      .filter((item): item is GrafanaTemplateSpec => Boolean(item))
      .map((item) => {
        const title = titleFromTemplateKey(item.templateKey);
        return widgetForTemplate({
          item,
          id: `signals-inspector-${stage}-${item.templateKey}`,
          title,
          varBindings: {
            [SITE_VAR]: selectedSiteId,
            [TARGET_VAR]: selectedTargetId ?? '.*',
            [DEVICE_VAR]: selectedDeviceBinding,
            [IF_NAME_VAR]: selectedTargetId ? interfaceNameFromTargetId(selectedTargetId) : undefined
          },
          timeRange,
          dashboardsMockEnabled
        });
      });
  }, [dashboardsMockEnabled, family, hasConcreteTargetSelection, selectedDeviceBinding, selectedSiteId, selectedTargetId, stage, templateByKey, timeRange]);

  const siteCockpitDeviceBinding = selectedDeviceId || selectedRoleDeviceBinding;
  const siteCockpitWidgets = useMemo(
    () =>
      ((siteCockpitData?.widgets ?? []).filter((widget) => widget.type === 'grafana_panel_iframe') as DashboardWidgetSpec[]).map((widget) =>
        withGrafanaVarBindings(widget, { [DEVICE_VAR]: siteCockpitDeviceBinding })
      ),
    [siteCockpitData?.widgets, siteCockpitDeviceBinding]
  );
  const pipelineHealthWidgets = useMemo(
    () =>
      buildCatalogWidgets({
        keys: PIPELINE_HEALTH_TEMPLATE_KEYS,
        prefix: `${selectedScope}-pipeline-health`,
        templateByKey,
        varBindings: {
          [SITE_VAR]: selectedSiteId || undefined,
          [DEVICE_VAR]: selectedDeviceId || undefined
        },
        timeRange,
        dashboardsMockEnabled
      }),
    [dashboardsMockEnabled, selectedDeviceId, selectedScope, selectedSiteId, templateByKey, timeRange]
  );
  const deviceHealthExtraWidgets = useMemo(
    () =>
      buildCatalogWidgets({
        keys: DEVICE_HEALTH_EXTRA_TEMPLATE_KEYS,
        prefix: 'device-health-extra',
        templateByKey,
        varBindings: {
          [SITE_VAR]: selectedSiteId || undefined,
          [TARGET_VAR]: selectedTargetId,
          [DEVICE_VAR]: selectedDeviceBinding
        },
        timeRange,
        dashboardsMockEnabled
      }),
    [dashboardsMockEnabled, selectedDeviceBinding, selectedSiteId, selectedTargetId, templateByKey, timeRange]
  );

  const familyOptions = useMemo(() => {
    if (stage === 'signals') {
      return FAMILY_OPTIONS.filter((option) => option.id !== 'all' && FAMILY_SIGNAL_KEYS[option.id].length > 0);
    }
    if (stage === 'features') {
      return FAMILY_OPTIONS.filter((option) => option.id !== 'all' && FAMILY_FEATURE_KEYS[option.id].length > 0);
    }
    return FAMILY_OPTIONS.filter((option) => option.id === 'all' || FAMILY_CONDITION_KEYS[option.id].length > 0);
  }, [stage]);

  useEffect(() => {
    if (!grafanaRouteMatch) {
      return;
    }
    const liveSearchParams = new URLSearchParams(searchParams);
    const next = buildGrafanaExplorerSearchParams(liveSearchParams, {
      selectedScope,
      stage,
      family,
      selectedSiteId,
      selectedDeviceId,
      selectedDeviceRole,
      selectedKey,
      conditionState
    });
    if (liveSearchParams.toString() !== next.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [conditionState, family, grafanaRouteMatch, searchParams, selectedDeviceId, selectedDeviceRole, selectedKey, selectedScope, selectedSiteId, setSearchParams, stage]);

  const chartDrawerTitle = chartDrawer ? labelForKey(chartDrawer.row.key) : 'Chart';

  const openChartDrawer = useCallback(
    (row: InspectorRow) => {
      if (!selectedSiteId) {
        return;
      }
      const plan = chartPlanForRow(row);
      if (!plan) {
        return;
      }
      const buildWidget = (spec: RowChartSpec, index: number): DashboardWidgetSpec | null => {
        const template = templateByKey.get(spec.templateKey);
        if (!template) {
          return null;
        }
        return widgetForTemplate({
          item: template,
          id: `row-chart-${row.id}-${index}-${spec.templateKey}`,
          title: labelForKey(row.key),
          varBindings: varBindingsForRow(row, selectedSiteId, spec.bindingMode),
          timeRange,
          dashboardsMockEnabled
        });
      };

      const primary = buildWidget(plan.primary, 0);
      setChartDrawer({ row, primary });
    },
    [dashboardsMockEnabled, selectedSiteId, templateByKey, timeRange]
  );

  const refreshGrafanaEmbedSession = useCallback(() => {
    if (!orgId) {
      return Promise.resolve();
    }
    return dashboardsApi.orgsOrgIdGrafanaEmbedSessionPost({ orgId }, { credentials: 'include' }).then(() => undefined);
  }, [dashboardsApi, orgId]);

  const grafanaWidgetCount =
    dashboardWidgets.length +
    siteCockpitWidgets.length +
    pipelineHealthWidgets.length +
    deviceHealthExtraWidgets.length;

  useGrafanaEmbedSessionKeepalive({
    dashboardsApi,
    orgId,
    enabled: Boolean(orgId) && grafanaWidgetCount > 0,
    logTag: 'telemetry-inspector'
  });

  const { rowCount, displayRows, hasPrevPage, hasNextPage, totalPages, pageStart, pageEnd } = useMemo(
    () =>
      computeInspectionPagination({
        filteredRows,
        currentPage,
        inspectionPageLimit,
        pageMeta: inspectionPageMeta
      }),
    [currentPage, filteredRows, inspectionPageLimit, inspectionPageMeta]
  );
  const hasDeviceContext = selectedScope !== 'org' && Boolean(selectedDeviceId);
  const scopeDescription =
    selectedScope === 'org'
      ? 'Inspect organization-wide telemetry health, active conditions, and recommended dashboards.'
      : hasDeviceContext
        ? 'Inspect device-scoped signals, derived features, active conditions, and related dashboards.'
        : 'Inspect site-scoped signals, derived features, active conditions, and related dashboards.';
  const scopeLabel = DASHBOARD_SCOPE_OPTIONS.find((option) => option.id === selectedScope)?.label ?? 'Site';
  const showScopeDashboards = selectedScope !== 'org' && selectedSiteId;
  const relatedDashboardsDescription =
    hasDeviceContext
      ? 'Curated Grafana panels for the selected device and category, including core device-health metrics such as CPU, memory, and uptime when catalog templates are available.'
      : 'Curated Grafana panels for the selected category. These stay site-scoped by default and become target-scoped when the filters narrow to one relevant target.';
  const signalSummaryScopeId = (hasDeviceContext ? selectedDeviceId : selectedScope === 'site' ? selectedSiteId : orgId) ?? '';
  const orgInterfaceErrorRateState = interfaceErrorRateCoverageState({
    value: topInterfaceErrorRateDisplay.value,
    observed: orgInterfaceErrorCoverage.observed,
    expected: orgInterfaceErrorCoverage.expected,
    failed: orgInterfaceErrorCoverage.failed
  });

  if (!orgId) {
    return (
      <OrgModuleLayout
        title="Telemetry Explorer"
        description="Inspect raw signals, derived features, and active conditions for your organization."
        topSlot={<OrgModulePlaceholder intent="Pick an organization to inspect telemetry." />}
      >
        <OrgModulePlaceholder intent="Telemetry inspection appears here once an organization is active." />
      </OrgModuleLayout>
    );
  }

  return (
    <OrgModuleLayout title="Telemetry Explorer" description={scopeDescription}>
      <div className="org-cockpit__layout">
        {selectedScope !== 'org' ? (
          <div className="telemetry-inspector__stage-bar">
            {STAGE_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={stage === option.id ? 'telemetry-inspector__stage-button telemetry-inspector__stage-button--active' : 'telemetry-inspector__stage-button'}
                onClick={() => setStage(option.id)}
              >
                <strong>{option.label}</strong>
                <span>{option.description}</span>
              </button>
            ))}
          </div>
        ) : null}

        <div className="org-cockpit__toolbar">
          <label>
            Scope
            <select
              value={selectedScope}
              onChange={(event) => {
                const nextScope = event.target.value as DashboardScope;
                setSelectedScope(nextScope);
                setSelectedDeviceId('');
                if (nextScope === 'org') {
                  setSelectedSiteId('');
                  setSelectedDeviceRole('all');
                  setSelectedKey('');
                }
              }}
            >
              {DASHBOARD_SCOPE_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            Category
            <select value={selectedScope === 'org' ? 'all' : family} onChange={(event) => setFamily(event.target.value as SignalFamily)} disabled={selectedScope === 'org'}>
              {selectedScope === 'org' ? (
                <option value="all">All telemetry</option>
              ) : (
                familyOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))
              )}
            </select>
          </label>

          <label>
            Site
            <select
              value={selectedScope === 'org' ? '' : selectedSiteId}
              onChange={(event) => {
                setSelectedSiteId(event.target.value);
                setSelectedDeviceId('');
              }}
              disabled={selectedScope === 'org' || sitesLoading}
            >
              {selectedScope === 'org' ? <option value="">All sites</option> : null}
              {sites.length === 0 ? <option value="">No sites</option> : null}
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Role/Layer
            <select
              value={selectedScope === 'org' ? 'all' : selectedDeviceRole}
              onChange={(event) => {
                setSelectedDeviceRole(event.target.value);
                setSelectedDeviceId('');
              }}
              disabled={selectedScope === 'org' || !selectedSiteId || devicesLoading}
            >
              <option value="all">All roles</option>
              {siteDeviceRoleOptions.map((role) => (
                <option key={role} value={role}>
                  {formatDeviceRoleLabel(role)}
                </option>
              ))}
            </select>
          </label>

          <label>
            Device
            <select
              value={selectedDeviceId}
              onChange={(event) => setSelectedDeviceId(event.target.value)}
              disabled={selectedScope === 'org' || !selectedSiteId || devicesLoading}
            >
              <option value="">{hasDeviceRoleFilter ? `All ${formatDeviceRoleLabel(selectedDeviceRole)} devices` : 'All devices'}</option>
              {roleFilteredDeviceOptions.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            {stage === 'signals' ? 'Signal Key' : stage === 'features' ? 'Feature Key' : 'Condition Key'}
            <select
              value={selectedKey}
              onChange={(event) => setSelectedKey(event.target.value)}
              disabled={selectedScope === 'org' || !selectedSiteId || activeStageLoading}
            >
              <option value="">All</option>
              {keyOptions.map((key) => (
                <option key={key} value={key} title={key}>
                  {labelForKey(key)}
                </option>
              ))}
            </select>
          </label>

          {stage === 'conditions' && selectedScope !== 'org' ? (
            <label>
              State
              <select value={conditionState} onChange={(event) => setConditionState(event.target.value as ConditionStateFilter)}>
                {CONDITION_STATE_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {selectedScope === 'org' ? (
            <label>
              Time range
              <select value={rangePreset} onChange={(event) => setRangePreset(event.target.value as TimeRangePreset)}>
                <option value="15m">Last 15m</option>
                <option value="1h">Last 1h</option>
                <option value="6h">Last 6h</option>
                <option value="24h">Last 24h</option>
              </select>
            </label>
          ) : null}

          <label>
            Refresh
            <select value={refreshSeconds} onChange={(event) => setRefreshSeconds(parseInt(event.target.value, 10) || 0)}>
              {GRAFANA_REFRESH_OPTIONS_SECONDS.map((seconds) => (
                <option key={seconds} value={seconds}>
                  {seconds === 0 ? 'Off' : `${seconds}s`}
                </option>
              ))}
            </select>
          </label>
        </div>

        {sitesError ? <p className="org-shell__banner">{sitesError.message}</p> : null}
        {selectedScope !== 'org' && activeStageError ? <p className="org-shell__banner">{activeStageError.message}</p> : null}
        {selectedScope !== 'org' && siteCockpitError ? <p className="org-shell__banner">{siteCockpitError.message}</p> : null}
        {shouldShowDeviceSummaryError ? <p className="org-shell__banner">{deviceSummaryError?.message}</p> : null}
        {selectedScope === 'org' && connectorSummaryError ? <p className="org-shell__banner">{connectorSummaryError.message}</p> : null}
        {selectedScope === 'org' && reportingCoverageWarning ? <p className="org-shell__banner">{reportingCoverageWarning}</p> : null}
        {selectedScope === 'org' && orgCpuRangeError && !orgCpuRangeBackendMissing ? <p className="org-shell__banner">Organization CPU Top‑5 query failed: {orgCpuRangeError.message}</p> : null}
        {selectedScope === 'org' && shouldShowCpuInstantFallbackError(orgCpuRangeHasSamples, orgCpuTopInstantError, orgCpuTopInstantBackendMissing) ? (
          <p className="org-shell__banner">Organization CPU current-value fallback failed: {orgCpuTopInstantError?.message}</p>
        ) : null}
        {selectedScope === 'org' && orgInterfaceErrorRateRangeError && !orgInterfaceErrorRateRangeBackendMissing ? <p className="org-shell__banner">{orgInterfaceErrorRateRangeError.message}</p> : null}
        {selectedScope === 'org' && orgBgpStatusError && !orgBgpStatusBackendMissing ? <p className="org-shell__banner">{orgBgpStatusError.message}</p> : null}
        {selectedScope === 'org' && orgOspfStatusError && !orgOspfStatusBackendMissing ? <p className="org-shell__banner">{orgOspfStatusError.message}</p> : null}
        {selectedScope === 'org' && orgStpTopChangesError && !orgStpTopChangesBackendMissing ? <p className="org-shell__banner">{orgStpTopChangesError.message}</p> : null}
        {selectedScope === 'org' && orgLacpSyncError && !orgLacpSyncBackendMissing ? <p className="org-shell__banner">{orgLacpSyncError.message}</p> : null}

        {selectedScope === 'org' ? (
          <>
            <div className="telemetry-inspector__summary kpi-grid">
              <KpiCard
                label="Reporting coverage"
                value={reportingCoverageLabel}
                secondary={reportingCoverageSecondary}
                trend={
                  <ReportingCoverageTrend
                    staleDeviceCount={staleDeviceCount}
                    onlineConnectorCount={onlineConnectorCount}
                    staleConnectorCount={staleConnectorCount}
                    connectorTotalCount={connectorTotalCount}
                  />
                }
                accent={staleDeviceCount > 0 || staleConnectorCount > 0 ? 'warning' : 'success'}
              />
              <InterfaceStatusCard
                counts={orgInterfaceCountsDisplay}
                loading={orgInterfaceStatusLoading}
                error={Boolean(orgInterfaceStatusError && !orgInterfaceStatusBackendUnavailable)}
                backendMissing={orgInterfaceStatusBackendUnavailable}
                freshnessReferenceTime={timeRange.to}
              />
              <CpuTopFiveCard
                fleetCpu={orgFleetCpuDisplay}
                loading={
                  !effectiveOrgCpuRangeData &&
                  !orgCpuTopInstantData &&
                  (orgCpuRangeLoading || orgCpuTopInstantLoading)
                }
                backendMissing={
                  shouldShowCpuBackendMissing(
                    orgCpuRangeLoading || orgCpuTopInstantLoading,
                    orgCpuRankedSeries.length > 0,
                    Boolean(orgFleetCpuDisplay.aggregateOnly)
                  )
                }
                timeRange={timeRange}
              />
              <KpiCard
                label={ORG_INTERFACE_ERROR_RATE_CONTRACT.title}
                value={
                  effectiveOrgInterfaceErrorRateRangeData
                    ? shouldPrioritizeInterfaceErrorRate(orgInterfaceErrorRateState, topInterfaceErrorRateDisplay.value)
                      ? formatRate(topInterfaceErrorRateDisplay.value)
                      : orgInterfaceErrorCoverage.integrityError
                        ? 'No errors'
                      : orgInterfaceErrorCoverage.failed
                        ? 'n/a'
                        : orgInterfaceErrorCoverage.complete
                          ? 'No errors'
                          : orgInterfaceErrorCoverage.observed > 0
                            ? 'No errors (partial)'
                            : 'Checking…'
                    : orgInterfaceErrorRateUnavailable
                      ? 'n/a'
                    : orgInterfaceErrorRateFailed
                      ? 'n/a'
                    : '…'
                }
                secondary={
                  effectiveOrgInterfaceErrorRateRangeData
                    ? shouldPrioritizeInterfaceErrorRate(orgInterfaceErrorRateState, topInterfaceErrorRateDisplay.value) && topInterfaceErrorRateDisplay.targetId
                      ? `${interfaceNameFromTargetId(topInterfaceErrorRateDisplay.targetId) ?? topInterfaceErrorRateDisplay.instance ?? 'interface'} on ${
                          orgDeviceNameById.get(topInterfaceErrorRateDisplay.deviceId) ?? topInterfaceErrorRateDisplay.deviceId
                      }`
                      : orgInterfaceErrorCoverage.failed && !orgInterfaceErrorCoverage.integrityError
                        ? 'error/discard coverage query failed'
                        : orgInterfaceErrorCoverage.integrityError
                          ? `Prometheus oper-status: ${formatInteger(orgInterfaceErrorCoverage.expected)} · error-counter interfaces: ${formatInteger(orgInterfaceErrorCoverage.observedRaw)}`
                        : `${formatInteger(orgInterfaceErrorCoverage.observed)} of ${formatInteger(orgInterfaceErrorCoverage.expected)} Prometheus oper-status interfaces checked`
                    : orgInterfaceErrorRateUnavailable
                      ? 'raw interface error/discard query unavailable'
                    : orgInterfaceErrorRateFailed
                      ? 'raw interface error/discard query failed'
                      : 'loading raw interface error/discard query'
                }
                trend={
                  orgInterfaceErrorRateUnavailable
                    ? 'Raw interface error/discard telemetry is unavailable in this environment'
                    : orgInterfaceErrorRateFailed
                      ? 'Raw interface error/discard query failed'
                    : shouldPrioritizeInterfaceErrorRate(orgInterfaceErrorRateState, topInterfaceErrorRateDisplay.value)
                    ? `${formatInteger(topInterfaceErrorRateDisplay.reportingInterfaces)} distinct interfaces appeared in the selected range top ${ORG_INTERFACE_ERROR_RATE_CONTRACT.topKLimit} (maximum ${ORG_INTERFACE_ERROR_RATE_CONTRACT.topKLimit} per timestamp); coverage ${formatInteger(orgInterfaceErrorCoverage.observed)}/${formatInteger(orgInterfaceErrorCoverage.expected)}`
                    : orgInterfaceErrorCoverage.failed
                      ? orgInterfaceErrorCoverage.integrityError
                        ? `Coverage mismatch — Prometheus oper-status: ${formatInteger(orgInterfaceErrorCoverage.expected)}; error-counter interfaces: ${formatInteger(orgInterfaceErrorCoverage.observedRaw)}`
                        : 'Raw interface error/discard coverage query failed'
                      : orgInterfaceErrorCoverage.complete
                      ? 'No current error/discard rate observed across all monitored interfaces'
                      : orgInterfaceErrorCoverage.observed > 0
                        ? `No errors in ${formatInteger(orgInterfaceErrorCoverage.observed)} checked; checking remaining ${formatInteger(Math.max(0, orgInterfaceErrorCoverage.expected - orgInterfaceErrorCoverage.observed))} interfaces`
                        : `Checking error/discard rates across all monitored interfaces (${formatInteger(orgInterfaceErrorCoverage.observed)}/${formatInteger(orgInterfaceErrorCoverage.expected)})`
                }
                accent={
                  orgInterfaceErrorCoverage.integrityError
                    ? 'warning'
                    : orgInterfaceErrorRateUnavailable || orgInterfaceErrorRateFailed || orgInterfaceErrorRateState === 'unavailable'
                    ? 'default'
                    : shouldPrioritizeInterfaceErrorRate(orgInterfaceErrorRateState, topInterfaceErrorRateDisplay.value)
                      ? topInterfaceErrorRateDisplay.value >= 5
                      ? 'danger'
                      : topInterfaceErrorRateDisplay.value >= 1
                        ? 'warning'
                        : 'success'
                      : orgInterfaceErrorCoverage.complete
                        ? 'success'
                        : 'warning'
                }
              />
              <KpiCard
                label="Routing adjacency health"
                value={routingHealthDisplay.value}
                secondary={
                  <div className="telemetry-inspector__routing-health">
                    {routingProtocolHealth.map((item) => (
                      <div key={item.protocol} className="telemetry-inspector__routing-health-row">
                        <span>
                          {item.protocol === 'BGP'
                            ? 'BGP peers'
                            : item.protocol === 'OSPF'
                              ? 'OSPF neighbors'
                              : 'IS-IS adjacencies'}
                        </span>
                        <strong
                          className={
                            item.down > 0
                              ? 'telemetry-inspector__state-split-down'
                              : routingHealthLiveTelemetryUnavailable && item.total > 0
                                ? 'telemetry-inspector__state-split-operational-down'
                                : 'telemetry-inspector__state-split-up'
                          }
                        >
                          {item.unavailable
                            ? 'unavailable'
                            : item.total === 0
                              ? 'no peers'
                              : item.down > 0
                                ? `${formatInteger(item.down)} down / ${formatInteger(item.total)}`
                                : routingHealthLiveTelemetryUnavailable
                                  ? `Topology context / ${formatInteger(item.total)}`
                                : `All up / ${formatInteger(item.total)}`}
                        </strong>
                      </div>
                    ))}
                  </div>
                }
                trend={routingHealthDisplay.trend}
                accent={routingHealthDisplay.accent}
              />
            </div>

            <div className="telemetry-inspector__org-detail-grid">
              <OrgErrorRateTrendCard
                summary={topInterfaceErrorRateDisplay}
                loading={
                  !effectiveOrgInterfaceErrorRateRangeData &&
                  !orgInterfaceErrorRateRangeError &&
                  !orgInterfaceErrorRateRangeBackendMissing
                }
                error={Boolean(!effectiveOrgInterfaceErrorRateRangeData && orgInterfaceErrorRateRangeError && !orgInterfaceErrorRateRangeBackendMissing)}
                backendMissing={orgInterfaceErrorRateRangeBackendMissing}
                timeRange={timeRange}
              />
              <OrgBgpSessionTable
                rows={orgBgpPeerRows}
                total={routingTableTotal({
                  deviceInventoryLoaded,
                  detailRowCount: orgBgpPeerRows.length,
                  detailTruncated: Boolean(orgBgpStatusData?.meta?.next),
                  aggregateTotal: orgDashboardSignalSummaries?.bgp?.total,
                  exactTotal: orgBgpStatusData?.meta?.exactTotal
                })}
                partial={deviceInventoryLoaded && Boolean(orgBgpStatusData?.meta?.next)}
                loading={!orgBgpStatusData && !orgBgpStatusBackendMissing}
                backendMissing={orgBgpStatusBackendMissing}
              />
              <OrgOspfAdjacencyTable
                rows={orgOspfAdjacencyRows}
                loading={!orgOspfStatusData && !orgOspfStatusBackendMissing}
                backendMissing={orgOspfStatusBackendMissing}
                liveTelemetryUnavailable={routingHealthLiveTelemetryUnavailable}
              />
              <OrgIsisAdjacencyTable
                rows={orgIsisAdjacencyRows}
                loading={routingAdjacencyTopologyInitialLoading}
                unavailable={orgIsisAdjacencyUnavailable}
                partialUnavailable={orgIsisAdjacencyPartialUnavailable}
                liveTelemetryUnavailable={routingHealthLiveTelemetryUnavailable}
              />
              <OrgBfdSessionTable
                rows={orgBfdSessionRows}
                total={routingTableTotal({
                  deviceInventoryLoaded,
                  detailRowCount: orgBfdSessionRows.length,
                  detailTruncated: Boolean(orgBfdStatusData?.meta?.next),
                  aggregateTotal: orgDashboardSignalSummaries?.bfd?.total,
                  exactTotal: orgBfdStatusData?.meta?.exactTotal
                })}
                partial={deviceInventoryLoaded && Boolean(orgBfdStatusData?.meta?.next)}
                loading={orgBfdSessionInitialLoading}
                unavailable={orgBfdSessionUnavailable}
                liveTelemetryUnavailable={bfdSessionLiveTelemetryUnavailable}
              />
              <OrgStpLagCard
                summary={orgStpLagSummary}
                loading={
                  (!effectiveOrgStpTopChangesRangeData &&
                    !stpTopChangesRangeUnavailable &&
                    !orgStpTopChangesData &&
                    !orgStpTopChangesBackendMissing &&
                    !orgLacpSyncData &&
                    !orgLacpSyncBackendMissing) ||
                  (Boolean(orgCliStpTopChangesData) &&
                    !effectiveOrgCliStpTopChangesRangeData &&
                    orgCliStpTopChangesRangeLoading &&
                    !cliStpTopChangesRangeUnavailable)
                }
              />
              <OrgDeviceHealthTable
                rows={orgDeviceHealthRows}
                loading={shouldLoadDeviceHealthSummary(
                  Boolean(effectiveDeviceSummaryData),
                  deviceSummaryError,
                  orgLatestDeviceHealthLoading,
                  orgDeviceHealthTopKLoading
                )}
                truncated={Boolean(orgLatestDeviceHealthData?.truncated)}
                error={orgLatestDeviceHealthError}
                backendMissing={orgLatestDeviceHealthBackendMissing}
              />
            </div>

            {runtimeMetricsData ? <RuntimeCoverageSection runtimeMetrics={runtimeMetricsData} selectedScope={selectedScope} /> : null}

            {!runtimeMetricsData && !runtimeMetricsBackendMissing && runtimeMetricsError ? (
              <OrgModulePlaceholder intent="Collector runtime metrics are unavailable for the current role or environment. Curated pipeline panels remain below when available." />
            ) : null}

            {pipelineHealthWidgets.length > 0 ? (
              <GraphRowSection
                title="OTEL / Connector Runtime Dashboards"
                description="Sync age, export pressure, dropped samples, restarts, and VPN health for the telemetry pipeline."
                widgets={pipelineHealthWidgets}
                refreshSeconds={refreshSeconds}
                rangePreset={rangePreset}
                onAuthRefresh={refreshGrafanaEmbedSession}
              />
            ) : null}

          </>
        ) : !selectedSiteId ? (
          <OrgModulePlaceholder intent="Pick a site to inspect telemetry pipeline state." />
        ) : activeStageBackendMissing ? (
          <OrgModulePlaceholder intent="Signals APIs are unavailable from the backend for this environment." />
        ) : (
          <>
            <SignalsSummary scopeType={hasDeviceContext ? 'device' : 'site'} scopeId={signalSummaryScopeId} refreshSeconds={refreshSeconds} />

            {pipelineHealthWidgets.length > 0 ? (
              <GraphRowSection
                title="OTEL / Connector Runtime Dashboards"
                description="Sync age, scrape/export failures, dropped samples, queue pressure, restarts, and VPN health for the selected scope."
                widgets={pipelineHealthWidgets}
                refreshSeconds={refreshSeconds}
                rangePreset={rangePreset}
                onAuthRefresh={refreshGrafanaEmbedSession}
              />
            ) : null}

            {hasDeviceContext ? (
              <div className="telemetry-inspector__summary kpi-grid">
                <KpiCard
                  label="Telemetry freshness"
                  value={freshnessLabel(selectedDeviceSummary?.lastSeenAt)}
                  secondary={selectedDeviceSummary?.lastSeenAt ? `Last seen ${formatTimestamp(selectedDeviceSummary.lastSeenAt)}` : 'No device heartbeat yet'}
                  accent={(ageInMinutes(selectedDeviceSummary?.lastSeenAt) ?? Number.POSITIVE_INFINITY) > DEVICE_STALE_MINUTES ? 'warning' : 'success'}
                />
                <KpiCard
                  label="Device state"
                  value={selectedDeviceSummary?.state ?? 'unknown'}
                  secondary={selectedDeviceSummary?.displayName ?? selectedDeviceSummary?.id ?? selectedDeviceId}
                />
                <KpiCard label="Startup config drift" value={selectedDeviceSummary?.startupConfigDrift ? 'Yes' : 'No'} secondary="Latest collector snapshot" />
              </div>
            ) : null}

            {family === 'device_health' && selectedDeviceId && deviceHealthExtraWidgets.length > 0 ? (
              <GraphRowSection
                title="Device Health Timelines"
                description="Condition timelines for CPU and memory pressure, plus uptime and telemetry freshness for the selected device."
                widgets={deviceHealthExtraWidgets}
                refreshSeconds={refreshSeconds}
                rangePreset={rangePreset}
                onAuthRefresh={refreshGrafanaEmbedSession}
              />
            ) : null}

            <div className="telemetry-inspector__table-card">
              <div className="telemetry-inspector__table-header">
                <div>
                  <h2>Inspection Rows</h2>
                  <p>
                    {activeStageLoading
                      ? 'Loading telemetry rows…'
                        : displayRows.length === 0
                        ? inspectionEmptyMessage(stage, family)
                        : hasDeviceRoleFilter
                          ? `Page ${currentPage}. Showing ${displayRows.length} rows matching ${formatDeviceRoleLabel(selectedDeviceRole)} devices on this page.`
                        : `Page ${currentPage} of ${totalPages}. Showing ${pageStart}-${pageEnd} of ${rowCount} rows.`}
                  </p>
                </div>
                <div className="telemetry-inspector__table-actions">
                  <label className="telemetry-inspector__page-size">
                    Rows
                    <select
                      value={inspectionPageLimit}
                      onChange={(event) => {
                        const nextLimit = Number(event.target.value);
                        setInspectionPageLimit(Number.isFinite(nextLimit) ? nextLimit : 10);
                      }}
                      disabled={activeStageLoading}
                    >
                      {INSPECTION_PAGE_SIZE_OPTIONS.map((limit) => (
                        <option key={limit} value={limit}>
                          {limit}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="telemetry-inspector__pager-button"
                    onClick={() => {
                      setCurrentPage((prev) => Math.max(1, prev - 1));
                      setBeforeCursor(inspectionPageMeta?.prev ?? '');
                      setAfterCursor('');
                    }}
                    disabled={!hasPrevPage || activeStageLoading}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="telemetry-inspector__pager-button"
                    onClick={() => {
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1));
                      setAfterCursor(inspectionPageMeta?.next ?? '');
                      setBeforeCursor('');
                    }}
                    disabled={!hasNextPage || activeStageLoading}
                  >
                    Next
                  </button>
                </div>
              </div>
              {activeStageLoading ? (
                <p className="signals-summary__empty">Loading telemetry rows…</p>
              ) : displayRows.length === 0 ? null : (
                <div className="telemetry-inspector__table-wrap">
                  <table className="telemetry-inspector__table">
                    <thead>
                      <tr>
                        <th>Key</th>
                        {isSSHSessionTable ? (
                          <>
                            <th>Active SSH Sessions</th>
                            <th>Global SSH Limit</th>
                            <th>VRFs</th>
                            <th>Per-Source-IP Limit</th>
                          </>
                        ) : (
                          <th>{stage === 'conditions' ? 'State' : 'Value'}</th>
                        )}
                        <th>{stage === 'signals' ? 'Observed' : 'Updated'}</th>
                        <th>Device</th>
                        {showTargetColumn ? <th>{targetColumnLabelForFamily(family)}</th> : null}
                        {showBgpPeerColumns ? (
                          <>
                            <th>Peer</th>
                            <th>Local</th>
                          </>
                        ) : showInstanceColumn ? (
                          <th>{instanceColumnLabelForFamily(family)}</th>
                        ) : null}
                        <th>Chart</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.map((row) => (
                        <tr key={row.id} className="telemetry-inspector__row">
                          <td>
                            <div className="telemetry-inspector__cell-primary">{labelForKey(row.key)}</div>
                            <div className="telemetry-inspector__cell-meta">{row.key}</div>
                          </td>
                          {isSSHSessionTable ? (
                            <>
                              <td>{row.sshSessionSummary?.active ?? '—'}</td>
                              <td>{row.sshSessionSummary?.globalLimit ?? '—'}</td>
                              <td title={row.sshSessionSummary?.vrfs}>{row.sshSessionSummary?.vrfs ?? '—'}</td>
                              <td>{row.sshSessionSummary?.perSourceIPLimit ?? '—'}</td>
                            </>
                          ) : (
                            <td>
                              <div className="telemetry-inspector__cell-primary">{row.valueText}</div>
                              {shouldShowSignalNumericMeta(row.key, row.numericValue) ? (
                                <div className="telemetry-inspector__cell-meta">numeric</div>
                              ) : null}
                            </td>
                          )}
                          <td>{formatTimestamp(row.timestamp)}</td>
                          <td title={row.deviceName || row.deviceId || 'Unknown device'}>
                            {row.deviceName || row.deviceId || 'Unknown device'}
                          </td>
                          {showTargetColumn ? (
                            <td>
                              <div className="telemetry-inspector__cell-primary">
                                {isInterfaceScopedRow(row) ? friendlyTargetLabel(row) : '—'}
                              </div>
                              <div className="telemetry-inspector__cell-meta">
                                {isInterfaceScopedRow(row)
                                  ? row.targetType || (hasInterfaceLikeTargetId(row.targetId) ? 'interface' : 'target')
                                  : 'device-scoped'}
                              </div>
                            </td>
                          ) : null}
                          {showBgpPeerColumns ? (
                            <>
                              <td>{bgpSessionParts(row.instance).peerAddr || '—'}</td>
                              <td>{bgpSessionParts(row.instance).localAddr || '—'}</td>
                            </>
                          ) : showInstanceColumn ? (
                            <td>{inspectorInstanceDisplay(row) || '—'}</td>
                          ) : null}
                          <td>
                            <button
                              type="button"
                              className="telemetry-inspector__chart-button"
                              onClick={() => openChartDrawer(row)}
                              aria-label={`View chart for ${row.key}`}
                              title={`View chart for ${row.key}`}
                              disabled={!chartPlanForRow(row)}
                            >
                              <ChartIcon size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {showScopeDashboards ? (
              siteCockpitBackendMissing ? (
                <OrgModulePlaceholder intent={`${scopeLabel} cockpit dashboards are unavailable from the backend for this environment.`} />
              ) : (
                <GraphRowSection
                  title={hasDeviceContext ? 'Device Context Dashboard' : 'Site Context Dashboard'}
                  description={
                    hasDeviceContext
                      ? 'Recommended dashboards for the selected device inside its site context.'
                      : 'Recommended dashboards for the selected site.'
                  }
                  widgets={siteCockpitWidgets}
                  refreshSeconds={refreshSeconds}
                  rangePreset={rangePreset}
                  onAuthRefresh={refreshGrafanaEmbedSession}
                />
              )
            ) : null}

            {templatesBackendMissing ? (
              <OrgModulePlaceholder intent="Dashboard templates are unavailable from the backend, so related charts cannot be rendered." />
            ) : (
              <GraphRowSection
                title="Related Dashboards"
                description={relatedDashboardsDescription}
                widgets={dashboardWidgets}
                refreshSeconds={refreshSeconds}
                rangePreset={rangePreset}
                onAuthRefresh={refreshGrafanaEmbedSession}
              />
            )}
          </>
        )}
      </div>
      <Drawer
        open={Boolean(chartDrawer)}
        onClose={() => setChartDrawer(null)}
        title={chartDrawerTitle}
        subtitle={
          chartDrawer
            ? (() => {
                const base = `${chartDrawer.row.deviceName || chartDrawer.row.deviceId || 'Unknown device'} • ${labelForKey(chartDrawer.row.key)}`;
                if (chartDrawer.row.family === 'bgp') {
                  const { peerAddr, localAddr } = bgpSessionParts(chartDrawer.row.instance);
                  if (peerAddr && localAddr) {
                    return `${base} • ${peerAddr} / ${localAddr}`;
                  }
                  if (peerAddr) {
                    return `${base} • ${peerAddr}`;
                  }
                  if (localAddr) {
                    return `${base} • ${localAddr}`;
                  }
                  return base;
                }
                const instanceDisplay = inspectorInstanceDisplay(chartDrawer.row);
                return instanceDisplay ? `${base} • ${instanceDisplay}` : base;
              })()
            : undefined
        }
        width="wide"
      >
        {chartDrawer == null ? null : (
          <div className="telemetry-inspector__drawer">
            <div className="telemetry-inspector__drawer-copy">
              <p>
                Exact row-level charting for the selected {chartDrawer.row.stage.slice(0, -1)}.
              </p>
              <p>{chartContextParts.join(' • ')}</p>
            </div>
            {chartDrawer.primary ? (
              <div className="telemetry-inspector__drawer-section">
                <h3>Exact row chart</h3>
                <div className="org-cockpit__grid">
                  <GrafanaIframeCard
                    widget={chartDrawer.primary}
                    refreshSeconds={refreshSeconds}
                    timeRangePreset={rangePreset}
                    onAuthRefresh={refreshGrafanaEmbedSession}
                  />
                </div>
              </div>
            ) : (
              <OrgModulePlaceholder intent="No exact chart template is available for this row yet." />
            )}
          </div>
        )}
      </Drawer>
    </OrgModuleLayout>
  );
};

export default GrafanaExplorerPage;
